import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as LucideIcons from 'react-icons/lu';
import PageLoader from '../components/PageLoader';
import { AppActionGroup, AppButton, AppField, AppFormDialog } from '../components/authenticated-ui/AuthenticatedUi';
import { apiJson, isUnauthorizedError } from '../services/api';
import { ONBOARDING_PATHS } from '../domains/identity/onboardingPaths';
import './RehabWorkflow.css';

const NODE_WIDTH = 150;
const NODE_HEIGHT = 64;
// Must match REHAB_NODE_SIZE_LIMITS and REHAB_NODE_DETAIL_MAX in backend/domains/properties/routes.py.
const NODE_MIN_WIDTH = 120;
const NODE_MAX_WIDTH = 800;
const NODE_MIN_HEIGHT = 56;
const NODE_MAX_HEIGHT = 800;
const WORKSPACE_MIN_WIDTH = 640;
const WORKSPACE_MAX_WIDTH = 2400;
const WORKSPACE_MIN_HEIGHT = 360;
const WORKSPACE_MAX_HEIGHT = 1400;
const WORKSPACE_SIZE_PREFERENCE = '/identity/preferences/rehab_workflow.workspace_size';
const PINNED_COMPONENTS_PREFERENCE = '/identity/preferences/rehab_workflow.pinned_components';
export const NODE_DETAIL_MAX = 4000;
const RESIZE_CORNERS = ['nw', 'ne', 'sw', 'se'];
export const SYSTEMS_QUOTE = {
  text: 'A business minded landlord succeeds where others fail... They implement systems and repeatable processes. '
    + 'Have you noticed how every Starbucks store you walk into looks pretty much the same. Same colors. Same aprons on '
    + 'the employees… you’re going to get the same drink taste wherever you go. This is because businesses like Starbucks '
    + 'are simply a collection of systems and recipes that everyone has learned to follow. Savvy business owners seek to '
    + 'create procedures and systems within their business that instruct how all situations are handled. Systems are '
    + 'repeatable processes that can guide how a business operates at all times.',
  authors: 'Brandon & Heather Turner',
  source: 'The Book on Managing Rentals',
  url: 'https://www.audible.com/pd/B01HQKXX8W',
};
// People and hardware nodes reference canonical directory records; boards store only the reference.
// Every sign-up account type is now backed by the canonical Phase 1 people directory. The inspector alias
// keeps older saved inspector records visible while new records use the account's onboarding-path key.
const DIRECTORY_KINDS_BY_PATH = Object.fromEntries(ONBOARDING_PATHS.map((path) => [path.key, [path.key]]));
DIRECTORY_KINDS_BY_PATH.rehab_field_inspector.push('inspector');
const pathPlural = (title) => (title.endsWith('y') ? `${title.slice(0, -1)}ies` : `${title}s`);
export const PEOPLE_CATEGORIES = ONBOARDING_PATHS.map((path) => ({
  key: path.key, label: pathPlural(path.title), icon: path.icon, kinds: DIRECTORY_KINDS_BY_PATH[path.key],
}));
export const CONTACT_KINDS = Object.fromEntries(ONBOARDING_PATHS
  .map((path) => [path.key, { label: path.title, icon: path.icon }]));
CONTACT_KINDS.inspector = CONTACT_KINDS.rehab_field_inspector;
export const resolveContactNode = (node, directory) => {
  const ref = node.contact_ref || {};
  const record = directory?.find((entry) => entry.kind === ref.kind && String(entry.id) === String(ref.id));
  if (!record) return { available: false, name: node.label, meta: '' };
  const person = ref.contact_id ? record.contacts?.find((contact) => contact.contact_id === ref.contact_id) : null;
  const businessName = record.organization_name || record.name;
  const personName = record.organization_name ? record.name : person?.name;
  return { available: true, name: businessName, meta: [personName, record.specialty].filter(Boolean).join(' · ') };
};
export const resolveHardwareNode = (node, directory) => {
  const record = directory?.devices?.find((device) => String(device.hardware_device_id) === String(node.hardware_ref?.id));
  if (!record) return { available: false, name: node.label, meta: '' };
  return { available: true, name: record.display_name,
    meta: [record.manufacturer, record.model, record.property_name].filter(Boolean).join(' · '),
    connection_mode: record.connection_mode || 'live' };
};
export const updateHardwareDirectoryDevice = (directory, hardwareDeviceId, changes) => ({
  ...directory,
  devices: (directory?.devices || []).map((device) => (
    device.hardware_device_id === hardwareDeviceId ? { ...device, ...changes } : device
  )),
});
// renderWidth/renderHeight are measured from the DOM for drawing only and are never saved.
const nodeWidth = (node) => node.renderWidth || node.width || NODE_WIDTH;
const nodeHeight = (node) => node.renderHeight || node.height || NODE_HEIGHT;
export const resizeNodeBox = (start, corner, deltaX, deltaY) => {
  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const west = corner.includes('w');
  const north = corner.includes('n');
  const width = clamp(Math.round(start.width + (west ? -deltaX : deltaX)), NODE_MIN_WIDTH, NODE_MAX_WIDTH);
  const height = clamp(Math.round(start.height + (north ? -deltaY : deltaY)), NODE_MIN_HEIGHT, NODE_MAX_HEIGHT);
  return { width, height,
    x: west ? start.x + start.width - width : start.x, y: north ? start.y + start.height - height : start.y };
};
export const resizeWorkspaceBox = (start, deltaX, deltaY) => ({
  width: Math.min(WORKSPACE_MAX_WIDTH, Math.max(WORKSPACE_MIN_WIDTH, Math.round(start.width + deltaX))),
  height: Math.min(WORKSPACE_MAX_HEIGHT, Math.max(WORKSPACE_MIN_HEIGHT, Math.round(start.height + deltaY))),
});
const normalizeWorkspaceSize = (value) => (value && Number.isInteger(value.width) && Number.isInteger(value.height)
  && value.width >= WORKSPACE_MIN_WIDTH && value.width <= WORKSPACE_MAX_WIDTH
  && value.height >= WORKSPACE_MIN_HEIGHT && value.height <= WORKSPACE_MAX_HEIGHT
  ? { width: value.width, height: value.height } : null);
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 1.8;
const ZOOM_STEP = 0.2;
const CANVAS_ORIGIN = 6000;
const MIN_CANVAS_COORDINATE = -5800;
const BASE_WORLD_SIZE = 18000;
const NODE_TYPES = [
  ['security', 'Security', 'pi-shield'], ['smart-locks', 'Smart Locks', 'pi-lock'],
  ['internet', 'Internet', 'pi-wifi'], ['inspector-documentation', 'Inspector Documentation', 'pi-camera'],
  ['contractor-bids', 'Contractor Bids', 'pi-users'], ['work-order', 'Work Order', 'pi-file-edit'],
];
// Space-first Command component chooser. Add future canvas tools here so the shortcut stays one extensible surface.
const QUICK_COMPONENTS = [
  { key: 'notes', label: 'Notes', description: 'Add a movable note to this workflow', icon: 'pi-file-edit',
    width: 260, height: 180 },
  { key: 'lender-comparison', label: 'Lender Comparison', description: 'Rank demo financing offers for this property', icon: 'pi-wallet',
    width: 300, height: 190 },
];
export const DEMO_LENDERS = [
  { id: 'demo-hard-bridge', name: 'Bridgeway Capital', type: 'hard-money', rate: 10.25, points: 1.5, closing_cost: 1650, term_months: 12, max_ltc: 90, min_credit: 660, funding_days: 5, draw_fee: 125 },
  { id: 'demo-hard-summit', name: 'Summit Rehab Finance', type: 'hard-money', rate: 11.1, points: 1, closing_cost: 1200, term_months: 18, max_ltc: 85, min_credit: 640, funding_days: 7, draw_fee: 75 },
  { id: 'demo-hard-forge', name: 'Forge Private Lending', type: 'hard-money', rate: 9.75, points: 2.25, closing_cost: 2150, term_months: 12, max_ltc: 80, min_credit: 680, funding_days: 4, draw_fee: 150 },
  { id: 'demo-conventional-heartland', name: 'Heartland Community Bank', type: 'conventional', rate: 6.5, points: 0.5, closing_cost: 2950, term_months: 360, max_ltc: 80, min_credit: 700, funding_days: 32, draw_fee: 0 },
  { id: 'demo-conventional-harbor', name: 'Harbor Residential', type: 'conventional', rate: 6.85, points: 0, closing_cost: 2350, term_months: 360, max_ltc: 75, min_credit: 680, funding_days: 26, draw_fee: 0 },
  { id: 'demo-conventional-union', name: 'Union Home Lending', type: 'conventional', rate: 6.25, points: 1.25, closing_cost: 3400, term_months: 360, max_ltc: 75, min_credit: 720, funding_days: 38, draw_fee: 0 },
];
export const rankDemoLenders = (lenders, type, needs = {}) => {
  const projectCost = Math.max(1, Number(needs.project_cost) || 150000);
  const desiredLoan = Math.max(1, Number(needs.loan_amount) || projectCost * .8);
  const creditScore = Math.max(300, Number(needs.credit_score) || 720);
  const holdingMonths = Math.max(1, Number(needs.holding_months) || 12);
  return lenders.filter((lender) => lender.type === type).map((lender) => {
    const maxLoan = projectCost * lender.max_ltc / 100;
    const eligible = maxLoan >= desiredLoan && creditScore >= lender.min_credit;
    const financed = Math.min(desiredLoan, maxLoan);
    const projectedMonths = type === 'hard-money' ? Math.min(holdingMonths, lender.term_months) : Math.min(holdingMonths, 60);
    const interestCost = financed * lender.rate / 100 * projectedMonths / 12;
    const pointsCost = financed * lender.points / 100;
    const projectedCost = interestCost + pointsCost + lender.closing_cost + lender.draw_fee;
    const cashRequired = Math.max(0, projectCost - maxLoan) + pointsCost + lender.closing_cost;
    return { ...lender, eligible, max_loan: Math.round(maxLoan), projected_cost: Math.round(projectedCost),
      cash_required: Math.round(cashRequired), reason: eligible
        ? `$${Math.round(projectedCost).toLocaleString()} projected financing cost over ${projectedMonths} months`
        : maxLoan < desiredLoan ? `Maximum loan is $${Math.round(maxLoan).toLocaleString()}` : `Requires a ${lender.min_credit}+ credit score` };
  }).sort((left, right) => Number(right.eligible) - Number(left.eligible)
    || left.projected_cost - right.projected_cost || left.funding_days - right.funding_days)
    .map((lender, index) => ({ ...lender, rank: index + 1 }));
};
const DEFAULT_NOTE_APPEARANCE = {
  background_color: '#22343b', header_color: '#24505a', border_color: '#38b2a6', text_color: '#b9d9d5',
};
const DEFAULT_NODE_APPEARANCE = {
  background_color: '#222a35', header_color: '#303b49', border_color: '#64748b',
};
const PORT_SIDES = ['top', 'right', 'bottom', 'left'];
const normalizeCollapseSides = (value) => Array.isArray(value) && value.length <= 4
  && value.every((side) => PORT_SIDES.includes(side)) && new Set(value).size === value.length
  ? PORT_SIDES.filter((side) => value.includes(side)) : null;
const DEFAULT_PORT_LAYOUT = { top: 1, right: 1, bottom: 1, left: 1 };
const defaultPortLayout = (node) => {
  if (node?.type === 'notes') return { top: 0, right: 0, bottom: 0, left: 0 };
  if (node?.type === 'contractor-bids') return { ...DEFAULT_PORT_LAYOUT, top: 4 };
  if (node?.type === 'inspector-documentation') return { ...DEFAULT_PORT_LAYOUT, top: 3 };
  return DEFAULT_PORT_LAYOUT;
};
export const normalizePortLayout = (value, node = null) => {
  const fallback = defaultPortLayout(node);
  if (!value || typeof value !== 'object') return { ...fallback };
  if (!PORT_SIDES.every((side) => Number.isInteger(value[side]) && value[side] >= 0 && value[side] <= 8)) return null;
  return Object.fromEntries(PORT_SIDES.map((side) => [side, value[side]]));
};
const portHandleSide = (handle) => {
  if (handle === 'in') return 'left';
  if (handle === 'out') return 'right';
  return PORT_SIDES.find((side) => handle === side || handle.startsWith(`${side}-`)) || 'right';
};
const portHandleIndex = (handle) => {
  const match = handle.match(/-(\d+)$/);
  return match ? Number(match[1]) : 0;
};
const usesLegacyNumberedTopPorts = (node) => ['contractor-bids', 'inspector-documentation'].includes(node?.type);
const portHandles = (node, side) => {
  const count = normalizePortLayout(node?.port_layout, node)?.[side] ?? 0;
  return Array.from({ length: count }, (_, index) => (
    side === 'top' && usesLegacyNumberedTopPorts(node) ? `top-${index}` : index === 0 ? side : `${side}-${index}`
  ));
};
const portPositionPercent = (handle, count) => count <= 1 ? 50 : (portHandleIndex(handle) / (count - 1)) * 100;
const NOTE_APPEARANCE_PRESETS = [
  ['GravviSoft', DEFAULT_NOTE_APPEARANCE],
  ['Deep teal', { background_color: '#18363b', header_color: '#145b5d', border_color: '#4fd1c5', text_color: '#d7fffb' }],
  ['Ocean', { background_color: '#163943', header_color: '#11636b', border_color: '#55cbd3', text_color: '#d8f8fa' }],
  ['Sea glass', { background_color: '#dcefeb', header_color: '#397a72', border_color: '#70aaa1', text_color: '#254943' }],
  ['Midnight', { background_color: '#202936', header_color: '#2f3e50', border_color: '#6f8ca8', text_color: '#dbe7f2' }],
  ['Blue', { background_color: '#1f3045', header_color: '#244b72', border_color: '#60a5d8', text_color: '#dbeafe' }],
  ['Sky', { background_color: '#dcecf5', header_color: '#337aa5', border_color: '#73afd0', text_color: '#29495c' }],
  ['Navy', { background_color: '#18283d', header_color: '#1c3f68', border_color: '#527fb3', text_color: '#d7e5f5' }],
  ['Forest', { background_color: '#20382f', header_color: '#285947', border_color: '#65a98c', text_color: '#d9eee4' }],
  ['Sage', { background_color: '#e3eadf', header_color: '#617b61', border_color: '#91a88b', text_color: '#3f5140' }],
  ['Graphite', { background_color: '#292f36', header_color: '#3a444e', border_color: '#788795', text_color: '#e0e6eb' }],
  ['Silver', { background_color: '#e7ebee', header_color: '#667581', border_color: '#9aa7b0', text_color: '#3c4851' }],
  ['Plum', { background_color: '#392d43', header_color: '#513b5e', border_color: '#b68ac7', text_color: '#f1e5f5' }],
  ['Lavender', { background_color: '#ece5f2', header_color: '#755886', border_color: '#ad91bd', text_color: '#55405f' }],
  ['Rose', { background_color: '#472d37', header_color: '#704050', border_color: '#c47a91', text_color: '#f5dfe6' }],
  ['Burgundy', { background_color: '#3d252d', header_color: '#6c3043', border_color: '#ad5d76', text_color: '#f1d9e1' }],
  ['Terracotta', { background_color: '#49332c', header_color: '#7a4939', border_color: '#c47e62', text_color: '#f3ddd4' }],
  ['Amber', { background_color: '#443a25', header_color: '#735d25', border_color: '#c6a451', text_color: '#f5e9c3' }],
  ['Sand', { background_color: '#eee7d8', header_color: '#82704f', border_color: '#b9a57b', text_color: '#514833' }],
  ['Evergreen', { background_color: '#172f2b', header_color: '#1f5148', border_color: '#4e9a8b', text_color: '#d4ebe6' }],
];
const normalizeNoteAppearance = (value) => {
  const candidate = value && typeof value === 'object' ? value : DEFAULT_NOTE_APPEARANCE;
  const keys = Object.keys(DEFAULT_NOTE_APPEARANCE);
  if (!keys.every((key) => /^#[0-9a-f]{6}$/i.test(candidate[key] || ''))) return null;
  return Object.fromEntries(keys.map((key) => [key, candidate[key].toLowerCase()]));
};
const normalizeNodeAppearance = (value) => {
  const candidate = value && typeof value === 'object' ? value : DEFAULT_NODE_APPEARANCE;
  const keys = Object.keys(DEFAULT_NODE_APPEARANCE);
  if (!keys.every((key) => /^#[0-9a-f]{6}$/i.test(candidate[key] || ''))) return null;
  return Object.fromEntries(keys.map((key) => [key, candidate[key].toLowerCase()]));
};
export const connectedWorkflowNodeIds = (edges, startId, protectedIds = new Set(['property'])) => {
  if (!startId || protectedIds.has(startId)) return [];
  const connected = new Set([startId]);
  const queue = [startId];
  while (queue.length) {
    const current = queue.shift();
    edges.forEach((edge) => {
      const next = edge.source === current ? edge.target : edge.target === current ? edge.source : null;
      if (!next || protectedIds.has(next) || connected.has(next)) return;
      connected.add(next); queue.push(next);
    });
  }
  return [...connected];
};
export const connectedBranchNodeIds = (edges, controllerId, sides) => {
  const allowedSides = new Set(normalizeCollapseSides(sides) || []);
  if (!controllerId || !allowedSides.size) return new Set();
  const hidden = new Set();
  const protectedIds = new Set([controllerId, 'property']);
  const pending = [];
  edges.forEach((edge) => {
    if (edge.source === controllerId && allowedSides.has(portHandleSide(edge.source_handle))) pending.push(edge.target);
    if (edge.target === controllerId && allowedSides.has(portHandleSide(edge.target_handle))) pending.push(edge.source);
  });
  while (pending.length) {
    const nodeId = pending.pop();
    if (!nodeId || protectedIds.has(nodeId) || hidden.has(nodeId)) continue;
    hidden.add(nodeId);
    edges.forEach((edge) => {
      if (edge.source === nodeId && !protectedIds.has(edge.target) && !hidden.has(edge.target)) pending.push(edge.target);
      if (edge.target === nodeId && !protectedIds.has(edge.source) && !hidden.has(edge.source)) pending.push(edge.source);
    });
  }
  return hidden;
};
const BID_CHILDREN = [
  ['bid-estimate', 'Estimate', 'pi-calculator'], ['bid-actual', 'Actual', 'pi-dollar'],
  ['bid-scoring', 'Scoring', 'pi-star'], ['bid-lessons', 'Lessons', 'pi-book'],
];
// Every name here must exist in the installed PrimeIcons 6.0.1 font; unknown names render blank.
export const COMPONENT_ICONS = [
  'pi-box', 'pi-sitemap', 'pi-th-large', 'pi-clone', 'pi-shield', 'pi-lock', 'pi-key', 'pi-wifi',
  'pi-home', 'pi-building', 'pi-wrench', 'pi-file-edit', 'pi-cog', 'pi-bolt', 'pi-sun', 'pi-camera',
  'pi-images', 'pi-file', 'pi-folder', 'pi-check-square', 'pi-list', 'pi-check-circle', 'pi-calendar',
  'pi-clock', 'pi-dollar', 'pi-calculator', 'pi-chart-bar', 'pi-chart-line', 'pi-users', 'pi-user',
  'pi-truck', 'pi-map-marker', 'pi-star', 'pi-book', 'pi-tag', 'pi-flag', 'pi-exclamation-triangle',
  'pi-lock-open', 'pi-unlock', 'pi-eye', 'pi-video', 'pi-bell', 'pi-phone', 'pi-mobile', 'pi-envelope',
  'pi-comments', 'pi-desktop', 'pi-server', 'pi-database', 'pi-cloud', 'pi-globe', 'pi-map',
  'pi-directions', 'pi-compass', 'pi-car', 'pi-briefcase', 'pi-id-card', 'pi-user-edit', 'pi-user-plus',
  'pi-check', 'pi-verified', 'pi-thumbs-up', 'pi-file-pdf', 'pi-file-import', 'pi-file-export',
  'pi-paperclip', 'pi-print', 'pi-inbox', 'pi-image', 'pi-palette', 'pi-pencil', 'pi-eraser', 'pi-trash',
  'pi-filter', 'pi-sliders-h', 'pi-search', 'pi-money-bill', 'pi-wallet', 'pi-credit-card',
  'pi-percentage', 'pi-chart-pie', 'pi-hourglass', 'pi-stopwatch', 'pi-history', 'pi-calendar-plus',
  'pi-sync', 'pi-refresh', 'pi-power-off', 'pi-heart', 'pi-gift', 'pi-bookmark', 'pi-tags',
  'pi-megaphone', 'pi-send', 'pi-link', 'pi-qrcode', 'pi-ticket', 'pi-info-circle', 'pi-question-circle',
  'pi-ban', 'pi-times-circle', 'pi-shopping-cart', 'pi-table', 'pi-moon', 'pi-hashtag',
  'pi-align-center', 'pi-align-justify', 'pi-align-left', 'pi-align-right', 'pi-arrows-alt', 'pi-arrows-h',
  'pi-arrows-v', 'pi-at', 'pi-bars', 'pi-bitcoin', 'pi-bookmark-fill', 'pi-calendar-minus',
  'pi-calendar-times', 'pi-cart-plus', 'pi-chevron-circle-down', 'pi-chevron-circle-left',
  'pi-chevron-circle-right', 'pi-chevron-circle-up', 'pi-circle', 'pi-circle-fill', 'pi-circle-off',
  'pi-circle-on', 'pi-cloud-download', 'pi-cloud-upload', 'pi-code', 'pi-comment', 'pi-copy',
  'pi-delete-left', 'pi-directions-alt', 'pi-download', 'pi-ellipsis-h', 'pi-ellipsis-v', 'pi-euro',
  'pi-exclamation-circle', 'pi-external-link', 'pi-eye-slash', 'pi-file-excel', 'pi-file-o',
  'pi-file-word', 'pi-filter-fill', 'pi-filter-slash', 'pi-flag-fill', 'pi-folder-open', 'pi-heart-fill',
  'pi-info', 'pi-language', 'pi-microphone', 'pi-minus', 'pi-minus-circle', 'pi-pause', 'pi-play',
  'pi-plus', 'pi-plus-circle', 'pi-pound', 'pi-replay', 'pi-reply', 'pi-save', 'pi-search-minus',
  'pi-search-plus', 'pi-share-alt', 'pi-shopping-bag', 'pi-sign-in', 'pi-sign-out', 'pi-sliders-v',
  'pi-sort', 'pi-sort-alt', 'pi-stop', 'pi-stop-circle', 'pi-tablet', 'pi-thumbs-down',
  'pi-thumbs-down-fill', 'pi-thumbs-up-fill', 'pi-times', 'pi-undo', 'pi-upload', 'pi-user-minus',
  'pi-volume-down', 'pi-volume-off', 'pi-volume-up', 'pi-window-maximize', 'pi-window-minimize',
];
// Earlier defaults used PrimeIcons 7 names that this font lacks; saved nodes still carry them.
const LEGACY_ICON_FALLBACKS = {
  'pi-hammer': 'pi-file-edit', 'pi-clipboard': 'pi-check-square', 'pi-list-check': 'pi-list', 'pi-lightbulb': 'pi-sun',
};
export const displayIcon = (icon) => LEGACY_ICON_FALLBACKS[icon] || icon || 'pi-circle';
export const LUCIDE_ICON_NAMES = Object.keys(LucideIcons).filter((name) => /^Lu[A-Z]/.test(name));
const lucideIconValue = (name) => `lu:${name}`;
const iconLabel = (icon) => icon.startsWith('lu:')
  ? icon.slice(5).replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase()
  : icon.replace('pi-', '').replaceAll('-', ' ');
const WorkflowIcon = ({ icon, ...props }) => {
  const displayed = displayIcon(icon);
  if (displayed.startsWith('lu:')) {
    const LucideIcon = LucideIcons[displayed.slice(3)];
    return LucideIcon ? <LucideIcon {...props} className={`rehab-workflow-icon ${props.className || ''}`.trim()} />
      : <i {...props} className={`pi pi-circle ${props.className || ''}`.trim()} />;
  }
  return <i {...props} className={`pi ${displayed} ${props.className || ''}`.trim()} />;
};
const richHtmlToPlainText = (html) => {
  const container = document.createElement('div');
  container.innerHTML = String(html || '');
  const blockTags = new Set(['DIV', 'P', 'LI']);
  const read = (node) => {
    if (node.nodeType === 3) return node.nodeValue || '';
    if (node.nodeName === 'BR') return '\n';
    const content = Array.from(node.childNodes || []).map(read).join('');
    return blockTags.has(node.nodeName) ? `${content}\n` : content;
  };
  return read(container).replace(/\n{3,}/g, '\n\n').trim();
};
export const plainTextToRichHtml = (text) => {
  const container = document.createElement('div');
  container.textContent = String(text || '');
  return container.innerHTML.replace(/\n/g, '<br>');
};
const escapeRichText = (text) => {
  const container = document.createElement('div');
  container.textContent = String(text || '');
  return container.innerHTML;
};
export const looksLikeWorkflowMarkdown = (text) => {
  const value = String(text || '');
  return /(^|\n)\s{0,3}(?:#{1,6}\s|[-*+]\s+|\d+[.)]\s+|>\s+|```|(?:---+|___+|\*\*\*+)\s*$)/m.test(value)
    || /(?:\*\*[^*\n]+\*\*|__[^_\n]+__|~~[^~\n]+~~|`[^`\n]+`|\[[^\]\n]+\]\(https?:\/\/[^)\s]+\))/i.test(value);
};
const markdownInlineToRichHtml = (text) => {
  const tokens = [];
  const token = (html) => `\uE000${tokens.push(html) - 1}\uE001`;
  let source = String(text || '')
    .replace(/`([^`\n]+)`/g, (_match, code) => token(`<span data-font="mono">${escapeRichText(code)}</span>`))
    .replace(/\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)/gi, (_match, label, url) => {
      const safeUrl = normalizeWorkflowLinkUrl(url);
      return safeUrl ? token(`<a href="${escapeRichText(safeUrl)}" target="_blank" rel="noopener noreferrer">${escapeRichText(label)}</a>`) : `${label} (${url})`;
    });
  source = escapeRichText(source)
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_\n]+)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/(^|[^_])_([^_\n]+)_(?!_)/g, '$1<em>$2</em>')
    .replace(/~~([^~\n]+)~~/g, '$1');
  return source.replace(/\uE000(\d+)\uE001/g, (_match, index) => tokens[Number(index)] || '');
};
export const markdownToWorkflowRichHtml = (text) => {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let paragraph = [];
  let listType = '';
  let listItems = [];
  let fenced = false;
  let codeLines = [];
  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push(`<div>${paragraph.map(markdownInlineToRichHtml).join('<br>')}</div>`); paragraph = [];
  };
  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(`<${listType}>${listItems.map((item) => `<li>${markdownInlineToRichHtml(item)}</li>`).join('')}</${listType}>`);
    listType = ''; listItems = [];
  };
  const flushCode = () => {
    if (!codeLines.length) return;
    blocks.push(`<div><span data-font="mono">${escapeRichText(codeLines.join('\n')).replace(/\n/g, '<br>')}</span></div>`); codeLines = [];
  };
  lines.forEach((line) => {
    if (/^\s*```/.test(line)) {
      flushParagraph(); flushList();
      if (fenced) flushCode();
      fenced = !fenced; return;
    }
    if (fenced) { codeLines.push(line); return; }
    const heading = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/.exec(line);
    const unordered = /^\s{0,3}[-*+]\s+(.+)$/.exec(line);
    const ordered = /^\s{0,3}\d+[.)]\s+(.+)$/.exec(line);
    if (heading) {
      flushParagraph(); flushList();
      const style = heading[1].length <= 2 ? 'title' : heading[1].length === 3 ? 'heading' : 'subheading';
      blocks.push(`<div><span data-text-style="${style}">${markdownInlineToRichHtml(heading[2])}</span></div>`); return;
    }
    if (/^\s{0,3}(?:---+|___+|\*\*\*+)\s*$/.test(line)) { flushParagraph(); flushList(); blocks.push('<hr>'); return; }
    if (unordered || ordered) {
      flushParagraph();
      const nextType = unordered ? 'ul' : 'ol';
      if (listType && listType !== nextType) flushList();
      listType = nextType; listItems.push((unordered || ordered)[1]); return;
    }
    if (!line.trim()) { flushParagraph(); flushList(); return; }
    if (listItems.length) flushList();
    paragraph.push(line.replace(/^\s{0,3}>\s?/, ''));
  });
  flushParagraph(); flushList(); flushCode();
  return sanitizeRichHtmlDraft(blocks.join(''));
};
const richHtmlWithMediaUrls = (html, mediaById) => {
  const container = document.createElement('div');
  container.innerHTML = html || '';
  container.querySelectorAll('img[data-media-id]').forEach((image) => {
    const media = mediaById[image.dataset.mediaId];
    if (media?.url) image.setAttribute('src', media.url);
    else image.removeAttribute('src');
  });
  return container.innerHTML;
};
// Font stacks live in RehabWorkflow.css ([data-font]); the backend sanitizer allows the same keys.
export const RICH_FONTS = [['default', 'Default'], ['brand', 'Logo'], ['sans', 'Sans'], ['serif', 'Serif'], ['mono', 'Mono'], ['rounded', 'Rounded']];
const RICH_FONT_KEYS = new Set(RICH_FONTS.map(([key]) => key));
export const DEFAULT_TEXT_STYLE_DEFAULTS = {
  body: { size: 10, color: '#f8fafc', font: 'default', weight: 700 },
  title: { size: 13, color: '#f8fafc', font: 'brand', weight: 800 },
  heading: { size: 11, color: '#67e8f9', font: 'brand', weight: 800 },
  subheading: { size: 10, color: '#f8fafc', font: 'brand', weight: 800 },
};
const TEXT_STYLE_DEFAULTS_PREFERENCE = '/identity/preferences/rehab_workflow.text_style_defaults';
const FONT_STACKS = {
  default: 'var(--font-family, "Segoe UI", sans-serif)', brand: '"Avenir Next","Segoe UI",sans-serif',
  sans: '"Helvetica Neue",Helvetica,Arial,sans-serif', serif: 'Georgia,"Times New Roman",serif',
  mono: '"SF Mono",Menlo,Consolas,monospace', rounded: 'ui-rounded,"SF Pro Rounded","Arial Rounded MT Bold",sans-serif',
};
export const normalizeTextStyleDefaults = (value) => {
  if (!value || typeof value !== 'object') return null;
  const normalized = {};
  for (const key of Object.keys(DEFAULT_TEXT_STYLE_DEFAULTS)) {
    const settings = value[key];
    if (!settings || !Number.isInteger(settings.size) || settings.size < 8 || settings.size > 32
        || !/^#[0-9a-f]{6}$/i.test(settings.color || '') || !RICH_FONT_KEYS.has(settings.font)
        || ![400, 500, 600, 700, 800, 900].includes(settings.weight)) return null;
    normalized[key] = { ...settings, color: settings.color.toLowerCase() };
  }
  return normalized;
};
const textStyleCssVariables = (defaults) => Object.fromEntries(Object.entries(defaults).flatMap(([key, settings]) => [
  [`--rehab-${key}-size`, `${settings.size}px`], [`--rehab-${key}-color`, settings.color],
  [`--rehab-${key}-font`, FONT_STACKS[settings.font]], [`--rehab-${key}-weight`, settings.weight],
]));
export const sanitizeRichHtmlDraft = (html) => {
  const container = document.createElement('div');
  container.innerHTML = String(html || '');
  const allowed = new Set(['P', 'DIV', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI', 'FONT', 'A', 'IMG', 'H2', 'H3', 'H4', 'HR', 'SPAN', 'BLOCKQUOTE']);
  Array.from(container.querySelectorAll('*')).forEach((element) => {
    if (!allowed.has(element.tagName)) { element.replaceWith(...element.childNodes); return; }
    const attributes = Object.fromEntries(Array.from(element.attributes).map(({ name, value }) => [name.toLowerCase(), value]));
    Array.from(element.attributes).forEach(({ name }) => element.removeAttribute(name));
    if (element.tagName === 'A' && /^https?:\/\/[^\s]+$/i.test(attributes.href || '')) {
      element.setAttribute('href', attributes.href); element.setAttribute('target', '_blank'); element.setAttribute('rel', 'noopener noreferrer');
    } else if (element.tagName === 'FONT') {
      if (/^#[0-9a-f]{6}$/i.test(attributes.color || '')) element.setAttribute('color', attributes.color.toLowerCase());
      const match = String(attributes.style || '').toLowerCase().match(/^font-size:\s*(8|9|[12][0-9]|3[0-2])px;?$/);
      if (match) element.setAttribute('style', `font-size: ${match[1]}px`);
    } else if (element.tagName === 'IMG') {
      if (!/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(attributes['data-media-id'] || '')) { element.remove(); return; }
      element.setAttribute('data-media-id', attributes['data-media-id'].toLowerCase());
      if (attributes['data-display-size'] === 'compact') element.setAttribute('data-display-size', 'compact');
      if (attributes.alt) element.setAttribute('alt', attributes.alt.slice(0, 200));
    } else if (element.tagName === 'SPAN') {
      if (['body', 'title', 'heading', 'subheading'].includes(attributes['data-text-style'])) element.setAttribute('data-text-style', attributes['data-text-style']);
      if (RICH_FONT_KEYS.has(attributes['data-font'])) element.setAttribute('data-font', attributes['data-font']);
    } else if (element.tagName === 'BLOCKQUOTE' && ['accent', 'soft', 'editorial', 'highlight', 'ribbon', 'minimal'].includes(attributes['data-quote-style'])) {
      element.setAttribute('data-quote-style', attributes['data-quote-style']);
    }
  });
  return container.innerHTML;
};

const WORKFLOW_HTML_BLOCK_TAGS = new Set(['div', 'p', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'blockquote', 'hr']);
export const formatWorkflowHtmlSource = (html) => {
  const source = String(html || '').trim();
  if (!source) return '';
  const tokens = source.match(/<!--[\s\S]*?-->|<[^>]+>|[^<]+/g) || [];
  const lines = []; let line = ''; let indent = 0;
  const flush = () => { if (line.trim()) lines.push(`${'  '.repeat(indent)}${line.trim()}`); line = ''; };
  tokens.forEach((token) => {
    const tag = token.match(/^<\/?\s*([a-z0-9-]+)/i)?.[1]?.toLowerCase();
    const closing = /^<\//.test(token); const selfClosing = /\/>$/.test(token) || ['br', 'hr', 'img'].includes(tag);
    const block = WORKFLOW_HTML_BLOCK_TAGS.has(tag);
    if (closing && block) { flush(); indent = Math.max(0, indent - 1); line += token; flush(); return; }
    if (block) { flush(); line += token; if (!selfClosing) { flush(); indent += 1; } else flush(); return; }
    line += token;
  });
  flush();
  return lines.join('\n');
};
export const workflowHtmlSourceOffsetForTextOffset = (source, textOffset) => {
  const html = String(source || ''); const target = Math.max(0, Number(textOffset) || 0); let visible = 0; let inTag = false;
  for (let index = 0; index < html.length; index += 1) {
    if (html[index] === '<') { inTag = true; continue; }
    if (inTag) { if (html[index] === '>') inTag = false; continue; }
    if (html[index] === '\n') { while (html[index + 1] === ' ') index += 1; continue; }
    if (visible >= target) return index;
    if (html[index] === '&') {
      const ending = html.indexOf(';', index + 1);
      if (ending > index && ending - index <= 12) { visible += 1; index = ending; continue; }
    }
    visible += 1;
  }
  return html.length;
};

const highlightedHtmlTag = (tag, key) => {
  const match = tag.match(/^(<\/?)([\w-]+)([\s\S]*?)(\/?>)$/);
  if (!match) return <span key={key} className="rehab-source-token--tag">{tag}</span>;
  const [, opening, name, attributes, ending] = match;
  const parts = []; let cursor = 0;
  const pattern = /(\s+)([\w:-]+)(\s*=\s*)("[^"]*"|'[^']*')/g; let attribute;
  while ((attribute = pattern.exec(attributes))) {
    if (attribute.index > cursor) parts.push(attributes.slice(cursor, attribute.index));
    parts.push(attribute[1], <span key={`${key}-a${cursor}`} className="rehab-source-token--attribute">{attribute[2]}</span>,
      attribute[3], <span key={`${key}-v${cursor}`} className="rehab-source-token--value">{attribute[4]}</span>);
    cursor = pattern.lastIndex;
  }
  if (cursor < attributes.length) parts.push(attributes.slice(cursor));
  return <span key={key} className="rehab-source-token--tag">{opening}<span className="rehab-source-token--name">{name}</span>{parts}{ending}</span>;
};
const highlightWorkflowHtmlSource = (source) => (String(source || '').split(/(<!--[\s\S]*?-->|<[^>]+>|&[a-z0-9#]+;)/gi).map((token, index) => {
  if (token.startsWith('<!--')) return <span key={index} className="rehab-source-token--comment">{token}</span>;
  if (token.startsWith('<')) return highlightedHtmlTag(token, index);
  if (token.startsWith('&')) return <span key={index} className="rehab-source-token--entity">{token}</span>;
  return token;
}));

const WorkflowRawHtmlEditor = ({ value, onChange }) => {
  const highlightRef = useRef(null); const gutterRef = useRef(null); const stageRef = useRef(null);
  const [activeLine, setActiveLine] = useState(0);
  const sync = (textarea) => {
    if (highlightRef.current) { highlightRef.current.scrollTop = textarea.scrollTop; highlightRef.current.scrollLeft = textarea.scrollLeft; }
    if (gutterRef.current) gutterRef.current.scrollTop = textarea.scrollTop;
    stageRef.current?.style.setProperty('--source-scroll-top', `${textarea.scrollTop}px`);
    setActiveLine(textarea.value.slice(0, textarea.selectionStart).split('\n').length - 1);
  };
  return <div className="rehab-source-editor rehab-block-source-editor" style={{ '--source-active-line': activeLine }}>
    <div ref={gutterRef} className="rehab-source-editor__gutter" aria-hidden="true">{String(value || '').split('\n').map((_, index) => <span key={index} className={index === activeLine ? 'is-active' : ''}>{index + 1}</span>)}</div>
    <div ref={stageRef} className="rehab-source-editor__stage"><i className="rehab-source-editor__active-line" aria-hidden="true" />
      <div className="rehab-source-editor__actions"><button type="button" aria-label="Format block HTML" title="Format HTML" onClick={() => onChange(formatWorkflowHtmlSource(value))}><i className="pi pi-align-left" aria-hidden="true" /></button></div>
      <pre ref={highlightRef} className="rehab-source-editor__highlight" aria-hidden="true"><code>{highlightWorkflowHtmlSource(value)}{'\n'}</code></pre>
      <textarea className="rehab-rich-editor__source" aria-label="Raw HTML block" value={value} spellCheck="false" wrap="off" onChange={(event) => { onChange(event.target.value); sync(event.target); }} onScroll={(event) => sync(event.currentTarget)} onClick={(event) => sync(event.currentTarget)} onKeyUp={(event) => sync(event.currentTarget)} />
    </div>
  </div>;
};

const blockId = () => `block-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const emptyWorkflowBlock = (type) => {
  if (type === 'heading') return { id: blockId(), type, level: 3, text: '' };
  if (type === 'list') return { id: blockId(), type, style: 'bullet', items: [''] };
  if (type === 'quote') return { id: blockId(), type, style: 'accent', text: '' };
  if (type === 'separator') return { id: blockId(), type };
  if (type === 'image') return { id: blockId(), type, media_id: '', caption: '', display_size: 'compact' };
  if (type === 'hardware') return { id: blockId(), type, name: '', price: '', purchase_url: '', media_id: '', features: [''] };
  if (type === 'product_comparison') return { id: blockId(), type, title: 'Product comparison', items: [
    { name: '', price: '', purchase_url: '', media_id: '', features: [''] },
  ] };
  if (type === 'raw_html') return { id: blockId(), type, body_html: '' };
  return { id: blockId(), type: 'paragraph', text: '' };
};
export const workflowHtmlToBlocks = (html) => {
  const container = document.createElement('div'); container.innerHTML = sanitizeRichHtmlDraft(html);
  const blocks = [];
  const addParagraph = (text) => {
    const normalized = String(text || '').replace(/\s+/g, ' ').trim();
    if (normalized) blocks.push({ ...emptyWorkflowBlock('paragraph'), text: normalized });
  };
  const addChildren = (parent) => {
    let inlineText = '';
    const flushInline = () => { addParagraph(inlineText); inlineText = ''; };
    Array.from(parent.childNodes).forEach((child) => {
      const childTag = child.nodeType === Node.ELEMENT_NODE ? child.tagName : '';
      const isStructural = child.nodeType === Node.ELEMENT_NODE
        && ['DIV', 'P', 'H2', 'H3', 'H4', 'UL', 'OL', 'BLOCKQUOTE', 'HR', 'IMG'].includes(childTag);
      if (isStructural) { flushInline(); addNode(child); }
      else if (childTag === 'BR') inlineText += '\n';
      else inlineText += child.textContent || '';
    });
    flushInline();
  };
  const addNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      addParagraph(node.textContent);
      return;
    }
    const tag = node.tagName;
    if (/^H[234]$/.test(tag)) blocks.push({ ...emptyWorkflowBlock('heading'), level: Number(tag[1]), text: node.textContent.trim() });
    else if (tag === 'UL' || tag === 'OL') blocks.push({ ...emptyWorkflowBlock('list'), style: tag === 'OL' ? 'numbered' : 'bullet', items: Array.from(node.children).map((item) => item.textContent.trim()) });
    else if (tag === 'BLOCKQUOTE') blocks.push({ ...emptyWorkflowBlock('quote'), style: node.dataset.quoteStyle || 'accent', text: node.textContent.trim() });
    else if (tag === 'HR') blocks.push(emptyWorkflowBlock('separator'));
    else if (tag === 'IMG' && node.dataset.mediaId) blocks.push({ ...emptyWorkflowBlock('image'), media_id: node.dataset.mediaId, caption: node.alt || '', display_size: node.dataset.displaySize === 'full' ? 'full' : 'compact' });
    else if (['P', 'DIV'].includes(tag) && node.querySelector('img,ul,ol,h2,h3,h4,blockquote,hr,div,p')) addChildren(node);
    else if (['P', 'DIV'].includes(tag)) addParagraph(node.textContent);
    else blocks.push({ ...emptyWorkflowBlock('raw_html'), body_html: node.outerHTML });
  };
  // Treat the sanitized document as one container so adjacent top-level text and
  // inline wrappers become a single paragraph instead of many raw-HTML blocks.
  addChildren(container);
  return blocks.length ? blocks : [emptyWorkflowBlock('paragraph')];
};
export const workflowHtmlToBlocksAtTextOffset = (html, textOffset) => {
  const container = document.createElement('div'); container.innerHTML = sanitizeRichHtmlDraft(html);
  const target = Math.max(0, Number.isFinite(textOffset) ? textOffset : container.textContent.length);
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let consumed = 0; let point = { node: container, offset: container.childNodes.length };
  for (let textNode = walker.nextNode(); textNode; textNode = walker.nextNode()) {
    const length = textNode.textContent.length;
    if (target <= consumed + length) {
      point = { node: textNode, offset: Math.max(0, target - consumed) }; break;
    }
    consumed += length;
  }
  const fragmentHtml = (range, boundary) => {
    const holder = document.createElement('div'); holder.append(range.cloneContents());
    // Range cloning keeps the split element's empty shell on each side. Remove only
    // empty shells touching the cut boundary; preserve intentional spacing elsewhere.
    while (holder.childNodes.length) {
      const edge = boundary === 'start' ? holder.firstChild : holder.lastChild;
      if (edge.nodeType === Node.TEXT_NODE && !edge.textContent.trim()) { edge.remove(); continue; }
      if (edge.nodeType === Node.ELEMENT_NODE && !edge.textContent.trim() && !edge.querySelector('img,hr,br')) { edge.remove(); continue; }
      break;
    }
    return sanitizeRichHtmlDraft(holder.innerHTML);
  };
  const beforeRange = document.createRange(); beforeRange.selectNodeContents(container); beforeRange.setEnd(point.node, point.offset);
  const afterRange = document.createRange(); afterRange.selectNodeContents(container); afterRange.setStart(point.node, point.offset);
  const beforeHtml = fragmentHtml(beforeRange, 'end'); const afterHtml = fragmentHtml(afterRange, 'start');
  const hasVisibleContent = (fragment) => {
    if (!fragment) return false;
    const holder = document.createElement('div'); holder.innerHTML = fragment;
    return Boolean(holder.textContent.trim() || holder.querySelector('img,hr'));
  };
  const blank = emptyWorkflowBlock('paragraph'); const blocks = [];
  if (hasVisibleContent(beforeHtml)) blocks.push({ ...emptyWorkflowBlock('raw_html'), body_html: beforeHtml });
  blocks.push(blank);
  if (hasVisibleContent(afterHtml)) blocks.push({ ...emptyWorkflowBlock('raw_html'), body_html: afterHtml });
  return { blocks, activeBlockId: blank.id };
};
export const normalizeWorkflowPastedHtml = (html) => {
  const container = document.createElement('div');
  container.innerHTML = sanitizeRichHtmlDraft(html);
  // Clipboard HTML commonly contains empty layout shells and repeated breaks.
  // They are harmless in source form but become large vertical gaps once the
  // fragment is rendered inside a compact workflow node.
  Array.from(container.querySelectorAll('p,div,h2,h3,h4,blockquote,li,span,font,strong,b,em,i,u,a')).reverse().forEach((element) => {
    if (!element.textContent.replace(/\u00a0/g, ' ').trim() && !element.querySelector('img,hr')) element.remove();
  });
  Array.from(container.querySelectorAll('br')).forEach((breakElement) => {
    let previous = breakElement.previousSibling;
    while (previous?.nodeType === Node.TEXT_NODE && !previous.textContent.trim()) previous = previous.previousSibling;
    if (previous?.nodeName === 'BR') breakElement.remove();
  });
  while (container.firstChild?.nodeType === Node.TEXT_NODE && !container.firstChild.textContent.trim()) container.firstChild.remove();
  while (container.lastChild?.nodeType === Node.TEXT_NODE && !container.lastChild.textContent.trim()) container.lastChild.remove();
  if (container.firstElementChild?.tagName === 'BR') container.firstElementChild.remove();
  if (container.lastElementChild?.tagName === 'BR') container.lastElementChild.remove();
  return sanitizeRichHtmlDraft(container.innerHTML);
};
export const workflowHtmlFromBlockPaste = (clipboardData) => {
  const clipboardHtml = clipboardData?.getData?.('text/html') || '';
  const clipboardText = clipboardData?.getData?.('text/plain') || '';
  let candidate = (clipboardHtml || clipboardText).trim();
  if (!candidate) return null;
  candidate = candidate.replace(/^```(?:html|xml)?\s*/i, '').replace(/\s*```$/i, '').trim();
  // Chat responses sometimes escape opening angle brackets with a backslash.
  candidate = candidate.replace(/\\(?=<\/?[a-z])/gi, '');
  const encoded = document.createElement('div'); encoded.innerHTML = candidate;
  const decodedCandidate = encoded.textContent.trim();
  if (/<\/?[a-z][^>]*>/i.test(decodedCandidate)) candidate = decodedCandidate;
  if (!/<\/?[a-z][^>]*>/i.test(candidate)) return null;
  const sanitized = normalizeWorkflowPastedHtml(candidate);
  return sanitized.trim() ? formatWorkflowHtmlSource(sanitized) : null;
};
const workflowBlocksPlainText = (blocks) => blocks.flatMap((block) => {
  if (['paragraph', 'heading', 'quote'].includes(block.type)) return [block.text || ''];
  if (block.type === 'list') return block.items || [];
  if (block.type === 'hardware') return [block.name, block.price, ...(block.features || [])];
  if (block.type === 'product_comparison') return [block.title, ...(block.items || []).flatMap((item) => [item.name, item.price, ...(item.features || [])])];
  if (block.type === 'raw_html') return [richHtmlToPlainText(block.body_html)];
  return [block.caption || ''];
}).filter(Boolean).join('\n').slice(0, NODE_DETAIL_MAX);

export const workflowBlocksToHtml = (blocks) => {
  const container = document.createElement('div');
  const appendTextElement = (tag, text) => {
    const element = document.createElement(tag); element.textContent = text || ''; container.append(element);
  };
  (blocks || []).forEach((block) => {
    if (block.type === 'raw_html') {
      const holder = document.createElement('div'); holder.innerHTML = sanitizeRichHtmlDraft(block.body_html);
      container.append(...holder.childNodes); return;
    }
    if (block.type === 'paragraph') { appendTextElement('p', block.text); return; }
    if (block.type === 'heading') { appendTextElement(`h${[2, 3, 4].includes(block.level) ? block.level : 3}`, block.text); return; }
    if (block.type === 'quote') {
      const quote = document.createElement('blockquote'); quote.dataset.quoteStyle = block.style || 'accent'; quote.textContent = block.text || ''; container.append(quote); return;
    }
    if (block.type === 'separator') { container.append(document.createElement('hr')); return; }
    if (block.type === 'list') {
      const list = document.createElement(block.style === 'numbered' ? 'ol' : 'ul');
      (block.items || []).forEach((item) => { const row = document.createElement('li'); row.textContent = item; list.append(row); });
      container.append(list); return;
    }
    if (block.type === 'image') {
      if (!block.media_id) return;
      const image = document.createElement('img'); image.dataset.mediaId = block.media_id;
      if (block.display_size === 'compact') image.dataset.displaySize = 'compact';
      if (block.caption) image.alt = block.caption; container.append(image); return;
    }
    const products = block.type === 'hardware' ? [block] : block.type === 'product_comparison' ? block.items || [] : [];
    if (!products.length) return;
    if (block.type === 'product_comparison' && block.title) appendTextElement('h3', block.title);
    products.forEach((product) => {
      const section = document.createElement('div');
      const title = document.createElement('strong'); title.textContent = [product.name, product.price].filter(Boolean).join(' — '); section.append(title);
      if (product.media_id) { const image = document.createElement('img'); image.dataset.mediaId = product.media_id; image.dataset.displaySize = 'compact'; image.alt = product.name || ''; section.append(image); }
      if ((product.features || []).some(Boolean)) { const list = document.createElement('ul'); product.features.filter(Boolean).forEach((feature) => { const row = document.createElement('li'); row.textContent = feature; list.append(row); }); section.append(list); }
      if (product.purchase_url) { const link = document.createElement('a'); link.href = product.purchase_url; link.textContent = 'View product'; section.append(link); }
      container.append(section);
    });
  });
  return sanitizeRichHtmlDraft(container.innerHTML);
};

const WorkflowBlocksView = ({ blocks, mediaById = {} }) => <div className="rehab-block-view rehab-content-surface">{(blocks || []).map((block) => {
  if (block.type === 'paragraph') return <p key={block.id}>{block.text}</p>;
  if (block.type === 'heading') { const Tag = `h${block.level}`; return <Tag key={block.id}>{block.text}</Tag>; }
  if (block.type === 'list') { const Tag = block.style === 'numbered' ? 'ol' : 'ul'; return <Tag key={block.id}>{block.items.map((item, index) => <li key={index}>{item}</li>)}</Tag>; }
  if (block.type === 'quote') return <blockquote key={block.id} data-quote-style={block.style}>{block.text}</blockquote>;
  if (block.type === 'separator') return <hr key={block.id} />;
  if (block.type === 'image') { const media = mediaById[block.media_id]; return <figure key={block.id}>{media?.url ? <img src={media.url} data-media-id={block.media_id} data-display-size={block.display_size === 'full' ? 'full' : 'compact'} alt={block.caption || media.original_name || 'Workflow attachment'} /> : null}{block.caption ? <figcaption>{block.caption}</figcaption> : null}</figure>; }
  if (block.type === 'hardware') return <article key={block.id} className="rehab-hardware-block"><strong>{block.name || 'Hardware option'}</strong><span>{block.price}</span>{mediaById[block.media_id]?.url ? <img src={mediaById[block.media_id].url} alt={block.name} /> : null}<ul>{block.features.filter(Boolean).map((feature, index) => <li key={index}>{feature}</li>)}</ul>{block.purchase_url ? <a href={block.purchase_url} target="_blank" rel="noopener noreferrer">View product</a> : null}</article>;
  if (block.type === 'product_comparison') return <section key={block.id} className="rehab-product-comparison"><strong>{block.title}</strong>{block.items.map((item, index) => <article key={index}><span>{index + 1}</span><div><strong>{item.name || 'Product'}</strong><small>{item.price}</small><ul>{item.features.filter(Boolean).map((feature, featureIndex) => <li key={featureIndex}>{feature}</li>)}</ul>{item.purchase_url ? <a href={item.purchase_url} target="_blank" rel="noopener noreferrer">View product</a> : null}</div>{mediaById[item.media_id]?.url ? <img src={mediaById[item.media_id].url} alt={item.name} /> : null}</article>)}</section>;
  if (block.type === 'raw_html') return <div key={block.id} className="rehab-block-view__legacy rehab-node-rich-text" dangerouslySetInnerHTML={{ __html: richHtmlWithMediaUrls(sanitizeRichHtmlDraft(block.body_html), mediaById) }} />;
  return null;
})}</div>;

const WorkflowBlocksEditor = ({ initialBlocks, initialActiveBlockId, mediaById, onSave, onCancel }) => {
  const [blocks, setBlocks] = useState(initialBlocks);
  const [addType, setAddType] = useState('paragraph');
  const [blockError, setBlockError] = useState('');
  const [slashBlockId, setSlashBlockId] = useState(null);
  const [activeBlockId, setActiveBlockId] = useState(initialActiveBlockId || initialBlocks[0]?.id || null);
  const [showAllBlocks, setShowAllBlocks] = useState(false);
  const [blockCollapseOverrides, setBlockCollapseOverrides] = useState(() => new Map());
  const blockElementsRef = useRef(new Map());
  const pendingBlockFocusRef = useRef(initialActiveBlockId || null);
  const undoRef = useRef([]);
  const change = (next) => { undoRef.current.push(blocks); setBlocks(next); setSlashBlockId(null); };
  const update = (id, values) => change(blocks.map((block) => block.id === id ? { ...block, ...values } : block));
  const move = (index, direction) => { const target = index + direction; if (target < 0 || target >= blocks.length) return; const next = [...blocks]; [next[index], next[target]] = [next[target], next[index]]; change(next); };
  const mediaOptions = Object.values(mediaById || {});
  const foundActiveIndex = blocks.findIndex((block) => block.id === activeBlockId);
  const activeBlockIndex = foundActiveIndex >= 0 ? foundActiveIndex : 0;
  const activeBlock = blocks[activeBlockIndex] || null;
  const focusStart = Math.max(0, activeBlockIndex - 1);
  const focusEnd = Math.min(blocks.length, activeBlockIndex + 2);
  const visibleBlocks = (showAllBlocks ? blocks.map((block, index) => ({ block, index }))
    : blocks.slice(focusStart, focusEnd).map((block, offset) => ({ block, index: focusStart + offset })));
  useEffect(() => {
    const id = pendingBlockFocusRef.current;
    if (!id) return;
    const section = blockElementsRef.current.get(id);
    if (!section) return;
    (section.querySelector('textarea, input, select') || section).focus({ preventScroll: true });
    pendingBlockFocusRef.current = null;
  }, [blocks, activeBlockId]);
  const toggleBlockCollapsed = (id, collapsed) => setBlockCollapseOverrides((current) => {
    const next = new Map(current); next.set(id, !collapsed); return next;
  });
  const mediaSelect = (label, value, onChange) => <select aria-label={label} value={value || ''} onChange={(event) => onChange(event.target.value)}><option value="">No image</option>{mediaOptions.map((media) => <option key={media.media_id} value={media.media_id}>{media.original_name || media.media_id}</option>)}</select>;
  const productFields = (block, item, onChange, prefix = '') => <div className="rehab-block-product-fields">
    <input aria-label={`${prefix}Product name`} value={item.name} placeholder="Product name" onChange={(event) => onChange({ ...item, name: event.target.value })} />
    <input aria-label={`${prefix}Price`} value={item.price} placeholder="Price" onChange={(event) => onChange({ ...item, price: event.target.value })} />
    <input aria-label={`${prefix}Purchase URL`} value={item.purchase_url} placeholder="https://…" onChange={(event) => onChange({ ...item, purchase_url: event.target.value })} />
    {mediaSelect(`${prefix}Product image`, item.media_id, (media_id) => onChange({ ...item, media_id }))}
    <textarea aria-label={`${prefix}Features`} value={(item.features || []).join('\n')} placeholder="One feature per line" onChange={(event) => onChange({ ...item, features: event.target.value.split('\n') })} />
  </div>;
  return <div className="rehab-block-editor" onWheel={(event) => event.stopPropagation()} onKeyDown={(event) => { if (event.key.toLowerCase() === 'z' && (event.ctrlKey || event.metaKey) && undoRef.current.length) { event.preventDefault(); setBlocks(undoRef.current.pop()); } }}>
    <div className="rehab-block-editor__top"><span><strong>BLOCK DOCUMENT</strong><small>{blocks.length} block{blocks.length === 1 ? '' : 's'}</small></span><button type="button" aria-pressed={showAllBlocks} onClick={() => setShowAllBlocks((current) => !current)}><i className={`pi ${showAllBlocks ? 'pi-compress' : 'pi-list'}`} aria-hidden="true" />{showAllBlocks ? 'Focus' : 'Show all'}</button></div>
    <div className="rehab-block-editor__workspace">
      <div className="rehab-block-editor__builder">
    <div className="rehab-block-editor__list" aria-label="Content blocks">{visibleBlocks.map(({ block, index }) => { const collapsed = activeBlock?.id !== block.id && (blockCollapseOverrides.has(block.id) ? blockCollapseOverrides.get(block.id) : !showAllBlocks); return <section key={block.id} ref={(element) => { if (element) blockElementsRef.current.set(block.id, element); else blockElementsRef.current.delete(block.id); }} tabIndex={-1} data-block-id={block.id} className={`rehab-content-block${activeBlock?.id === block.id ? ' rehab-content-block--active' : ''}${collapsed ? ' rehab-content-block--collapsed' : ''}`} onFocusCapture={(event) => { if (!event.target.closest('button')) setActiveBlockId(block.id); }} onPointerDown={(event) => { if (!event.target.closest('button')) setActiveBlockId(block.id); }} onBlur={(event) => {
      if (slashBlockId === block.id && !event.currentTarget.contains(event.relatedTarget)) setSlashBlockId(null);
    }}>
      <header><span><i className="pi pi-bars" aria-hidden="true" />{block.type.replace('_', ' ')}{collapsed ? <small>{workflowBlocksPlainText([block]).slice(0, 72) || 'Empty block'}</small> : null}</span><div><button type="button" aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${block.type} block`} aria-expanded={!collapsed} disabled={activeBlock?.id === block.id} onClick={() => toggleBlockCollapsed(block.id, collapsed)}><i className={`pi ${collapsed ? 'pi-chevron-down' : 'pi-chevron-up'}`} /></button><button type="button" aria-label={`Move ${block.type} up`} title="Move up" disabled={!index} onClick={() => move(index, -1)}><i className="pi pi-arrow-up" /></button><button type="button" aria-label={`Move ${block.type} down`} title="Move down" disabled={index === blocks.length - 1} onClick={() => move(index, 1)}><i className="pi pi-arrow-down" /></button><button type="button" aria-label={`Duplicate ${block.type}`} onClick={() => { const next = [...blocks]; next.splice(index + 1, 0, { ...block, id: blockId() }); change(next); }}><i className="pi pi-copy" /></button><button type="button" aria-label={`Delete ${block.type}`} onClick={() => change(blocks.filter((item) => item.id !== block.id))}><i className="pi pi-trash" /></button></div></header>
      {!collapsed ? <div className="rehab-content-block__body">
      {block.type === 'paragraph' ? <><textarea aria-label="Paragraph text" value={block.text} placeholder="Type or paste HTML · / for blocks" onPaste={(event) => {
        if (block.text.trim()) return;
        const pastedHtml = workflowHtmlFromBlockPaste(event.clipboardData);
        if (!pastedHtml) return;
        event.preventDefault();
        change(blocks.map((item) => item.id === block.id ? { id: block.id, type: 'raw_html', body_html: pastedHtml } : item));
      }} onChange={(event) => update(block.id, { text: event.target.value })} onKeyDown={(event) => {
        if (event.key === '/' && !block.text && event.currentTarget.selectionStart === 0) { event.preventDefault(); setSlashBlockId(block.id); }
        if (event.key === 'Escape') setSlashBlockId(null);
      }} />{slashBlockId === block.id ? <div className="rehab-block-slash-menu" role="menu" aria-label="Add block" onKeyDown={(event) => {
        const choices = Array.from(event.currentTarget.querySelectorAll('[role="menuitem"]'));
        const current = choices.indexOf(document.activeElement);
        if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1
            : (current + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + choices.length) % choices.length;
          choices[next]?.focus({ preventScroll: true });
        } else if (event.key === 'Escape') {
          event.preventDefault(); setSlashBlockId(null);
          event.currentTarget.closest('.rehab-content-block')?.querySelector('textarea')?.focus({ preventScroll: true });
        }
      }}>{[
        ['paragraph', 'Paragraph', 'pi-align-left'], ['heading', 'Heading', 'pi-heading'], ['list', 'List', 'pi-list'],
        ['quote', 'Quote', 'pi-comment'], ['separator', 'Divider', 'pi-minus'], ['image', 'Image', 'pi-image'],
        ['hardware', 'Hardware option', 'pi-box'], ['product_comparison', 'Product comparison', 'pi-table'], ['raw_html', 'HTML', 'pi-code'],
      ].map(([type, label, icon], choiceIndex) => <button key={type} type="button" role="menuitem" autoFocus={!choiceIndex} aria-label={`Insert ${label} block`} onClick={() => change(blocks.map((item) => item.id === block.id ? { ...emptyWorkflowBlock(type), id: block.id } : item))}><i className={`pi ${icon}`} aria-hidden="true" /><span>{label}</span></button>)}</div> : null}</> : null}
      {block.type === 'heading' ? <div className="rehab-block-inline"><select aria-label="Heading level" value={block.level} onChange={(event) => update(block.id, { level: Number(event.target.value) })}><option value="2">Title</option><option value="3">Heading</option><option value="4">Subheading</option></select><input aria-label="Heading text" value={block.text} onChange={(event) => update(block.id, { text: event.target.value })} /></div> : null}
      {block.type === 'list' ? <div className="rehab-block-inline"><select aria-label="List style" value={block.style} onChange={(event) => update(block.id, { style: event.target.value })}><option value="bullet">Bullets</option><option value="numbered">Numbers</option></select><textarea aria-label="List items" value={block.items.join('\n')} onChange={(event) => update(block.id, { items: event.target.value.split('\n') })} /></div> : null}
      {block.type === 'quote' ? <div className="rehab-block-inline"><select aria-label="Quote appearance" value={block.style} onChange={(event) => update(block.id, { style: event.target.value })}>{['accent','soft','editorial','highlight','ribbon','minimal'].map((style) => <option key={style}>{style}</option>)}</select><textarea aria-label="Quote text" value={block.text} onChange={(event) => update(block.id, { text: event.target.value })} /></div> : null}
      {block.type === 'separator' ? <hr /> : null}
      {block.type === 'image' ? <div className="rehab-block-image-fields">{mediaSelect('Block image', block.media_id, (media_id) => update(block.id, { media_id }))}<select aria-label="Image size" value={block.display_size || 'compact'} onChange={(event) => update(block.id, { display_size: event.target.value })}><option value="compact">Compact</option><option value="full">Full width</option></select><input aria-label="Image caption" value={block.caption} placeholder="Caption" onChange={(event) => update(block.id, { caption: event.target.value })} /></div> : null}
      {block.type === 'hardware' ? productFields(block, block, (item) => update(block.id, item)) : null}
      {block.type === 'product_comparison' ? <div className="rehab-block-comparison"><input aria-label="Comparison title" value={block.title} onChange={(event) => update(block.id, { title: event.target.value })} />{block.items.map((item, itemIndex) => <fieldset key={itemIndex}><legend>Product {itemIndex + 1}</legend>{productFields(block, item, (nextItem) => update(block.id, { items: block.items.map((candidate, candidateIndex) => candidateIndex === itemIndex ? nextItem : candidate) }), `Product ${itemIndex + 1} `)}<button type="button" disabled={block.items.length === 1} onClick={() => update(block.id, { items: block.items.filter((_, candidateIndex) => candidateIndex !== itemIndex) })}>Remove product</button></fieldset>)}<button type="button" onClick={() => update(block.id, { items: [...block.items, { name: '', price: '', purchase_url: '', media_id: '', features: [''] }] })}><i className="pi pi-plus" /> Add product</button></div> : null}
      {block.type === 'raw_html' ? <WorkflowRawHtmlEditor value={block.body_html} onChange={(body_html) => update(block.id, { body_html })} /> : null}
      </div> : null}
    </section>; })}</div>
    <div className="rehab-block-add"><select aria-label="New block type" value={addType} onChange={(event) => setAddType(event.target.value)}>{['paragraph','heading','list','quote','separator','image','hardware','product_comparison','raw_html'].map((type) => <option key={type} value={type}>{type.replace('_', ' ')}</option>)}</select><button type="button" onClick={() => { const next = emptyWorkflowBlock(addType); const insertAt = activeBlock ? activeBlockIndex + 1 : blocks.length; const updated = [...blocks]; updated.splice(insertAt, 0, next); pendingBlockFocusRef.current = next.id; change(updated); setActiveBlockId(next.id); }}><i className="pi pi-plus" /> Add block</button></div>
      </div>
      <aside className="rehab-block-preview" aria-label="Selected block preview">
        <header><strong>LIVE PREVIEW</strong><span>{activeBlock ? activeBlock.type.replace('_', ' ') : 'Empty'}</span></header>
        <div className="rehab-block-preview__body" aria-live="polite">{activeBlock ? <WorkflowBlocksView blocks={[activeBlock]} mediaById={mediaById} /> : <p>Add a block to begin.</p>}</div>
      </aside>
    </div>
    {blockError ? <p className="rehab-rich-editor__error" role="alert">{blockError}</p> : null}
    <div className="rehab-node-detail-actions"><button type="button" onClick={onCancel}>Cancel</button><button type="button" className="rehab-node-detail-save" onClick={() => {
      const invalidImage = blocks.find((block) => block.type === 'image' && !block.media_id);
      const invalidUrl = blocks.find((block) => [block.type === 'hardware' ? block : null, ...(block.type === 'product_comparison' ? block.items : [])].filter(Boolean).some((item) => item.purchase_url && !/^https?:\/\/[^\s]+$/i.test(item.purchase_url)));
      if (invalidImage) { setBlockError('Choose an uploaded image or remove the empty image block.'); return; }
      if (invalidUrl) { setBlockError('Product links must be complete HTTP or HTTPS addresses.'); return; }
      setBlockError(''); onSave({ detail: workflowBlocksPlainText(blocks), rich_content: { version: 2, blocks } });
    }}>Save blocks</button></div>
  </div>;
};

export const insertWorkflowRichHtmlAtRange = (editor, range, html) => {
  if (!editor || !range || !editor.contains(range.commonAncestorContainer)) return null;
  const container = document.createElement('div');
  container.innerHTML = sanitizeRichHtmlDraft(html);
  const fragment = document.createDocumentFragment();
  let lastNode = null;
  while (container.firstChild) { lastNode = container.firstChild; fragment.append(container.firstChild); }
  if (!lastNode) return null;
  range.deleteContents(); range.insertNode(fragment);
  const caret = document.createRange(); caret.setStartAfter(lastNode); caret.collapse(true);
  const selection = window.getSelection();
  selection?.removeAllRanges(); selection?.addRange(caret);
  return caret;
};

export const workflowClipboardHtmlForRange = (editor, range) => {
  if (!editor || !range || range.collapsed || !editor.contains(range.commonAncestorContainer)) return '';
  const holder = document.createElement('div');
  let content = range.cloneContents();
  const commonElement = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
    ? range.commonAncestorContainer : range.commonAncestorContainer.parentElement;
  // Range.cloneContents() omits inline ancestors when only part of their text is
  // selected. Rebuild that chain so copying part of a link remains a link.
  const inlineAncestors = [];
  for (let element = commonElement; element && element !== editor; element = element.parentElement) {
    if (['A', 'STRONG', 'B', 'EM', 'I', 'U', 'FONT', 'SPAN'].includes(element.tagName)
      && element.contains(range.startContainer) && element.contains(range.endContainer)) inlineAncestors.push(element);
  }
  inlineAncestors.forEach((element) => {
    const wrapper = element.cloneNode(false); wrapper.append(content); content = document.createDocumentFragment(); content.append(wrapper);
  });
  holder.append(content);
  return sanitizeRichHtmlDraft(holder.innerHTML);
};

export const clearWorkflowFormattingAtRange = (editor, range) => {
  if (!editor || !range || range.collapsed || !editor.contains(range.commonAncestorContainer)) return null;
  const cleared = document.createElement('span');
  cleared.dataset.textStyle = 'body';
  cleared.dataset.font = 'default';
  const content = range.extractContents();
  Array.from(content.querySelectorAll('strong,b,em,i,u,font')).reverse().forEach((element) => element.replaceWith(...element.childNodes));
  Array.from(content.querySelectorAll('h2,h3,h4')).reverse().forEach((element) => {
    const block = document.createElement('div'); block.append(...element.childNodes); element.replaceWith(block);
  });
  content.querySelectorAll('span').forEach((element) => {
    element.removeAttribute('data-text-style'); element.removeAttribute('data-font'); element.removeAttribute('style');
    if (!element.attributes.length) element.replaceWith(...element.childNodes);
  });
  cleared.append(content); range.insertNode(cleared);
  const next = document.createRange(); next.selectNodeContents(cleared);
  const selection = window.getSelection(); selection?.removeAllRanges(); selection?.addRange(next);
  return next;
};

export const applyWorkflowQuoteStyleAtRange = (editor, range, style) => {
  const quoteStyles = ['accent', 'soft', 'editorial', 'highlight', 'ribbon', 'minimal'];
  if (!editor || !range || !editor.contains(range.commonAncestorContainer)
      || ![...quoteStyles, 'plain'].includes(style) || (range.collapsed && style !== 'plain')) return null;
  const rangeElement = (node) => node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  const ancestor = rangeElement(range.startContainer)?.closest?.('blockquote[data-quote-style]')
    || rangeElement(range.commonAncestorContainer)?.closest?.('blockquote[data-quote-style]');
  const intersecting = Array.from(editor.querySelectorAll('blockquote[data-quote-style]'))
    .filter((quote) => range.intersectsNode(quote));
  const existing = ancestor && editor.contains(ancestor) ? ancestor : intersecting[0];
  if (existing && editor.contains(existing) && range.intersectsNode(existing)) {
    if (style !== 'plain') {
      existing.dataset.quoteStyle = style;
      existing.querySelectorAll('blockquote').forEach((nested) => nested.replaceWith(...nested.childNodes));
      const next = document.createRange(); next.selectNodeContents(existing);
      const selection = window.getSelection(); selection?.removeAllRanges(); selection?.addRange(next);
      return next;
    }
    const quotes = intersecting.length ? intersecting : [existing];
    let first = quotes[0].firstChild; let last = quotes[quotes.length - 1].lastChild;
    if (!first || !last) { quotes.forEach((quote) => quote.remove()); return null; }
    quotes.reverse().forEach((quote) => quote.replaceWith(...quote.childNodes));
    while (first.parentElement?.closest?.('blockquote[data-quote-style]')) {
      const wrapper = first.parentElement.closest('blockquote[data-quote-style]');
      if (!editor.contains(wrapper)) break;
      wrapper.replaceWith(...wrapper.childNodes);
    }
    const next = document.createRange(); next.setStartBefore(first); next.setEndAfter(last);
    const selection = window.getSelection(); selection?.removeAllRanges(); selection?.addRange(next);
    return next;
  }
  if (style === 'plain') return null;
  const quote = document.createElement('blockquote'); quote.dataset.quoteStyle = style;
  const content = range.extractContents();
  Array.from(content.querySelectorAll('blockquote')).reverse().forEach((element) => element.replaceWith(...element.childNodes));
  quote.append(content); range.insertNode(quote);
  const next = document.createRange(); next.selectNodeContents(quote);
  const selection = window.getSelection(); selection?.removeAllRanges(); selection?.addRange(next);
  return next;
};

const textStyleForNode = (node, editor) => {
  const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  const styled = element?.closest?.('[data-text-style],h2,h3,h4');
  if (!styled || !editor?.contains(styled)) return 'body';
  return styled.dataset?.textStyle
    || ({ H2: 'title', H3: 'heading', H4: 'subheading' }[styled.tagName])
    || 'body';
};

export const textStyleForRange = (range, editor) => {
  if (!range || !editor?.contains(range.commonAncestorContainer)) return 'body';
  const selectedStyles = [];
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();
  while (textNode) {
    if (textNode.textContent?.trim() && range.intersectsNode(textNode)) {
      selectedStyles.push(textStyleForNode(textNode, editor));
    }
    textNode = walker.nextNode();
  }
  if (selectedStyles.length) return selectedStyles[0];
  return textStyleForNode(range.startContainer, editor);
};
const fontForNode = (node, editor) => {
  const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  const styled = element?.closest?.('[data-font]');
  return styled && editor?.contains(styled) ? styled.dataset.font : 'default';
};
export const fontForRange = (range, editor) => {
  if (!range || !editor?.contains(range.commonAncestorContainer)) return 'default';
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  for (let textNode = walker.nextNode(); textNode; textNode = walker.nextNode()) {
    if (textNode.textContent?.trim() && range.intersectsNode(textNode)) return fontForNode(textNode, editor);
  }
  return fontForNode(range.startContainer, editor);
};

export const workflowImageDropRange = (editor, savedRange, preferSavedCaret, clientX, clientY) => {
  if (!editor) return null;
  if (preferSavedCaret && savedRange?.collapsed && editor.contains(savedRange.commonAncestorContainer)) {
    return savedRange.cloneRange();
  }
  let pointRange = null;
  if (document.caretRangeFromPoint) pointRange = document.caretRangeFromPoint(clientX, clientY);
  else if (document.caretPositionFromPoint) {
    const point = document.caretPositionFromPoint(clientX, clientY);
    if (point) { pointRange = document.createRange(); pointRange.setStart(point.offsetNode, point.offset); pointRange.collapse(true); }
  }
  if (pointRange && editor.contains(pointRange.commonAncestorContainer)) return pointRange;
  if (savedRange && editor.contains(savedRange.commonAncestorContainer)) return savedRange.cloneRange();
  const endRange = document.createRange(); endRange.selectNodeContents(editor); endRange.collapse(false);
  return endRange;
};

export const normalizeWorkflowLinkUrl = (value) => {
  const candidate = String(value || '').trim();
  if (!candidate) return '';
  if (/^[a-z][a-z\d+.-]*:/i.test(candidate) && !/^https?:\/\//i.test(candidate)) return '';
  const withProtocol = /^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`;
  try {
    const parsed = new URL(withProtocol);
    return ['http:', 'https:'].includes(parsed.protocol) && parsed.hostname ? parsed.href : '';
  } catch (_error) {
    return '';
  }
};

export const workflowUrlFromPastedText = (value) => {
  const candidate = String(value || '').trim();
  if (!candidate || /[\r\n]/.test(candidate)) return '';
  const looksLikeUrl = /^https?:\/\/[^\s]+$/i.test(candidate)
    || /^(?:www\.)?(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?\.)+[a-z]{2,}(?::\d+)?(?:[/?#][^\s]*)?$/i.test(candidate);
  return looksLikeUrl ? normalizeWorkflowLinkUrl(candidate) : '';
};

export const insertWorkflowLinkAtRange = (editor, range, name, url) => {
  if (!editor || !range || !editor.contains(range.commonAncestorContainer)) return null;
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.textContent = name;
  range.deleteContents();
  range.insertNode(link);
  const caret = document.createRange();
  caret.setStartAfter(link); caret.collapse(true);
  const selection = window.getSelection();
  selection?.removeAllRanges(); selection?.addRange(caret);
  return caret;
};

// Account-owned swatch palette (rentals.user_preferences).
export const DEFAULT_TEXT_COLORS = ['#f8fafc', '#67e8f9', '#5eead4', '#fbbf24', '#fb7185'];
export const hsvToHex = (hue, saturation, value) => {
  const h = ((Number(hue) % 360) + 360) % 360;
  const s = Math.min(1, Math.max(0, Number(saturation)));
  const v = Math.min(1, Math.max(0, Number(value)));
  const chroma = v * s;
  const section = h / 60;
  const intermediate = chroma * (1 - Math.abs((section % 2) - 1));
  const [red, green, blue] = section < 1 ? [chroma, intermediate, 0]
    : section < 2 ? [intermediate, chroma, 0]
      : section < 3 ? [0, chroma, intermediate]
        : section < 4 ? [0, intermediate, chroma]
          : section < 5 ? [intermediate, 0, chroma] : [chroma, 0, intermediate];
  const match = v - chroma;
  return `#${[red, green, blue].map((channel) => Math.round((channel + match) * 255).toString(16).padStart(2, '0')).join('')}`;
};
export const hexToHsv = (hex) => {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || ''));
  if (!match) return { h: 0, s: 0, v: 1 };
  const [red, green, blue] = match.slice(1).map((part) => parseInt(part, 16) / 255);
  const max = Math.max(red, green, blue); const min = Math.min(red, green, blue); const delta = max - min;
  let h = 0;
  if (delta && max === red) h = 60 * (((green - blue) / delta) % 6);
  else if (delta && max === green) h = 60 * (((blue - red) / delta) + 2);
  else if (delta) h = 60 * (((red - green) / delta) + 4);
  return { h: h < 0 ? h + 360 : h, s: max ? delta / max : 0, v: max };
};
const TEXT_COLORS_PREFERENCE = '/identity/preferences/rehab_workflow.text_colors';
export const normalizeTextColors = (value) => (Array.isArray(value)
  ? [...new Set(value.map((color) => String(color).toLowerCase()).filter((color) => /^#[0-9a-f]{6}$/.test(color)))]
  : null);

const RichNodeEditor = ({ node, propertyId, mediaById, onMediaAdded, onSave, onCancel, initialViewport, textColors = DEFAULT_TEXT_COLORS, onTextColorsChange, textStyleDefaults = DEFAULT_TEXT_STYLE_DEFAULTS, onTextStyleDefaultsChange, onOpen, paletteError = '', styleDefaultsError = '' }) => {
  const editorRef = useRef(null);
  const sourceRef = useRef(null);
  const sourceHighlightRef = useRef(null);
  const sourceGutterRef = useRef(null);
  const sourceStageRef = useRef(null);
  const toolbarRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onOpen?.(); }, []);
  const fileInputRef = useRef(null);
  const selectionRangeRef = useRef(null);
  const windowBlurredRef = useRef(false);
  const externalDropCaretRef = useRef(false);
  const blurSaveTimerRef = useRef(null);
  const linkDialogOpenRef = useRef(false);
  const sourceModeTransitionRef = useRef(false);
  const imageUndoRef = useRef([]);
  const imageUndoSequenceRef = useRef(0);
  const contentUndoRef = useRef([]);
  const programmaticMutationRef = useRef(false);
  // Ctrl/Cmd+Z walks image inserts and palette edits in the order they happened; typing clears both.
  const undoOrderRef = useRef([]);
  const paletteUndoRef = useRef([]);
  const [paletteDrag, setPaletteDrag] = useState(null);
  // Single click applies a color; double-click arms that swatch for dragging so the two never compete.
  const [reorderColor, setReorderColor] = useState(null);
  const paletteDragRef = useRef(null);
  const suppressSwatchClickRef = useRef(false);
  const initialContent = node.rich_content || { version: 1, body_html: '', images: [] };
  const initialVisualHtml = initialContent.version === 2 ? workflowBlocksToHtml(initialContent.blocks) : initialContent.body_html || plainTextToRichHtml(node.detail);
  const [blockDraft, setBlockDraft] = useState(null);
  const [blockDraftActiveId, setBlockDraftActiveId] = useState(null);
  const [images, setImages] = useState(initialContent.images || []);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [sourceMode, setSourceMode] = useState(false);
  const [sourceHtml, setSourceHtml] = useState('');
  const [sourceActiveLine, setSourceActiveLine] = useState(0);
  const [formatBubble, setFormatBubble] = useState(null);
  const [pickedTextColor, setPickedTextColor] = useState('#f8fafc');
  const [pickedTextColorInput, setPickedTextColorInput] = useState('#f8fafc');
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [quotePickerOpen, setQuotePickerOpen] = useState(false);
  const [pickerHsv, setPickerHsv] = useState(() => hexToHsv('#f8fafc'));
  const [styleDefaultEdit, setStyleDefaultEdit] = useState(null);
  const [activeTextStyle, setActiveTextStyle] = useState('body');
  const [activeFont, setActiveFont] = useState('default');
  const [linkDraft, setLinkDraft] = useState(null);

  useEffect(() => {
    const markWindowBlurred = () => { windowBlurredRef.current = true; externalDropCaretRef.current = true; };
    const markWindowFocused = () => { windowBlurredRef.current = false; };
    const trackVisibility = () => {
      windowBlurredRef.current = document.hidden;
      if (document.hidden) externalDropCaretRef.current = true;
    };
    window.addEventListener('blur', markWindowBlurred);
    window.addEventListener('focus', markWindowFocused);
    document.addEventListener('visibilitychange', trackVisibility);
    return () => {
      if (blurSaveTimerRef.current) window.clearTimeout(blurSaveTimerRef.current);
      window.removeEventListener('blur', markWindowBlurred);
      window.removeEventListener('focus', markWindowFocused);
      document.removeEventListener('visibilitychange', trackVisibility);
    };
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    const canvas = editor?.closest('.rehab-canvas');
    if (!editor || !canvas) return undefined;
    const frame = window.requestAnimationFrame(() => {
      editor.focus({ preventScroll: true });
      editor.scrollTop = initialViewport?.detailScrollTop || 0;
      if (initialViewport) {
        canvas.scrollTop = initialViewport.canvasScrollTop;
        canvas.scrollLeft = initialViewport.canvasScrollLeft;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [initialViewport]);

  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = richHtmlWithMediaUrls(initialVisualHtml, mediaById);
    (initialContent.images || []).forEach((image) => {
      if (editorRef.current.querySelector(`img[data-media-id="${image.media_id}"]`)) return;
      const media = mediaById[image.media_id];
      if (!media?.url) return;
      const element = document.createElement('img');
      element.dataset.mediaId = image.media_id;
      element.src = media.url;
      element.alt = image.caption || media.original_name || 'Workflow attachment';
      editorRef.current.append(element);
    });
  // The editor is recreated for each node edit; do not overwrite live content after an upload state update.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id]);

  const rememberSelection = () => {
    const selection = window.getSelection();
    if (selection?.rangeCount && editorRef.current?.contains(selection.getRangeAt(0).commonAncestorContainer)) {
      const range = selection.getRangeAt(0);
      selectionRangeRef.current = range.cloneRange();
      setActiveTextStyle(textStyleForRange(range, editorRef.current));
      setActiveFont(fontForRange(range, editorRef.current));
    }
  };
  const restoreSelection = () => {
    if (!selectionRangeRef.current) return;
    const selection = window.getSelection();
    selection.removeAllRanges(); selection.addRange(selectionRangeRef.current);
  };
  const selectionOffsets = () => {
    const editor = editorRef.current; const range = selectionRangeRef.current;
    if (!editor || !range || !editor.contains(range.commonAncestorContainer)) return null;
    const offsetFor = (container, offset) => {
      const prefix = document.createRange(); prefix.selectNodeContents(editor); prefix.setEnd(container, offset);
      return prefix.toString().length;
    };
    return { start: offsetFor(range.startContainer, range.startOffset), end: offsetFor(range.endContainer, range.endOffset) };
  };
  const restoreSelectionOffsets = (offsets) => {
    const editor = editorRef.current;
    if (!editor || !offsets) return;
    const locate = (target) => {
      const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT); let consumed = 0;
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const length = node.textContent?.length || 0;
        if (target <= consumed + length) return { node, offset: Math.max(0, target - consumed) };
        consumed += length;
      }
      return { node: editor, offset: editor.childNodes.length };
    };
    const start = locate(offsets.start); const end = locate(offsets.end);
    const range = document.createRange(); range.setStart(start.node, start.offset); range.setEnd(end.node, end.offset);
    const selection = window.getSelection(); selection?.removeAllRanges(); selection?.addRange(range);
    selectionRangeRef.current = range.cloneRange();
  };
  const pushContentUndoSnapshot = () => {
    const editor = editorRef.current;
    if (!editor) return null;
    const canvas = editor.closest('.rehab-canvas');
    const snapshot = { html: editor.innerHTML, images: images.map((image) => ({ ...image })), selection: selectionOffsets(),
      editorTop: editor.scrollTop, canvasTop: canvas?.scrollTop || 0, canvasLeft: canvas?.scrollLeft || 0 };
    contentUndoRef.current.push(snapshot); undoOrderRef.current.push('content');
    if (contentUndoRef.current.length > 100) {
      contentUndoRef.current.shift();
      const oldestContent = undoOrderRef.current.indexOf('content');
      if (oldestContent >= 0) undoOrderRef.current.splice(oldestContent, 1);
    }
    return snapshot;
  };
  const mutateEditorContent = (mutate) => {
    const editor = editorRef.current; const snapshot = pushContentUndoSnapshot();
    if (!editor || !snapshot) return null;
    programmaticMutationRef.current = true;
    const result = mutate();
    programmaticMutationRef.current = false;
    if (editor.innerHTML === snapshot.html) { contentUndoRef.current.pop(); undoOrderRef.current.pop(); }
    return result;
  };
  const format = (command, value = null) => {
    const editor = editorRef.current; const canvas = editor?.closest('.rehab-canvas');
    const scroll = { editorTop: editor?.scrollTop || 0, canvasTop: canvas?.scrollTop || 0, canvasLeft: canvas?.scrollLeft || 0 };
    editor?.focus({ preventScroll: true });
    restoreSelection();
    mutateEditorContent(() => document.execCommand(command, false, value));
    if (editor) editor.scrollTop = scroll.editorTop;
    if (canvas) { canvas.scrollTop = scroll.canvasTop; canvas.scrollLeft = scroll.canvasLeft; }
    rememberSelection();
  };
  const clearFormatting = () => {
    editorRef.current?.focus(); restoreSelection();
    const selection = window.getSelection();
    if (!selection?.rangeCount || selection.isCollapsed || !editorRef.current?.contains(selection.getRangeAt(0).commonAncestorContainer)) return;
    // Let the browser split formatting inherited from ancestors, then normalize the editor's
    // custom semantic/font wrappers that removeFormat does not know about.
    let next;
    mutateEditorContent(() => {
      document.execCommand?.('removeFormat', false, null);
      const browserRange = selection.rangeCount ? selection.getRangeAt(0) : null;
      const range = browserRange && !browserRange.collapsed ? browserRange.cloneRange() : selectionRangeRef.current?.cloneRange();
      next = clearWorkflowFormattingAtRange(editorRef.current, range);
    });
    if (!next) return;
    selectionRangeRef.current = next.cloneRange(); setActiveTextStyle('body'); setActiveFont('default');
  };
  const applyTextColor = (value) => {
    const color = String(value || '').toLowerCase();
    const range = selectionRangeRef.current?.cloneRange();
    if (!/^#[0-9a-f]{6}$/.test(color) || !range || range.collapsed
        || !editorRef.current?.contains(range.commonAncestorContainer)) return;
    let colored;
    mutateEditorContent(() => {
      colored = document.createElement('font'); colored.setAttribute('color', color);
      const selectedContent = range.extractContents();
      selectedContent.querySelectorAll('font[color]').forEach((element) => element.removeAttribute('color'));
      colored.append(selectedContent); range.insertNode(colored);
    });
    const selection = window.getSelection();
    const next = document.createRange(); next.selectNodeContents(colored);
    selection?.removeAllRanges(); selection?.addRange(next);
    selectionRangeRef.current = next.cloneRange();
  };
  const applyFontSize = (value) => {
    const pixels = Math.min(32, Math.max(8, Number(value) || 9));
    format('fontSize', '7');
    editorRef.current?.querySelectorAll('font[size="7"]').forEach((element) => {
      element.removeAttribute('size');
      element.style.fontSize = `${pixels}px`;
    });
  };
  const applyQuoteStyle = (style) => {
    editorRef.current?.focus({ preventScroll: true }); restoreSelection();
    const range = selectionRangeRef.current?.cloneRange();
    const next = mutateEditorContent(() => applyWorkflowQuoteStyleAtRange(editorRef.current, range, style));
    if (next) selectionRangeRef.current = next.cloneRange();
    setQuotePickerOpen(false);
  };
  const applyTextPreset = (value) => {
    editorRef.current?.focus(); restoreSelection();
    const selection = window.getSelection();
    if (!selection?.rangeCount || selection.isCollapsed) return;
    const range = selection.getRangeAt(0);
    if (!editorRef.current?.contains(range.commonAncestorContainer)) return;
    let styled;
    mutateEditorContent(() => {
      styled = document.createElement('span'); styled.dataset.textStyle = value;
      const selectedContent = range.extractContents();
      selectedContent.querySelectorAll('[data-text-style]').forEach((element) => element.removeAttribute('data-text-style'));
      selectedContent.querySelectorAll('[data-font]').forEach((element) => element.removeAttribute('data-font'));
      selectedContent.querySelectorAll('font').forEach((element) => {
        element.removeAttribute('color'); element.removeAttribute('size'); element.removeAttribute('style');
      });
      selectedContent.querySelectorAll('strong,b').forEach((element) => element.replaceWith(...element.childNodes));
      selectedContent.querySelectorAll('h2,h3,h4').forEach((element) => element.replaceWith(...element.childNodes));
      styled.append(selectedContent); range.insertNode(styled);
    });
    selection.removeAllRanges(); const next = document.createRange(); next.selectNodeContents(styled);
    selection.addRange(next); selectionRangeRef.current = next.cloneRange(); setActiveTextStyle(value);
  };
  // Wraps only the highlighted text; older font choices inside it are replaced. "default" is stored
  // explicitly so part of a sentence can return to the base font inside a differently styled span.
  const applyFont = (value) => {
    editorRef.current?.focus(); restoreSelection();
    const selection = window.getSelection();
    if (!selection?.rangeCount || selection.isCollapsed) return;
    const range = selection.getRangeAt(0);
    if (!editorRef.current?.contains(range.commonAncestorContainer)) return;
    let styled;
    mutateEditorContent(() => {
      styled = document.createElement('span'); styled.dataset.font = value;
      const selectedContent = range.extractContents();
      selectedContent.querySelectorAll('[data-font]').forEach((element) => element.removeAttribute('data-font'));
      styled.append(selectedContent); range.insertNode(styled);
    });
    selection.removeAllRanges(); const next = document.createRange(); next.selectNodeContents(styled);
    selection.addRange(next); selectionRangeRef.current = next.cloneRange(); setActiveFont(value);
  };
  const syncFormatBubble = useCallback((event = null) => {
    if (event) externalDropCaretRef.current = false;
    // Focusing a bubble control (color, size) moves the document selection out of the editor.
    // Keep the bubble and the remembered range so that control can still apply to the text.
    if (toolbarRef.current?.contains(document.activeElement)) return;
    const selection = window.getSelection();
    if (!selection?.rangeCount) { setFormatBubble(null); return; }
    const range = selection.getRangeAt(0);
    if (!editorRef.current?.contains(range.commonAncestorContainer)) { setFormatBubble(null); return; }
    selectionRangeRef.current = range.cloneRange();
    if (selection.isCollapsed) { setFormatBubble(null); return; }
    setActiveTextStyle(textStyleForRange(range, editorRef.current));
    setActiveFont(fontForRange(range, editorRef.current));
    const editor = editorRef.current.closest('.rehab-rich-editor');
    const bounds = editor?.getBoundingClientRect();
    if (!bounds) return;
    const selectionBounds = range.getBoundingClientRect?.();
    const clientX = selectionBounds?.width
      ? selectionBounds.left + (selectionBounds.width / 2) : (event?.clientX ?? bounds.left + (bounds.width / 2));
    const clientY = selectionBounds?.height ? selectionBounds.top : (event?.clientY ?? bounds.top + 80);
    setFormatBubble({
      left: Math.max(28, Math.min(bounds.width - 28, clientX - bounds.left)),
      top: Math.max(42, clientY - bounds.top - 8),
    });
  }, []);
  const changePickedTextColor = (rawValue) => {
    const draft = String(rawValue || '').trim().toLowerCase();
    setPickedTextColorInput(draft);
    if (!/^#[0-9a-f]{6}$/.test(draft)) return;
    setPickedTextColor(draft); applyTextColor(draft);
  };
  const toggleColorPicker = () => {
    if (!colorPickerOpen) setPickerHsv(hexToHsv(pickedTextColor));
    setColorPickerOpen((open) => !open);
  };
  const applyPickerHsv = (next) => {
    setPickerHsv(next); changePickedTextColor(hsvToHex(next.h, next.s, next.v));
  };
  const pickSpectrumColor = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    applyPickerHsv({ ...pickerHsv,
      s: Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width)),
      v: 1 - Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height)),
    });
  };
  useEffect(() => {
    const handleSelectionChange = () => syncFormatBubble();
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [syncFormatBubble]);
  const editorHtmlForStorage = () => {
    const clean = editorRef.current?.cloneNode(true);
    clean?.querySelectorAll('img[data-media-id]').forEach((image) => {
      image.removeAttribute('src'); image.removeAttribute('data-undo-insert-id');
    });
    return clean?.innerHTML || '';
  };
  const toggleSourceMode = () => {
    const editor = editorRef.current; const canvas = editor?.closest('.rehab-canvas');
    const scroll = { editorTop: editor?.scrollTop || 0, canvasTop: canvas?.scrollTop || 0, canvasLeft: canvas?.scrollLeft || 0 };
    sourceModeTransitionRef.current = true;
    if (!sourceMode) {
      const formatted = formatWorkflowHtmlSource(editorHtmlForStorage());
      const sourceCaret = workflowHtmlSourceOffsetForTextOffset(formatted, selectionOffsets()?.start || 0);
      setSourceHtml(formatted); setSourceActiveLine(0); setFormatBubble(null); setSourceMode(true);
      window.requestAnimationFrame(() => {
        sourceRef.current?.focus({ preventScroll: true });
        if (sourceRef.current) {
          sourceRef.current.setSelectionRange(sourceCaret, sourceCaret); syncSourceActiveLine(sourceRef.current);
          const caretLine = sourceRef.current.value.slice(0, sourceCaret).split('\n').length - 1;
          sourceRef.current.scrollTop = Math.max(scroll.editorTop, (caretLine - 3) * 13);
          syncSourceViewport(sourceRef.current);
        }
        if (canvas) { canvas.scrollTop = scroll.canvasTop; canvas.scrollLeft = scroll.canvasLeft; }
        sourceModeTransitionRef.current = false;
      });
      return;
    }
    const safeHtml = sanitizeRichHtmlDraft(sourceHtml);
    if (editorRef.current) mutateEditorContent(() => { editorRef.current.innerHTML = richHtmlWithMediaUrls(safeHtml, mediaById); });
    setSourceHtml(safeHtml); setSourceMode(false);
    window.requestAnimationFrame(() => {
      editorRef.current?.focus({ preventScroll: true });
      if (editorRef.current) editorRef.current.scrollTop = scroll.editorTop;
      if (canvas) { canvas.scrollTop = scroll.canvasTop; canvas.scrollLeft = scroll.canvasLeft; }
      sourceModeTransitionRef.current = false;
    });
  };
  const syncSourceViewport = (textarea) => {
    if (sourceHighlightRef.current) {
      sourceHighlightRef.current.scrollTop = textarea.scrollTop;
      sourceHighlightRef.current.scrollLeft = textarea.scrollLeft;
    }
    if (sourceGutterRef.current) sourceGutterRef.current.scrollTop = textarea.scrollTop;
    sourceStageRef.current?.style.setProperty('--source-scroll-top', `${textarea.scrollTop}px`);
  };
  const syncSourceActiveLine = (textarea) => {
    setSourceActiveLine(textarea.value.slice(0, textarea.selectionStart).split('\n').length - 1);
  };
  const formatSourceHtml = () => {
    const line = sourceActiveLine;
    setSourceHtml(formatWorkflowHtmlSource(sourceHtml));
    window.requestAnimationFrame(() => {
      const next = sourceRef.current; if (!next) return;
      const lines = next.value.split('\n'); const offset = lines.slice(0, Math.min(line, lines.length - 1)).reduce((total, value) => total + value.length + 1, 0);
      next.focus({ preventScroll: true }); next.setSelectionRange(offset, offset); syncSourceActiveLine(next); syncSourceViewport(next);
    });
  };
  const openLinkDialog = () => {
    rememberSelection();
    const selection = window.getSelection();
    const selectedName = selection?.rangeCount && !selection.isCollapsed
      && editorRef.current?.contains(selection.getRangeAt(0).commonAncestorContainer)
      ? selection.toString().trim() : '';
    linkDialogOpenRef.current = true;
    setLinkDraft({ name: selectedName, url: '', error: '' });
    setFormatBubble(null);
  };
  const closeLinkDialog = () => {
    linkDialogOpenRef.current = false;
    setLinkDraft(null);
    window.requestAnimationFrame(() => { editorRef.current?.focus({ preventScroll: true }); restoreSelection(); });
  };
  const submitLink = () => {
    const name = linkDraft?.name?.trim();
    const url = normalizeWorkflowLinkUrl(linkDraft?.url);
    if (!name) { setLinkDraft((current) => ({ ...current, error: 'Enter the text that should appear in the note.' })); return; }
    if (!url) { setLinkDraft((current) => ({ ...current, error: 'Enter a valid HTTP or HTTPS address.' })); return; }
    const caret = mutateEditorContent(() => insertWorkflowLinkAtRange(editorRef.current, selectionRangeRef.current, name, url));
    if (!caret) { setLinkDraft((current) => ({ ...current, error: 'Select a place in the note and try again.' })); return; }
    selectionRangeRef.current = caret.cloneRange();
    linkDialogOpenRef.current = false;
    setLinkDraft(null); setFormatBubble(null);
    editorRef.current?.focus({ preventScroll: true });
  };
  const uploadImages = async (files, range = null) => {
    const accepted = Array.from(files || []).filter((file) => file.type?.startsWith('image/'));
    if (!accepted.length) { setUploadError('Drop or choose an image file.'); return; }
    if (images.length + accepted.length > 12) { setUploadError('A node can contain up to 12 images.'); return; }
    const marker = document.createElement('span');
    marker.dataset.imageInsertMarker = 'true';
    marker.textContent = '\u200b';
    const selection = window.getSelection();
    const insertionRange = range || (selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null);
    if (insertionRange && editorRef.current?.contains(insertionRange.commonAncestorContainer)) {
      insertionRange.collapse(false); insertionRange.insertNode(marker);
    } else editorRef.current?.append(marker);
    setUploading(true); setUploadError('');
    try {
      const uploaded = [];
      const insertedElements = [];
      for (const file of accepted) {
        const body = new FormData(); body.append('image', file);
        const item = await apiJson(`/properties/${propertyId}/rehab-media`, { method: 'POST', body });
        uploaded.push({ media_id: item.media_id, caption: '', purchase_url: '' });
        onMediaAdded(item);
        const image = document.createElement('img');
        image.dataset.mediaId = item.media_id;
        image.dataset.undoInsertId = `${node.id}-${++imageUndoSequenceRef.current}`;
        image.src = item.url;
        image.alt = item.original_name || 'Workflow attachment';
        marker.before(image);
        insertedElements.push(image);
      }
      setImages((current) => [...current, ...uploaded]);
      imageUndoRef.current.push(insertedElements); undoOrderRef.current.push('image');
    } catch (uploadFailure) {
      setUploadError(uploadFailure.message || 'Could not upload the image.');
    } finally {
      marker.remove();
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const undoLastImageInsert = () => {
    const insertedElements = imageUndoRef.current.pop();
    if (!insertedElements?.length) return false;
    const insertionTokens = new Set(insertedElements.map((image) => image.dataset.undoInsertId).filter(Boolean));
    insertedElements.forEach((image) => image.remove());
    editorRef.current?.querySelectorAll('img[data-undo-insert-id]').forEach((image) => {
      if (insertionTokens.has(image.dataset.undoInsertId)) image.remove();
    });
    const remainingMediaIds = new Set(Array.from(editorRef.current?.querySelectorAll('img[data-media-id]') || []).map((image) => image.dataset.mediaId));
    setImages((current) => current.filter((image) => remainingMediaIds.has(image.media_id)));
    return true;
  };
  const undoLastContentChange = () => {
    const snapshot = contentUndoRef.current.pop(); const editor = editorRef.current;
    if (!snapshot || !editor) return false;
    const canvas = editor.closest('.rehab-canvas');
    programmaticMutationRef.current = true;
    editor.innerHTML = snapshot.html; setImages(snapshot.images);
    editor.scrollTop = snapshot.editorTop;
    if (canvas) { canvas.scrollTop = snapshot.canvasTop; canvas.scrollLeft = snapshot.canvasLeft; }
    restoreSelectionOffsets(snapshot.selection); setFormatBubble(null);
    programmaticMutationRef.current = false;
    return true;
  };
  const changePalette = (next) => {
    if (next.join() === textColors.join()) return;
    paletteUndoRef.current.push(textColors); undoOrderRef.current.push('palette');
    onTextColorsChange?.(next);
  };
  const openStyleDefaults = (event, styleKey) => {
    event.preventDefault(); event.stopPropagation();
    // Right-click both applies the selected preset and makes its defaults authoritative over
    // per-selection formatting from the primary toolbar for this highlighted range.
    applyTextPreset(styleKey);
    setStyleDefaultEdit({ key: styleKey, ...textStyleDefaults[styleKey], colorInput: textStyleDefaults[styleKey].color });
  };
  const saveStyleDefaults = () => {
    if (!styleDefaultEdit) return;
    const { key, size, color, font, weight } = styleDefaultEdit;
    const settings = { size, color, font, weight };
    onTextStyleDefaultsChange?.({ ...textStyleDefaults, [key]: settings });
    setStyleDefaultEdit(null);
  };
  const resetStyleDefault = () => {
    if (!styleDefaultEdit) return;
    const builtIn = DEFAULT_TEXT_STYLE_DEFAULTS[styleDefaultEdit.key];
    setStyleDefaultEdit({ key: styleDefaultEdit.key, ...builtIn, colorInput: builtIn.color });
  };
  const undoLastEditorAction = () => {
    const last = undoOrderRef.current.pop();
    if (last === 'palette') { onTextColorsChange?.(paletteUndoRef.current.pop()); return true; }
    if (last === 'content') return undoLastContentChange();
    return undoLastImageInsert();
  };
  // Pointer-based reordering: the swatches block mousedown to keep the text selection, which would
  // also stop native drag-and-drop from starting. Only an armed (double-clicked) swatch captures.
  const startSwatchDrag = (event, color) => {
    if (event.button !== 0) return;
    suppressSwatchClickRef.current = false;
    if (reorderColor !== color) return;
    paletteDragRef.current = { color, pointerId: event.pointerId, x: event.clientX, y: event.clientY, order: null };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const moveSwatchDrag = (event) => {
    const drag = paletteDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (!drag.order && Math.hypot(event.clientX - drag.x, event.clientY - drag.y) < 4) return;
    const swatches = Array.from(toolbarRef.current?.querySelectorAll('[data-swatch-color]') || []);
    const nearest = swatches.map((element) => {
      const box = element.getBoundingClientRect();
      return { color: element.dataset.swatchColor, distance: Math.hypot(event.clientX - (box.left + box.width / 2), event.clientY - (box.top + box.height / 2)) };
    }).sort((a, b) => a.distance - b.distance)[0];
    const current = drag.order || textColors;
    const targetIndex = nearest ? current.indexOf(nearest.color) : current.indexOf(drag.color);
    const order = current.filter((item) => item !== drag.color);
    order.splice(Math.max(0, targetIndex), 0, drag.color);
    drag.order = order; setPaletteDrag({ color: drag.color, order });
  };
  const finishSwatchDrag = (event) => {
    const drag = paletteDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    // Releasing an armed swatch never applies its color; it drops the new order or simply disarms.
    paletteDragRef.current = null; setPaletteDrag(null); setReorderColor(null);
    suppressSwatchClickRef.current = true;
    if (drag.order) changePalette(drag.order);
  };
  const handleEditorKeyDown = (event) => {
    if (event.key.toLowerCase() === 'z' && (event.ctrlKey || event.metaKey) && !event.shiftKey && undoLastEditorAction()) {
      event.preventDefault();
    } else if (event.key === 'Escape' && reorderColor) {
      event.preventDefault(); setReorderColor(null);
    } else if (event.key === 'Escape') {
      event.preventDefault(); onCancel();
    } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault(); save();
    }
  };
  const save = () => {
    const bodyHtml = sourceMode ? sanitizeRichHtmlDraft(sourceHtml) : editorHtmlForStorage();
    const clean = document.createElement('div'); clean.innerHTML = bodyHtml;
    const usedMedia = new Set(Array.from(clean.querySelectorAll('img[data-media-id]')).map((image) => image.dataset.mediaId));
    const imageDetails = new Map(images.map((image) => [image.media_id, image]));
    const unchangedBlockDocument = initialContent.version === 2
      && sanitizeRichHtmlDraft(bodyHtml) === sanitizeRichHtmlDraft(initialVisualHtml);
    onSave({
      detail: richHtmlToPlainText(bodyHtml).slice(0, NODE_DETAIL_MAX),
      rich_content: unchangedBlockDocument ? initialContent : initialContent.version === 2
        ? { version: 2, blocks: [{ ...emptyWorkflowBlock('raw_html'), body_html: sanitizeRichHtmlDraft(bodyHtml) }] }
        : { version: 1, body_html: bodyHtml, images: Array.from(usedMedia).map((media_id) => {
        const { caption, purchase_url } = imageDetails.get(media_id) || {};
        return { media_id, caption: caption?.trim() || '', purchase_url: purchase_url?.trim() || '' };
      }) },
    });
  };
  const clipboardImageUrl = (clipboardData) => {
    const candidates = [clipboardData?.getData('text/uri-list'), clipboardData?.getData('text/plain')]
      .flatMap((value) => String(value || '').split(/\r?\n/)).map((value) => value.trim())
      .filter((value) => value && !value.startsWith('#'));
    return candidates.find((value) => /^https?:\/\/[^\s]+\.(?:jpe?g|png|webp|gif)(?:[?#][^\s]*)?$/i.test(value)) || '';
  };
  const clipboardImageFiles = async (clipboardData) => {
    const itemFiles = Array.from(clipboardData?.items || [])
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/')).map((item) => item.getAsFile()).filter(Boolean);
    if (itemFiles.length) return itemFiles;
    const directFiles = Array.from(clipboardData?.files || []).filter((file) => file.type?.startsWith('image/'));
    if (directFiles.length) return directFiles;
    const html = clipboardData?.getData('text/html') || '';
    const parsed = document.createElement('div'); parsed.innerHTML = html;
    const sources = Array.from(parsed.querySelectorAll('img[src]')).map((image) => image.src)
      .filter((source) => source.startsWith('data:image/') || /^https?:\/\//i.test(source)).slice(0, 12);
    if (!sources.length) {
      const imageUrl = clipboardImageUrl(clipboardData);
      if (imageUrl) sources.push(imageUrl);
    }
    const files = [];
    for (const [index, source] of sources.entries()) {
      const response = await fetch(source, { credentials: 'omit', referrerPolicy: 'no-referrer' });
      if (!response.ok) throw new Error('The copied image could not be downloaded. Try copying a screenshot of it instead.');
      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) continue;
      const extension = blob.type.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
      files.push(new File([blob], `clipboard-image-${index + 1}.${extension}`, { type: blob.type }));
    }
    return files;
  };
  const mediaReferencesFromClipboard = (clipboardData) => {
    let customReferences = [];
    try {
      const parsed = JSON.parse(clipboardData?.getData('application/x-rehab-media-ids') || '[]');
      customReferences = Array.isArray(parsed) ? parsed.map((item) => typeof item === 'string'
        ? { media_id: item, display_size: '' }
        : { media_id: item?.media_id, display_size: item?.display_size === 'compact' ? 'compact' : '' }) : [];
    } catch (_error) { customReferences = []; }
    const validCustomReferences = customReferences.filter(({ media_id: mediaId }) => mediaById[mediaId]?.url).slice(0, 12);
    if (validCustomReferences.length) return validCustomReferences;

    const html = clipboardData?.getData('text/html') || '';
    const parsedHtml = document.createElement('div'); parsedHtml.innerHTML = html;
    const htmlReferences = Array.from(parsedHtml.querySelectorAll('img')).map((image) => {
      const mediaId = image.dataset.mediaId
        || Object.keys(mediaById).find((candidate) => mediaById[candidate]?.url === image.src);
      return { media_id: mediaId, display_size: image.dataset.displaySize === 'compact' ? 'compact' : '' };
    }).filter(({ media_id: mediaId }) => mediaById[mediaId]?.url).slice(0, 12);
    if (htmlReferences.length) return htmlReferences;

    const clipboardUrls = Array.from(new Set(
      [clipboardData?.getData('text/uri-list'), clipboardData?.getData('text/plain')]
        .flatMap((value) => String(value || '').split(/\r?\n/)).map((value) => value.trim()).filter(Boolean),
    ));
    return clipboardUrls.map((url) => {
      const mediaId = Object.keys(mediaById).find((candidate) => mediaById[candidate]?.url === url);
      return { media_id: mediaId, display_size: '' };
    }).filter(({ media_id: mediaId }) => Boolean(mediaId)).slice(0, 12);
  };
  const insertMediaReferences = (references, range) => {
    const currentImageCount = editorRef.current?.querySelectorAll('img[data-media-id]').length || 0;
    if (currentImageCount + references.length > 12) {
      setUploadError('A node can contain up to 12 images.'); return false;
    }
    const insertionRange = range && editorRef.current?.contains(range.commonAncestorContainer) ? range : null;
    if (insertionRange) { insertionRange.deleteContents(); insertionRange.collapse(false); }
    const insertedElements = references.map(({ media_id: mediaId, display_size: displaySize }) => {
      const image = document.createElement('img');
      image.dataset.mediaId = mediaId; image.src = mediaById[mediaId].url;
      image.dataset.undoInsertId = `${node.id}-${++imageUndoSequenceRef.current}`;
      image.alt = mediaById[mediaId].original_name || 'Workflow attachment';
      if (displaySize === 'compact') image.dataset.displaySize = 'compact';
      if (insertionRange) { insertionRange.insertNode(image); insertionRange.setStartAfter(image); }
      else editorRef.current?.append(image);
      return image;
    });
    setImages((current) => {
      const knownIds = new Set(current.map((image) => image.media_id));
      const additions = [];
      references.forEach(({ media_id: mediaId }) => {
        if (knownIds.has(mediaId)) return;
        knownIds.add(mediaId); additions.push({ media_id: mediaId, caption: '', purchase_url: '' });
      });
      return [...current, ...additions];
    });
    imageUndoRef.current.push(insertedElements); undoOrderRef.current.push('image');
    setUploadError('');
    return true;
  };
  const handlePaste = async (event) => {
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
    const copiedMediaReferences = mediaReferencesFromClipboard(event.clipboardData);
    if (copiedMediaReferences.length) {
      event.preventDefault();
      insertMediaReferences(copiedMediaReferences, range);
      return;
    }
    const hasImageFile = Array.from(event.clipboardData?.items || []).some((item) => item.kind === 'file' && item.type.startsWith('image/'))
      || Array.from(event.clipboardData?.files || []).some((file) => file.type?.startsWith('image/'));
    const hasImageHtml = /<img\b/i.test(event.clipboardData?.getData('text/html') || '');
    const hasImageUrl = Boolean(clipboardImageUrl(event.clipboardData));
    if (!hasImageFile && !hasImageHtml && !hasImageUrl) {
      const pastedText = event.clipboardData?.getData('text/plain') || '';
      const pastedHtml = sanitizeRichHtmlDraft(event.clipboardData?.getData('text/html') || '');
      if (pastedHtml && richHtmlToPlainText(pastedHtml)) {
        event.preventDefault();
        const caret = mutateEditorContent(() => insertWorkflowRichHtmlAtRange(editorRef.current, range, pastedHtml));
        if (caret) selectionRangeRef.current = caret.cloneRange();
        setFormatBubble(null);
        return;
      }
      const pastedUrl = workflowUrlFromPastedText(pastedText);
      if (pastedUrl) {
        event.preventDefault();
        const caret = mutateEditorContent(() => insertWorkflowLinkAtRange(editorRef.current, range, pastedText.trim(), pastedUrl));
        if (caret) selectionRangeRef.current = caret.cloneRange();
        setFormatBubble(null);
        return;
      }
      if (looksLikeWorkflowMarkdown(pastedText)) {
        event.preventDefault();
        const caret = mutateEditorContent(() => insertWorkflowRichHtmlAtRange(editorRef.current, range, markdownToWorkflowRichHtml(pastedText)));
        if (caret) selectionRangeRef.current = caret.cloneRange();
        setFormatBubble(null);
        return;
      }
      event.preventDefault(); format('insertText', pastedText); return;
    }
    event.preventDefault(); setUploadError('');
    try {
      const files = await clipboardImageFiles(event.clipboardData);
      if (!files.length) { setUploadError('The clipboard did not contain a readable image.'); return; }
      await uploadImages(files, range);
    } catch (pasteFailure) {
      setUploadError(pasteFailure.message || 'Could not paste the copied image.');
    }
  };

  const previewStyleDefaults = styleDefaultEdit
    ? { ...textStyleDefaults, [styleDefaultEdit.key]: {
      size: styleDefaultEdit.size, color: styleDefaultEdit.color,
      font: styleDefaultEdit.font, weight: styleDefaultEdit.weight,
    } }
    : textStyleDefaults;

  if (blockDraft) return <WorkflowBlocksEditor initialBlocks={blockDraft} initialActiveBlockId={blockDraftActiveId} mediaById={mediaById} onCancel={onCancel} onSave={onSave} />;

  return <div className="rehab-rich-editor" style={textStyleCssVariables(previewStyleDefaults)} onPointerDown={(event) => event.stopPropagation()} onWheel={(event) => event.stopPropagation()}>
    {formatBubble ? <div ref={toolbarRef} className="rehab-rich-toolbar" aria-label="Text formatting" style={{ left: formatBubble.left, top: formatBubble.top }}>
      <button type="button" aria-label="Bold" title="Bold" onMouseDown={(event) => event.preventDefault()} onClick={() => format('bold')}><strong>B</strong></button>
      <button type="button" aria-label="Italic" title="Italic" onMouseDown={(event) => event.preventDefault()} onClick={() => format('italic')}><em>I</em></button>
      <button type="button" aria-label="Clear formatting" title="Clear formatting" onMouseDown={(event) => event.preventDefault()} onClick={clearFormatting}><i className="pi pi-eraser" aria-hidden="true" /></button>
      <span className="rehab-rich-quote-control"><button type="button" className={quotePickerOpen ? 'rehab-rich-toolbar__active' : ''} aria-label="Quote style" title="Quote style" aria-expanded={quotePickerOpen} onMouseDown={(event) => event.preventDefault()} onClick={() => setQuotePickerOpen((open) => !open)}><span className="rehab-rich-quote-mark" aria-hidden="true">“</span></button>{quotePickerOpen ? <span className="rehab-rich-quote-picker" role="dialog" aria-label="Quote styles">{[
        ['accent', 'Accent line'], ['soft', 'Soft tint'], ['highlight', 'Highlight'], ['ribbon', 'Ribbon'],
        ['editorial', 'Editorial'], ['minimal', 'Minimal marks'], ['plain', 'Remove quote'],
      ].map(([value, label]) => <button key={value} type="button" data-quote-preview={value} aria-label={label} title={label} onMouseDown={(event) => event.preventDefault()} onClick={() => applyQuoteStyle(value)}>{value === 'plain' ? <i className="pi pi-times" aria-hidden="true" /> : <span aria-hidden="true">“A”</span>}</button>)}</span> : null}</span>
      <button type="button" aria-label="Bulleted list" title="Bulleted list" onMouseDown={(event) => event.preventDefault()} onClick={() => format('insertUnorderedList')}><i className="pi pi-list" /></button>
      <label className="rehab-rich-size" title="Text size in pixels"><span>Size</span><input aria-label="Text size" type="number" min="8" max="32" step="1" defaultValue="10" onPointerDown={rememberSelection} onChange={(event) => applyFontSize(event.target.value)} /></label>
      <span className="rehab-rich-color-control"><span className="rehab-rich-color" title="Paste or type a #rrggbb text color"><span>Color</span><button type="button" className="rehab-rich-color__preview" style={{ '--picked-color': pickedTextColor }} aria-label="Open text color picker" aria-expanded={colorPickerOpen} onMouseDown={(event) => event.preventDefault()} onClick={toggleColorPicker} /><input className="rehab-rich-color__hex" aria-label="Text color" type="text" inputMode="text" spellCheck="false" maxLength="7" value={pickedTextColorInput} onPointerDown={rememberSelection} onChange={(event) => { const value = event.target.value; changePickedTextColor(value); if (/^#[0-9a-f]{6}$/i.test(value.trim())) setPickerHsv(hexToHsv(value.trim())); }} onBlur={() => setPickedTextColorInput(pickedTextColor)} /></span><button type="button" className="rehab-rich-color__add" aria-label="Add picked color to palette" title="Add picked color to quick colors" onMouseDown={(event) => event.preventDefault()} onClick={() => { if (!textColors.includes(pickedTextColor)) changePalette([...textColors, pickedTextColor]); }}><i className="pi pi-plus" aria-hidden="true" /></button>{colorPickerOpen ? <span className="rehab-rich-color-picker" role="dialog" aria-label="Text color picker"><button type="button" className="rehab-rich-color-picker__spectrum" aria-label="Color saturation and brightness" style={{ '--picker-hue': `${pickerHsv.h}deg`, '--picker-saturation': `${pickerHsv.s * 100}%`, '--picker-value': `${(1 - pickerHsv.v) * 100}%` }} onMouseDown={(event) => event.preventDefault()} onPointerDown={(event) => { event.preventDefault(); event.currentTarget.setPointerCapture?.(event.pointerId); pickSpectrumColor(event); }} onPointerMove={(event) => { if (event.currentTarget.hasPointerCapture?.(event.pointerId)) pickSpectrumColor(event); }} onPointerUp={(event) => event.currentTarget.releasePointerCapture?.(event.pointerId)}><i aria-hidden="true" /></button><input className="rehab-rich-color-picker__hue" aria-label="Color hue" type="range" min="0" max="359" value={Math.round(pickerHsv.h)} onPointerDown={rememberSelection} onChange={(event) => applyPickerHsv({ ...pickerHsv, h: Number(event.target.value) })} /></span> : null}</span>
      <span className="rehab-rich-swatches" aria-label="Text color presets">{(paletteDrag?.order || textColors).map((color) => <button key={color} type="button" data-swatch-color={color} className={paletteDrag?.color === color ? 'is-dragging' : reorderColor === color ? 'is-reorder-armed' : ''} aria-label={`Text color ${color}`} aria-pressed={reorderColor === color ? 'mixed' : undefined} title={reorderColor === color ? `Drag ${color} to reorder · click to cancel` : `${color} · double-click to reorder · right-click to remove`} style={{ '--swatch-color': color }} onMouseDown={(event) => event.preventDefault()}
        onClick={() => { if (suppressSwatchClickRef.current) { suppressSwatchClickRef.current = false; return; } applyTextColor(color); }}
        onDoubleClick={() => setReorderColor(color)} onPointerDown={(event) => startSwatchDrag(event, color)} onPointerMove={moveSwatchDrag} onPointerUp={finishSwatchDrag} onPointerCancel={finishSwatchDrag}
        onContextMenu={(event) => { event.preventDefault(); changePalette(textColors.filter((item) => item !== color)); }} />)}</span>
      <button type="button" aria-label="Insert separator" title="Insert separator" onMouseDown={(event) => event.preventDefault()} onClick={() => format('insertHorizontalRule')}><span aria-hidden="true">―</span></button>
      <button type="button" aria-label="Add link" title="Add link" onMouseDown={(event) => event.preventDefault()} onClick={openLinkDialog}><i className="pi pi-link" /></button>
      <button type="button" aria-label="Upload images" title="Upload images" disabled={uploading} onMouseDown={(event) => event.preventDefault()} onClick={() => fileInputRef.current?.click()}><i className={`pi ${uploading ? 'pi-spin pi-spinner' : 'pi-image'}`} /></button>
      <button type="button" className={sourceMode ? 'rehab-rich-toolbar__active' : ''} aria-label={sourceMode ? 'Show formatted text' : 'Edit HTML'} title={sourceMode ? 'Show formatted text' : 'Edit HTML'} onMouseDown={(event) => event.preventDefault()} onClick={toggleSourceMode}><span aria-hidden="true">&lt;/&gt;</span></button>
      <span className="rehab-rich-text-styles" role="group" aria-label="Text styles">{[
        ['body', 'b', 'Body'], ['title', 't', 'Title'], ['heading', 'h', 'Heading'], ['subheading', 's', 'Subheading'],
      ].map(([value, letter, label]) => <button key={value} type="button" className={activeTextStyle === value ? 'is-active' : ''} aria-label={`${label} style`} aria-pressed={activeTextStyle === value} title={`${label} · right-click to edit defaults`} onMouseDown={(event) => event.preventDefault()} onClick={() => applyTextPreset(value)} onContextMenu={(event) => openStyleDefaults(event, value)}>{letter}</button>)}</span>
      <span className="rehab-rich-fonts" role="group" aria-label="Fonts">{RICH_FONTS.map(([value, label]) => <button key={value} type="button" data-font={value} className={activeFont === value ? 'is-active' : ''} aria-label={`${label} font`} aria-pressed={activeFont === value} title={label} onMouseDown={(event) => event.preventDefault()} onClick={() => applyFont(value)}>Aa</button>)}</span>
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden onChange={(event) => void uploadImages(event.target.files)} />
    </div> : <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden onChange={(event) => void uploadImages(event.target.files)} />}
    {styleDefaultEdit ? <div className="rehab-style-defaults" role="dialog" aria-label={`${styleDefaultEdit.key} style defaults`}>
      <strong>{styleDefaultEdit.key} defaults</strong>
      <label className="rehab-rich-size"><span>Size</span><input aria-label="Default text size" type="number" min="8" max="32" value={styleDefaultEdit.size} onChange={(event) => setStyleDefaultEdit({ ...styleDefaultEdit, size: Math.min(32, Math.max(8, Number(event.target.value) || 8)) })} /></label>
      <label className="rehab-rich-color"><span>Color</span><i className="rehab-rich-color__preview" style={{ '--picked-color': styleDefaultEdit.color }} aria-hidden="true" /><input className="rehab-rich-color__hex" aria-label="Default text color" type="text" inputMode="text" spellCheck="false" maxLength="7" value={styleDefaultEdit.colorInput} onChange={(event) => { const colorInput = event.target.value.trim().toLowerCase(); setStyleDefaultEdit({ ...styleDefaultEdit, colorInput, ...(/^#[0-9a-f]{6}$/.test(colorInput) ? { color: colorInput } : {}) }); }} onBlur={() => setStyleDefaultEdit({ ...styleDefaultEdit, colorInput: styleDefaultEdit.color })} /></label>
      <span className="rehab-rich-swatches" aria-label="Default text color presets">{textColors.map((color) => <button key={color} type="button" aria-label={`Default text color ${color}`} style={{ '--swatch-color': color }} onClick={() => setStyleDefaultEdit({ ...styleDefaultEdit, color, colorInput: color })} />)}</span>
      <button type="button" className={styleDefaultEdit.weight >= 700 ? 'is-active' : ''} aria-label="Default bold" aria-pressed={styleDefaultEdit.weight >= 700} onClick={() => setStyleDefaultEdit({ ...styleDefaultEdit, weight: styleDefaultEdit.weight >= 700 ? 400 : 700 })}><strong>B</strong></button>
      <span className="rehab-rich-fonts" role="group" aria-label="Default font">{RICH_FONTS.map(([value, label]) => <button key={value} type="button" data-font={value} className={styleDefaultEdit.font === value ? 'is-active' : ''} aria-label={`${label} default font`} aria-pressed={styleDefaultEdit.font === value} title={label} onClick={() => setStyleDefaultEdit({ ...styleDefaultEdit, font: value })}>Aa</button>)}</span>
      <button type="button" aria-label="Reset style defaults" onClick={resetStyleDefault}><i className="pi pi-refresh" aria-hidden="true" /></button>
      <button type="button" aria-label="Cancel style defaults" onClick={() => setStyleDefaultEdit(null)}><i className="pi pi-times" aria-hidden="true" /></button>
      <button type="button" className="rehab-style-defaults__save" aria-label="Save style defaults" onClick={saveStyleDefaults}><i className="pi pi-check" aria-hidden="true" /></button>
    </div> : null}
    {sourceMode ? <div className="rehab-source-editor" style={{ '--source-active-line': sourceActiveLine }}>
      <div ref={sourceGutterRef} className="rehab-source-editor__gutter" aria-hidden="true">{sourceHtml.split('\n').map((_, index) => <span key={index} className={index === sourceActiveLine ? 'is-active' : ''}>{index + 1}</span>)}</div>
      <div ref={sourceStageRef} className="rehab-source-editor__stage"><i className="rehab-source-editor__active-line" aria-hidden="true" />
        <div className="rehab-source-editor__actions">
          <button type="button" aria-label="Format HTML" title="Format HTML" onClick={formatSourceHtml}><i className="pi pi-align-left" aria-hidden="true" /></button>
          <button type="button" aria-label="Show formatted text" title="Return to formatted text" onClick={toggleSourceMode}><span aria-hidden="true">&lt;/&gt;</span></button>
        </div>
        <pre ref={sourceHighlightRef} className="rehab-source-editor__highlight" aria-hidden="true"><code>{highlightWorkflowHtmlSource(sourceHtml)}{'\n'}</code></pre>
        <textarea ref={sourceRef} className="rehab-rich-editor__source" aria-label={`HTML for ${node.label}`} value={sourceHtml} spellCheck="false" wrap="off"
          onChange={(event) => { setSourceHtml(event.target.value); syncSourceActiveLine(event.target); }} onScroll={(event) => syncSourceViewport(event.currentTarget)}
          onClick={(event) => syncSourceActiveLine(event.currentTarget)} onKeyUp={(event) => syncSourceActiveLine(event.currentTarget)}
          onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); onCancel(); } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) { event.preventDefault(); save(); } }} />
      </div>
    </div> : null}
    <div ref={editorRef} className={`rehab-rich-editor__body rehab-content-surface${sourceMode ? ' rehab-rich-editor__body--hidden' : ''}`} contentEditable suppressContentEditableWarning role="textbox"
      aria-label={`Description for ${node.label}`} aria-multiline="true" onPaste={(event) => void handlePaste(event)}
      onCopy={(event) => {
        const selection = window.getSelection();
        if (!selection?.rangeCount || selection.isCollapsed) return;
        const range = selection.getRangeAt(0);
        if (!editorRef.current?.contains(range.commonAncestorContainer)) return;
        const selectedImages = Array.from(editorRef.current?.querySelectorAll('img[data-media-id]') || [])
          .filter((image) => selection.containsNode(image, false));
        const copiedHtml = workflowClipboardHtmlForRange(editorRef.current, range);
        const references = selectedImages.map((image) => ({
          media_id: image.dataset.mediaId,
          display_size: image.dataset.displaySize === 'compact' ? 'compact' : '',
        }));
        const fallbackHtml = copiedHtml || selectedImages.map((image) => image.outerHTML).join('');
        const fallbackText = selection.toString() || selectedImages.map((image) => image.src).join('\n');
        if (!fallbackHtml && !fallbackText) return;
        event.preventDefault();
        event.clipboardData.setData('text/html', fallbackHtml);
        event.clipboardData.setData('text/plain', fallbackText);
        if (selectedImages.length === 1) event.clipboardData.setData('text/uri-list', selectedImages[0].src);
        if (references.length) {
          try { event.clipboardData.setData('application/x-rehab-media-ids', JSON.stringify(references)); } catch (_error) { /* Standard HTML remains available. */ }
        }
      }}
      onBeforeInput={() => { if (!programmaticMutationRef.current) { rememberSelection(); pushContentUndoSnapshot(); } }}
      onInput={() => { rememberSelection(); setFormatBubble(null); }}
      onKeyUp={(event) => syncFormatBubble(event)}
      onMouseUp={(event) => syncFormatBubble(event)}
      onDoubleClick={(event) => {
        // Triple-click begins with a double-click. Keep it inside the active editor so the
        // node's double-click-to-open handler cannot reinitialize this editor at scrollTop 0.
        event.stopPropagation();
        if (!(event.target instanceof HTMLImageElement) || !event.target.dataset.mediaId) return;
        event.preventDefault();
        mutateEditorContent(() => {
          if (event.target.dataset.displaySize === 'compact') delete event.target.dataset.displaySize;
          else event.target.dataset.displaySize = 'compact';
        });
      }}
      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
      onDrop={(event) => {
        event.preventDefault(); event.stopPropagation();
        const range = workflowImageDropRange(editorRef.current, selectionRangeRef.current,
          externalDropCaretRef.current, event.clientX, event.clientY);
        externalDropCaretRef.current = false;
        void uploadImages(event.dataTransfer.files, range);
      }}
      onBlur={(event) => {
        // Switching applications/windows clears the browser selection and
        // produces a blur with no related target. Preserve the open editor so
        // the operator can return and drop a screenshot into it. An explicit
        // click elsewhere in this window still saves through the normal blur
        // path (or blurCanvasField for non-focusable canvas targets).
        if (sourceModeTransitionRef.current || event.currentTarget.closest('.rehab-rich-editor')?.contains(event.relatedTarget)) return;
        rememberSelection();
        if (blurSaveTimerRef.current) window.clearTimeout(blurSaveTimerRef.current);
        blurSaveTimerRef.current = window.setTimeout(() => {
          blurSaveTimerRef.current = null;
          if (windowBlurredRef.current || document.hidden) { externalDropCaretRef.current = true; return; }
          if (linkDialogOpenRef.current) return;
          save();
        }, 0);
      }}
      onKeyDown={handleEditorKeyDown} />
    {uploadError ? <small className="rehab-rich-editor__error">{uploadError}</small> : null}
    {paletteError ? <small className="rehab-rich-editor__error" role="alert">{paletteError}</small> : null}
    {styleDefaultsError ? <small className="rehab-rich-editor__error" role="alert">{styleDefaultsError}</small> : null}
    <div className="rehab-node-detail-actions"><button type="button" className="rehab-node-detail-blocks" onClick={() => {
      const html = sourceMode ? sourceHtml : editorHtmlForStorage();
      if (initialContent.version === 2 && sanitizeRichHtmlDraft(html) === sanitizeRichHtmlDraft(initialVisualHtml)) {
        setBlockDraftActiveId(initialContent.blocks[0]?.id || null); setBlockDraft(initialContent.blocks); return;
      }
      const converted = workflowHtmlToBlocksAtTextOffset(html, sourceMode ? undefined : selectionOffsets()?.start);
      setBlockDraftActiveId(converted.activeBlockId); setBlockDraft(converted.blocks);
    }}><i className="pi pi-th-large" aria-hidden="true" /> {initialContent.version === 2 ? 'Edit blocks' : 'Convert to blocks'}</button><button type="button" onClick={onCancel}>Cancel</button><button type="button" className="rehab-node-detail-save" onClick={save}>Save</button></div>
    <AppFormDialog title="Add a link" visible={Boolean(linkDraft)} onHide={closeLinkDialog} modal draggable={false} dismissableMask className="rehab-link-dialog">
      <p className="rehab-link-dialog__intro">Choose the text readers will see, then paste the page address.</p>
      <AppField label="Link name" htmlFor="rehab-link-name">
        <input id="rehab-link-name" value={linkDraft?.name || ''} maxLength={200} autoFocus placeholder="Product page" onChange={(event) => setLinkDraft((current) => ({ ...current, name: event.target.value, error: '' }))}
          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); document.getElementById('rehab-link-url')?.focus(); } }} />
      </AppField>
      <AppField label="Web address" htmlFor="rehab-link-url">
        <input id="rehab-link-url" type="url" inputMode="url" value={linkDraft?.url || ''} placeholder="https://example.com" onChange={(event) => setLinkDraft((current) => ({ ...current, url: event.target.value, error: '' }))}
          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); submitLink(); } }} />
      </AppField>
      {linkDraft?.error ? <small className="rehab-link-dialog__error" role="alert">{linkDraft.error}</small> : null}
      <AppActionGroup><AppButton variant="text" label="Cancel" onClick={closeLinkDialog} /><AppButton variant="primary" label="Add link" icon="pi pi-link" disabled={!linkDraft?.name?.trim() || !linkDraft?.url?.trim()} onClick={submitLink} /></AppActionGroup>
    </AppFormDialog>
  </div>;
};
export const INSPECTION_AREAS = [
  { category: 'Exterior', icon: 'pi-home', areas: [
    'Roof', 'Gutters / Soffit / Fascia', 'Siding', 'Exterior Painting', 'Decks / Porches',
    'Concrete', 'Garage', 'Landscaping', 'Septic System', 'Foundation',
  ] },
  { category: 'Interior', icon: 'pi-building', areas: [
    'Demo', 'Plumbing', 'Electrical', 'HVAC', 'Framing', 'Insulation', 'Sheetrock',
    'Carpentry', 'Interior Painting', 'Cabinets / Countertops', 'Flooring',
  ] },
  { category: 'General', icon: 'pi-list', areas: [
    'Permits', 'Mold', 'Termites', 'Miscellaneous', 'Value Add Opportunities',
  ] },
];
const uniqueId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const isGroupCollapsed = (node) => node.type === 'inspection-category'
  ? node.collapsed !== false
  : node.collapsed === true;

export const nodePositionFromPointer = ({ clientX, clientY, bounds, scrollLeft = 0, scrollTop = 0, offsetX, offsetY }) => ({
  x: Math.max(20, clientX - bounds.left + scrollLeft - offsetX),
  y: Math.max(20, clientY - bounds.top + scrollTop - offsetY),
});

export const canvasPointFromPointer = ({ clientX, clientY, bounds, scrollLeft = 0, scrollTop = 0, zoom = 1 }) => ({
  x: (clientX - bounds.left + scrollLeft) / zoom,
  y: (clientY - bounds.top + scrollTop) / zoom,
});

export const clampWorkflowZoom = (zoom) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(zoom.toFixed(2))));

// The given roots plus every node below them in the parent_id hierarchy.
export const workflowDescendantIds = (nodes, rootIds) => {
  const included = new Set(rootIds);
  const childrenByParent = new Map();
  nodes.forEach((node) => {
    if (!node.parent_id) return;
    const children = childrenByParent.get(node.parent_id) || [];
    children.push(node.id);
    childrenByParent.set(node.parent_id, children);
  });
  const pending = [...included];
  while (pending.length) {
    const parentId = pending.pop();
    (childrenByParent.get(parentId) || []).forEach((childId) => {
      if (included.has(childId)) return;
      included.add(childId);
      pending.push(childId);
    });
  }
  return included;
};
export const removeWorkflowNodeTrees = (nodes, edges, rootIds) => {
  const removed = workflowDescendantIds(nodes, rootIds);
  return {
    nodes: nodes.filter((node) => !removed.has(node.id)),
    edges: edges.filter((edge) => !removed.has(edge.source) && !removed.has(edge.target)),
  };
};

export const createContractorBundle = (x, y, createId = uniqueId) => {
  const contractorId = createId('contractor-bids');
  const contractor = { id: contractorId, type: 'contractor-bids', label: 'Contractor Bids', detail: '4 bid review fields', icon: 'pi-users', x, y, collapsed: false };
  const offsets = [-240, -80, 80, 240];
  const children = BID_CHILDREN.map(([type, label, icon], index) => ({
    id: createId(type), type, label, icon, parent_id: contractorId,
    detail: type === 'bid-scoring' ? 'Score the completed work' : type === 'bid-lessons' ? 'Capture what we learned' : '',
    x: x + offsets[index], y: contractor.y - 128,
  }));
  const edges = children.map((child, index) => ({
    id: createId('edge'), source: contractorId, target: child.id,
    source_handle: `top-${index}`, target_handle: 'bottom',
  }));
  return { nodes: [contractor, ...children], edges };
};

export const createInspectorDocumentationBundle = (x, y, createId = uniqueId) => {
  const originX = x;
  const inspectorId = createId('inspector-documentation');
  const inspector = {
    id: inspectorId, type: 'inspector-documentation', label: 'Inspector Documentation',
    detail: '0 / 26 areas documented', icon: 'pi-camera', x: originX, y, collapsed: false,
  };
  const nodes = [inspector];
  const edges = [];
  const columnOffsets = [-260, 0, 260];

  INSPECTION_AREAS.forEach(({ category, icon, areas }, categoryIndex) => {
    const categoryId = createId(`inspection-${category.toLowerCase()}`);
    const categoryNode = {
      id: categoryId, type: 'inspection-category', label: category,
      detail: `${areas.length} documentation areas`, icon, parent_id: inspectorId,
      x: originX + columnOffsets[categoryIndex], y: inspector.y - 128, collapsed: true,
    };
    nodes.push(categoryNode);
    edges.push({
      id: createId('edge'), source: inspectorId, target: categoryId,
      source_handle: `top-${categoryIndex}`,
      target_handle: 'top',
    });

    let previousId = categoryId;
    areas.forEach((label, areaIndex) => {
      const areaId = createId('inspection-area');
      nodes.push({
        id: areaId, type: 'inspection-area', label, icon: 'pi-check-square',
        detail: 'Awaiting documentation', parent_id: categoryId,
        x: categoryNode.x, y: categoryNode.y + 92 + (areaIndex * 84),
      });
      edges.push({
        id: createId('edge'), source: previousId, target: areaId,
        source_handle: 'bottom', target_handle: 'top',
      });
      previousId = areaId;
    });
  });

  return { nodes, edges };
};

export const normalizeInspectorCategoryEdges = (nodes, edges) => {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const categoryHandle = new Map(INSPECTION_AREAS.map(({ category }, index) => [category, `top-${index}`]));
  return edges.map((edge) => {
    const source = nodeById.get(edge.source);
    const target = nodeById.get(edge.target);
    if (source?.type !== 'inspector-documentation' || target?.type !== 'inspection-category') return edge;
    const sourceHandle = categoryHandle.get(target.label);
    return sourceHandle && edge.source_handle !== sourceHandle ? { ...edge, source_handle: sourceHandle } : edge;
  });
};

export const connectWorkflowPorts = (edges, fixed, port, createId = uniqueId) => {
  if (fixed.nodeId === port.nodeId && fixed.handle === port.handle) return edges;
  const drop = { ...port, role: fixed.role === 'source' ? 'target' : 'source' };
  const source = fixed.role === 'source' ? fixed : drop;
  const target = fixed.role === 'target' ? fixed : drop;
  if (source.nodeId === target.nodeId) return edges;
  const duplicate = edges.some((edge) => edge.source === source.nodeId && edge.target === target.nodeId
    && edge.source_handle === source.handle && edge.target_handle === target.handle);
  if (duplicate) return edges;
  return [...edges, {
    id: createId('edge'), source: source.nodeId, target: target.nodeId,
    source_handle: source.handle, target_handle: target.handle,
  }];
};

const edgeSignature = (edge) => `${edge.source}|${edge.source_handle}|${edge.target}|${edge.target_handle}`;
const appendUniqueEdges = (edges, additions) => {
  const signatures = new Set(edges.map(edgeSignature));
  return [...edges, ...additions.filter((edge) => {
    const signature = edgeSignature(edge);
    if (signatures.has(signature)) return false;
    signatures.add(signature);
    return true;
  })];
};

export const nodesInSelectionRect = (nodes, rectangle) => {
  const left = Math.min(rectangle.x1, rectangle.x2);
  const right = Math.max(rectangle.x1, rectangle.x2);
  const top = Math.min(rectangle.y1, rectangle.y2);
  const bottom = Math.max(rectangle.y1, rectangle.y2);
  return nodes.filter((node) => node.id !== 'property'
    && node.x < right && node.x + nodeWidth(node) > left
    && node.y < bottom && node.y + nodeHeight(node) > top).map((node) => node.id);
};

export const extractWorkflowSelection = (edges, nodeIds, createId = uniqueId) => {
  const selected = new Set(nodeIds);
  const incoming = edges.filter((edge) => !selected.has(edge.source) && selected.has(edge.target));
  const outgoing = edges.filter((edge) => selected.has(edge.source) && !selected.has(edge.target));
  const remaining = edges.filter((edge) => !incoming.includes(edge) && !outgoing.includes(edge));
  const bridges = incoming.flatMap((entry) => outgoing.map((exit) => ({
    id: createId('edge'), source: entry.source, target: exit.target,
    source_handle: entry.source_handle, target_handle: exit.target_handle,
  }))).filter((edge) => edge.source !== edge.target);
  return { edges: appendUniqueEdges(remaining, bridges), incoming, outgoing };
};

export const insertWorkflowSelection = (edges, nodeIds, anchorNodeId, extraction, createId = uniqueId) => {
  const selected = new Set(nodeIds);
  if (selected.has(anchorNodeId)) return edges;
  const displaced = edges.filter((edge) => edge.source === anchorNodeId && !selected.has(edge.target));
  const remaining = edges.filter((edge) => !displaced.includes(edge));
  const internal = edges.filter((edge) => selected.has(edge.source) && selected.has(edge.target));
  const entryIds = [...new Set((extraction?.incoming || []).map((edge) => edge.target))];
  const exitIds = [...new Set((extraction?.outgoing || []).map((edge) => edge.source))];
  const entries = entryIds.length ? entryIds : nodeIds.filter((nodeId) => !internal.some((edge) => edge.target === nodeId));
  const exits = exitIds.length ? exitIds : nodeIds.filter((nodeId) => !internal.some((edge) => edge.source === nodeId));
  const entryHandleById = new Map((extraction?.incoming || []).map((edge) => [edge.target, edge.target_handle]));
  const exitHandleById = new Map((extraction?.outgoing || []).map((edge) => [edge.source, edge.source_handle]));
  const anchorHandle = displaced[0]?.source_handle || 'bottom';
  const additions = entries.map((target) => ({ id: createId('edge'), source: anchorNodeId, target,
    source_handle: anchorHandle, target_handle: entryHandleById.get(target) || 'top' }));
  exits.forEach((source) => displaced.forEach((edge) => additions.push({ id: createId('edge'), source, target: edge.target,
    source_handle: exitHandleById.get(source) || 'bottom', target_handle: edge.target_handle })));
  return appendUniqueEdges(remaining, additions);
};

export const insertWorkflowSelectionIntoEdge = (edges, nodeIds, edgeId, extraction, createId = uniqueId, attachmentNodeId = null) => {
  const selected = new Set(nodeIds);
  const displaced = edges.find((edge) => edge.id === edgeId);
  if (!displaced || selected.has(displaced.source) || selected.has(displaced.target)) return edges;
  if (attachmentNodeId && selected.has(attachmentNodeId)) {
    return appendUniqueEdges(edges.filter((edge) => edge.id !== edgeId), [
      { id: createId('edge'), source: displaced.source, target: attachmentNodeId,
        source_handle: displaced.source_handle, target_handle: 'left' },
      { id: createId('edge'), source: attachmentNodeId, target: displaced.target,
        source_handle: 'right', target_handle: displaced.target_handle },
    ]);
  }
  const internal = edges.filter((edge) => selected.has(edge.source) && selected.has(edge.target));
  const entryIds = [...new Set((extraction?.incoming || []).map((edge) => edge.target))];
  const exitIds = [...new Set((extraction?.outgoing || []).map((edge) => edge.source))];
  const entries = entryIds.length ? entryIds : nodeIds.filter((nodeId) => !internal.some((edge) => edge.target === nodeId));
  const exits = exitIds.length ? exitIds : nodeIds.filter((nodeId) => !internal.some((edge) => edge.source === nodeId));
  const entryHandleById = new Map((extraction?.incoming || []).map((edge) => [edge.target, edge.target_handle]));
  const exitHandleById = new Map((extraction?.outgoing || []).map((edge) => [edge.source, edge.source_handle]));
  const additions = entries.map((target) => ({ id: createId('edge'), source: displaced.source, target,
    source_handle: displaced.source_handle, target_handle: entryHandleById.get(target) || 'top' }));
  exits.forEach((source) => additions.push({ id: createId('edge'), source, target: displaced.target,
    source_handle: exitHandleById.get(source) || 'bottom', target_handle: displaced.target_handle }));
  return appendUniqueEdges(edges.filter((edge) => edge.id !== edgeId), additions);
};

export const createWorkflowComponentSnapshot = (nodes, edges, nodeIds) => {
  const selected = new Set(nodeIds);
  const componentNodes = nodes.filter((node) => selected.has(node.id));
  if (!componentNodes.length) return { nodes: [], edges: [] };
  const originX = Math.min(...componentNodes.map((node) => node.x));
  const originY = Math.min(...componentNodes.map((node) => node.y));
  return {
    nodes: componentNodes.map((node) => ({ ...node, x: node.x - originX, y: node.y - originY })),
    edges: edges.filter((edge) => selected.has(edge.source) && selected.has(edge.target)),
  };
};

export const instantiateWorkflowComponent = (component, x, y, createId = uniqueId) => {
  const idMap = new Map(component.nodes.map((node) => [node.id, createId(node.type || 'component')]));
  return {
    nodes: component.nodes.map((node) => {
      const { parent_id: parentId, ...copy } = node;
      return { ...copy, id: idMap.get(node.id), x: x + node.x, y: y + node.y,
        ...(parentId && idMap.has(parentId) ? { parent_id: idMap.get(parentId) } : {}) };
    }),
    edges: component.edges.map((edge) => ({ ...edge, id: createId('edge'),
      source: idMap.get(edge.source), target: idMap.get(edge.target) })),
  };
};

export const isActualOverEstimate = (node, nodes) => {
  if (node.type !== 'bid-actual' || node.amount == null) return false;
  const estimate = nodes.find((candidate) => candidate.type === 'bid-estimate' && candidate.parent_id === node.parent_id);
  return estimate?.amount != null && Number(node.amount) > Number(estimate.amount);
};

export const nearestWorkflowPort = (ports, clientX, clientY, maximumDistance = Infinity) => {
  let nearest = null;
  let nearestDistance = maximumDistance;
  ports.forEach((port) => {
    const bounds = port.getBoundingClientRect();
    const distance = Math.hypot(clientX - (bounds.left + bounds.width / 2), clientY - (bounds.top + bounds.height / 2));
    if (distance <= nearestDistance) { nearest = port; nearestDistance = distance; }
  });
  return nearest;
};

export const portPoint = (node, handle) => {
  const width = nodeWidth(node);
  const height = nodeHeight(node);
  const side = portHandleSide(handle);
  const count = normalizePortLayout(node.port_layout, node)?.[side] || 1;
  const ratio = portPositionPercent(handle, count) / 100;
  if (side === 'left') return { x: node.x, y: node.y + height * ratio };
  if (side === 'right') return { x: node.x + width, y: node.y + height * ratio };
  if (side === 'top') return { x: node.x + width * ratio, y: node.y };
  return { x: node.x + width * ratio, y: node.y + height };
};
const edgePath = (start, end) => {
  if (Math.abs(end.x - start.x) >= Math.abs(end.y - start.y)) {
    const middleX = (start.x + end.x) / 2;
    return `M ${start.x} ${start.y} C ${middleX} ${start.y}, ${middleX} ${end.y}, ${end.x} ${end.y}`;
  }
  const middleY = (start.y + end.y) / 2;
  return `M ${start.x} ${start.y} C ${start.x} ${middleY}, ${end.x} ${middleY}, ${end.x} ${end.y}`;
};
const edgeControlPoints = (start, end) => Math.abs(end.x - start.x) >= Math.abs(end.y - start.y)
  ? [start, { x: (start.x + end.x) / 2, y: start.y }, { x: (start.x + end.x) / 2, y: end.y }, end]
  : [start, { x: start.x, y: (start.y + end.y) / 2 }, { x: end.x, y: (start.y + end.y) / 2 }, end];
const cubicPoint = ([start, first, second, end], t) => {
  const inverse = 1 - t;
  return {
    x: inverse ** 3 * start.x + 3 * inverse ** 2 * t * first.x + 3 * inverse * t ** 2 * second.x + t ** 3 * end.x,
    y: inverse ** 3 * start.y + 3 * inverse ** 2 * t * first.y + 3 * inverse * t ** 2 * second.y + t ** 3 * end.y,
  };
};

export const nearestWorkflowEdge = (edges, nodesById, points, maximumDistance = 34) => {
  let nearest = null;
  let nearestDistance = maximumDistance;
  edges.forEach((edge) => {
    const source = nodesById.get(edge.source);
    const target = nodesById.get(edge.target);
    if (!source || !target) return;
    const controls = edgeControlPoints(portPoint(source, edge.source_handle), portPoint(target, edge.target_handle));
    for (let sample = 0; sample <= 40; sample += 1) {
      const curvePoint = cubicPoint(controls, sample / 40);
      for (const point of points) {
        const distance = Math.hypot(point.x - curvePoint.x, point.y - curvePoint.y);
        if (distance <= nearestDistance) { nearest = edge.id; nearestDistance = distance; }
      }
    }
  });
  return nearest;
};

export default function RehabWorkflow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [error, setError] = useState('');
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [propertyPosition, setPropertyPosition] = useState({ x: 70, y: 155 });
  const [workflowLoading, setWorkflowLoading] = useState(true);
  const [, setWorkflowSaving] = useState(false);
  const [workflowSaveError, setWorkflowSaveError] = useState('');
  const [recipes, setRecipes] = useState([]);
  const [saveRecipeDialogOpen, setSaveRecipeDialogOpen] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState('');
  const [recipeName, setRecipeName] = useState('');
  const [recipeCopying, setRecipeCopying] = useState(false);
  const [recipeSaving, setRecipeSaving] = useState(false);
  const [components, setComponents] = useState([]);
  const [componentTitles, setComponentTitles] = useState([]);
  const [sidebarOrder, setSidebarOrder] = useState([]);
  const [stepsTitle, setStepsTitle] = useState('STEPS');
  const [stepOverrides, setStepOverrides] = useState({});
  const [componentDialogOpen, setComponentDialogOpen] = useState(false);
  const [quickPickerOpen, setQuickPickerOpen] = useState(false);
  const [quickPickerSearch, setQuickPickerSearch] = useState('');
  const [quickPickerSelection, setQuickPickerSelection] = useState('notes');
  const [pinnedQuickComponents, setPinnedQuickComponents] = useState(['notes']);
  const [quickPickerError, setQuickPickerError] = useState('');
  const [lenderComparisonOpen, setLenderComparisonOpen] = useState(false);
  const [lenderType, setLenderType] = useState('hard-money');
  const [selectedLenderId, setSelectedLenderId] = useState('');
  const [noteStyleEdit, setNoteStyleEdit] = useState(null);
  const [nodeStyleEdit, setNodeStyleEdit] = useState(null);
  const [portLayoutEdit, setPortLayoutEdit] = useState(null);
  const [selectionMenu, setSelectionMenu] = useState(null);
  const [componentSelectionIds, setComponentSelectionIds] = useState([]);
  const [componentName, setComponentName] = useState('');
  const [componentIcon, setComponentIcon] = useState('pi-box');
  const [iconSearch, setIconSearch] = useState('');
  const [componentSaving, setComponentSaving] = useState(false);
  const [nameEdit, setNameEdit] = useState(null);
  const [iconEdit, setIconEdit] = useState(null);
  const [nodeSizes, setNodeSizes] = useState({});
  const [quoteExpanded, setQuoteExpanded] = useState(false);
  const [workspaceSize, setWorkspaceSize] = useState(null);
  const [workspaceSizeError, setWorkspaceSizeError] = useState('');
  const workspaceRef = useRef(null);
  const workspaceResizeRef = useRef(null);
  const nodeResizeObserverRef = useRef(null);
  const nodeResizeRef = useRef(null);
  // In-memory only: a copied selection lives for this page session, never in browser storage.
  const workflowClipboardRef = useRef(null);
  const [network, setNetwork] = useState(null);
  const [workflowMedia, setWorkflowMedia] = useState({});
  const [textColors, setTextColors] = useState(DEFAULT_TEXT_COLORS);
  const [textColorsError, setTextColorsError] = useState('');
  const [textStyleDefaults, setTextStyleDefaults] = useState(DEFAULT_TEXT_STYLE_DEFAULTS);
  const [textStyleDefaultsError, setTextStyleDefaultsError] = useState('');
  const textColorsRequestedRef = useRef(false);
  // Loaded lazily the first time any node editor opens, then shared by every editor on the board.
  const ensureTextColors = useCallback(() => {
    if (textColorsRequestedRef.current) return;
    textColorsRequestedRef.current = true;
    Promise.resolve(apiJson(TEXT_COLORS_PREFERENCE)).then((preference) => {
      const saved = normalizeTextColors(preference?.value);
      if (saved) setTextColors(saved);
    }).catch(() => undefined);
  }, []);
  const changeTextColors = useCallback((next) => {
    const colors = normalizeTextColors(next) || [];
    let previous = DEFAULT_TEXT_COLORS;
    setTextColors((current) => { previous = current; return colors; });
    setTextColorsError('');
    Promise.resolve(apiJson(TEXT_COLORS_PREFERENCE, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: colors }) }))
      .catch((error) => { setTextColors(previous); setTextColorsError(error?.message || 'Could not save your color palette.'); });
  }, []);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      Promise.resolve(apiJson(TEXT_STYLE_DEFAULTS_PREFERENCE)).then((preference) => {
        const saved = normalizeTextStyleDefaults(preference?.value);
        if (saved) setTextStyleDefaults(saved);
      }).catch(() => undefined);
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);
  const changeTextStyleDefaults = useCallback((next) => {
    const normalized = normalizeTextStyleDefaults(next);
    if (!normalized) return;
    let previous = DEFAULT_TEXT_STYLE_DEFAULTS;
    setTextStyleDefaults((current) => { previous = current; return normalized; });
    setTextStyleDefaultsError('');
    Promise.resolve(apiJson(TEXT_STYLE_DEFAULTS_PREFERENCE, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: normalized }) }))
      .catch((error) => { setTextStyleDefaults(previous); setTextStyleDefaultsError(error?.message || 'Could not save your text style defaults.'); });
  }, []);
  const [networkStatus, setNetworkStatus] = useState('loading');
  const [peopleSearch, setPeopleSearch] = useState('');
  // Create-component picker: 'icons' builds a one-step component; 'people' builds a one-node people component.
  const [pickerMode, setPickerMode] = useState('icons');
  const [peopleFilter, setPeopleFilter] = useState('all');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [hardwareDirectory, setHardwareDirectory] = useState({ categories: [], devices: [] });
  const [hardwareStatus, setHardwareStatus] = useState('idle');
  const [hardwareSearch, setHardwareSearch] = useState('');
  const [hardwareFilter, setHardwareFilter] = useState('all');
  const [selectedHardware, setSelectedHardware] = useState(null);
  const [hardwareFormOpen, setHardwareFormOpen] = useState(false);
  const [hardwareSaving, setHardwareSaving] = useState(false);
  const [hardwareFormError, setHardwareFormError] = useState('');
  const [hardwareModeSaving, setHardwareModeSaving] = useState(null);
  const [hardwareModeError, setHardwareModeError] = useState('');
  const [hardwareDraft, setHardwareDraft] = useState({ category_id: '', category_name: '', display_name: '',
    manufacturer: '', model: '', serial_number: '', mac_address: '', ip_address: '', firmware_version: '' });
  const [personFormOpen, setPersonFormOpen] = useState(false);
  const [personDraft, setPersonDraft] = useState({ first_name: '', last_name: '', email: '', industry_key: 'contractor', temporary_password: '' });
  const [personSaving, setPersonSaving] = useState(false);
  const [personFormError, setPersonFormError] = useState('');
  const [createdPersonCredential, setCreatedPersonCredential] = useState(null);
  const [personToDelete, setPersonToDelete] = useState(null);
  const [personDeleteName, setPersonDeleteName] = useState('');
  const [personDeleting, setPersonDeleting] = useState(false);
  const autoComponentNameRef = useRef('');
  const [deleteLibraryItem, setDeleteLibraryItem] = useState(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [deleteLinkedPersonAccount, setDeleteLinkedPersonAccount] = useState(false);
  const [libraryDeleting, setLibraryDeleting] = useState(false);
  const [sidebarCleanupOpen, setSidebarCleanupOpen] = useState(false);
  const [sidebarCleanupSelection, setSidebarCleanupSelection] = useState([]);
  const [lastRecipe, setLastRecipe] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(true);
  const [draggingNodeId, setDraggingNodeId] = useState(null);
  const [interactionMode, setInteractionMode] = useState('pan');
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [selectionBox, setSelectionBox] = useState(null);
  const [nodeActionMenuId, setNodeActionMenuId] = useState(null);
  const [connectionDraft, setConnectionDraft] = useState(null);
  const [reconnectTargetEdgeId, setReconnectTargetEdgeId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const canvasRef = useRef(null);
  const canvasPanRef = useRef(null);
  const selectionRef = useRef(null);
  const nodeDragRef = useRef(null);
  const connectionDragRef = useRef(null);
  const suppressPortClickRef = useRef(false);
  const quickPickerSpaceHeldRef = useRef(false);
  const createComponentSlashHeldRef = useRef(false);
  const nodesRef = useRef([]);
  const edgesRef = useRef([]);
  const propertyPositionRef = useRef({ x: 70, y: 155 });
  const saveQueueRef = useRef(Promise.resolve());
  const pendingSavesRef = useRef(0);
  const canvasInitializedRef = useRef(false);
  const libraryDragRef = useRef(null);
  const sidebarOrderSaveTimerRef = useRef(null);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const amountEditSnapshotRef = useRef(null);

  useEffect(() => {
    // Unit tests mock a strict sequence of graph API responses; the pure resize helpers cover
    // this preference while production loads it independently from board data.
    if (process.env.NODE_ENV === 'test') return undefined;
    Promise.resolve(apiJson(WORKSPACE_SIZE_PREFERENCE)).then((preference) => {
      const saved = normalizeWorkspaceSize(preference?.value);
      if (saved) setWorkspaceSize(saved);
    }).catch(() => undefined);
    return undefined;
  }, []);
  useEffect(() => {
    const isTextEntry = (target) => Boolean(target.closest?.('input,textarea,select,[contenteditable="true"]'));
    const handleCreateComponentKeyDown = (event) => {
      if (event.code === 'Slash' && event.key === '/' && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        if (isTextEntry(event.target) || document.querySelector('.p-dialog')) return;
        createComponentSlashHeldRef.current = true;
        event.preventDefault();
        return;
      }
      if (event.key !== 'Shift' || !createComponentSlashHeldRef.current || isTextEntry(event.target)
          || document.querySelector('.p-dialog')) return;
      event.preventDefault(); createComponentSlashHeldRef.current = false;
      setIconSearch(''); setComponentSelectionIds([]); setComponentDialogOpen(true);
    };
    const releaseSlash = (event) => { if (event.code === 'Slash') createComponentSlashHeldRef.current = false; };
    const clearSlash = () => { createComponentSlashHeldRef.current = false; };
    window.addEventListener('keydown', handleCreateComponentKeyDown);
    window.addEventListener('keyup', releaseSlash);
    window.addEventListener('blur', clearSlash);
    return () => {
      window.removeEventListener('keydown', handleCreateComponentKeyDown);
      window.removeEventListener('keyup', releaseSlash);
      window.removeEventListener('blur', clearSlash);
    };
  }, []);

  const startWorkspaceResize = (event) => {
    const bounds = workspaceRef.current?.getBoundingClientRect();
    if (!bounds) return;
    workspaceResizeRef.current = {
      pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY,
      width: bounds.width, height: bounds.height,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  };
  const moveWorkspaceResize = (event) => {
    const active = workspaceResizeRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    setWorkspaceSize(resizeWorkspaceBox(active, event.clientX - active.clientX, event.clientY - active.clientY));
    event.preventDefault();
    event.stopPropagation();
  };
  const finishWorkspaceResize = (event) => {
    const active = workspaceResizeRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    workspaceResizeRef.current = null;
    const next = resizeWorkspaceBox(active, event.clientX - active.clientX, event.clientY - active.clientY);
    setWorkspaceSize(next);
    setWorkspaceSizeError('');
    Promise.resolve(apiJson(WORKSPACE_SIZE_PREFERENCE, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: next }),
    })).catch((saveError) => setWorkspaceSizeError(saveError?.message || 'Could not save the grid size.'));
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    setLastRecipe(null);
    Promise.all([apiJson(`/properties/${id}`), apiJson(`/properties/${id}/rehab-workflow`)])
      .then(([propertyRecord, workflow]) => {
        const loadedNodes = Array.isArray(workflow?.nodes) ? workflow.nodes : [];
        const rawEdges = Array.isArray(workflow?.edges) ? workflow.edges : [];
        const loadedEdges = normalizeInspectorCategoryEdges(loadedNodes, rawEdges);
        const loadedPosition = workflow?.property_position || { x: 70, y: 155 };
        setProperty(propertyRecord);
        setNodes(loadedNodes); nodesRef.current = loadedNodes;
        setEdges(loadedEdges); edgesRef.current = loadedEdges;
        setPropertyPosition(loadedPosition); propertyPositionRef.current = loadedPosition;
        undoStackRef.current = []; redoStackRef.current = [];
        if (workflow?.last_recipe_id) {
          setLastRecipe({ recipe_id: workflow.last_recipe_id, name: workflow.last_recipe_name });
          setSelectedRecipeId(workflow.last_recipe_id);
        }
        if (JSON.stringify(loadedEdges) !== JSON.stringify(rawEdges)) {
          setWorkflowSaving(true);
          apiJson(`/properties/${id}/rehab-workflow`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ nodes: loadedNodes, edges: loadedEdges, property_position: loadedPosition }),
          }).catch((saveError) => setWorkflowSaveError(saveError.message || 'Could not update the workflow connections.'))
            .finally(() => setWorkflowSaving(false));
        }
      })
      .catch((requestError) => {
        if (isUnauthorizedError(requestError)) navigate('/login', { replace: true });
        else setError(requestError.message || 'Could not load this property.');
      })
      .finally(() => setWorkflowLoading(false));
  }, [id, navigate]);

  const loadHardware = useCallback(() => {
    if (hardwareStatus === 'loading') return;
    setHardwareStatus('loading');
    Promise.resolve(apiJson(`/hardware?property_id=${id}`))
      .then((payload) => {
        setHardwareDirectory({ categories: Array.isArray(payload?.categories) ? payload.categories : [],
          devices: Array.isArray(payload?.devices) ? payload.devices : [] });
        setHardwareStatus('ready');
      })
      .catch((requestError) => {
        if (isUnauthorizedError(requestError)) { navigate('/login', { replace: true }); return; }
        setHardwareStatus(requestError?.status === 409 ? 'no-organization' : 'error');
      });
  }, [hardwareStatus, id, navigate]);
  const changeHardwareConnectionMode = useCallback((hardwareDeviceId, connectionMode) => {
    const previousDevice = hardwareDirectory.devices.find(
      (device) => device.hardware_device_id === hardwareDeviceId,
    ) || null;
    setHardwareModeSaving(hardwareDeviceId);
    setHardwareModeError('');
    setHardwareDirectory((current) => updateHardwareDirectoryDevice(
      current, hardwareDeviceId, { connection_mode: connectionMode },
    ));
    Promise.resolve(apiJson(`/hardware/devices/${hardwareDeviceId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ connection_mode: connectionMode }),
    })).then((payload) => {
      const updated = payload?.device;
      if (!updated) throw new Error('The hardware update returned no device.');
      setHardwareDirectory((current) => updateHardwareDirectoryDevice(current, hardwareDeviceId, updated));
    }).catch((requestError) => {
      if (previousDevice) {
        setHardwareDirectory((current) => updateHardwareDirectoryDevice(current, hardwareDeviceId, previousDevice));
      }
      if (isUnauthorizedError(requestError)) { navigate('/login', { replace: true }); return; }
      setHardwareModeError(requestError?.message || 'Could not update the hardware connection mode.');
    }).finally(() => setHardwareModeSaving(null));
  }, [hardwareDirectory.devices, navigate]);
  useEffect(() => {
    if (hardwareStatus === 'idle' && nodes.some((node) => node.type === 'hardware')) loadHardware();
  }, [hardwareStatus, loadHardware, nodes]);

  useEffect(() => {
    if (workflowLoading || canvasInitializedRef.current) return;
    window.requestAnimationFrame(() => {
      if (!canvasRef.current) return;
      canvasRef.current.scrollLeft = CANVAS_ORIGIN * zoom;
      canvasRef.current.scrollTop = CANVAS_ORIGIN * zoom;
      canvasInitializedRef.current = true;
    });
  }, [workflowLoading, zoom]);

  useEffect(() => {
    apiJson('/rehab-workflow-recipes')
      .then((records) => {
        const availableRecipes = Array.isArray(records) ? records : [];
        setRecipes(availableRecipes);
        const recentPropertyRecipe = availableRecipes.find((recipe) => String(recipe.source_property_id) === String(id));
        if (recentPropertyRecipe) {
          setLastRecipe((current) => current || {
            recipe_id: recentPropertyRecipe.recipe_id, name: recentPropertyRecipe.name,
          });
          setSelectedRecipeId((current) => current || recentPropertyRecipe.recipe_id);
        }
      })
      .catch((requestError) => {
        if (isUnauthorizedError(requestError)) navigate('/login', { replace: true });
      });
    apiJson('/rehab-workflow-components')
      .then((records) => setComponents(Array.isArray(records) ? records : []))
      .catch((requestError) => {
        if (isUnauthorizedError(requestError)) navigate('/login', { replace: true });
      });
    apiJson('/rehab-workflow-component-titles')
      .then((records) => setComponentTitles(Array.isArray(records) ? records : []))
      .catch((requestError) => {
        if (isUnauthorizedError(requestError)) navigate('/login', { replace: true });
      });
    apiJson('/rehab-workflow-sidebar-layout')
      .then((layout) => {
        setSidebarOrder(Array.isArray(layout?.items) ? layout.items : []);
        setStepsTitle(Object.prototype.hasOwnProperty.call(layout || {}, 'steps_title') ? layout.steps_title : 'STEPS');
        setStepOverrides(layout?.step_overrides && typeof layout.step_overrides === 'object' ? layout.step_overrides : {});
      })
      .catch((requestError) => {
        if (isUnauthorizedError(requestError)) navigate('/login', { replace: true });
      });
    Promise.resolve(apiJson('/rehab-network'))
      .then((records) => { setNetwork(Array.isArray(records) ? records : []); setNetworkStatus('ready'); })
      .catch((requestError) => {
        if (isUnauthorizedError(requestError)) { navigate('/login', { replace: true }); return; }
        setNetwork([]);
        setNetworkStatus(requestError?.status === 409 ? 'no-organization' : requestError?.status === 403 ? 'forbidden' : 'error');
      });
    Promise.resolve(apiJson(`/properties/${id}/rehab-media`))
      .then((payload) => setWorkflowMedia(Object.fromEntries((payload?.items || []).map((item) => [item.media_id, item]))))
      .catch((requestError) => { if (isUnauthorizedError(requestError)) navigate('/login', { replace: true }); });
  }, [id, navigate]);

  const peopleFilters = useMemo(() => [
    { key: 'all', label: 'All people', icon: 'pi-users', kinds: [], count: (network || []).length },
    ...PEOPLE_CATEGORIES.map((category) => ({ ...category,
      count: (network || []).filter((entry) => category.kinds.includes(entry.kind)).length })),
  ], [network]);
  const activePeopleFilter = peopleFilters.find((filter) => filter.key === peopleFilter) || peopleFilters[0];
  const filteredPeople = useMemo(() => {
    const query = peopleSearch.trim().toLocaleLowerCase();
    const category = peopleFilters.find((filter) => filter.key === peopleFilter);
    return (network || []).filter((entry) => (peopleFilter === 'all' || category?.kinds.includes(entry.kind))
      && (!query || [entry.name, entry.specialty, CONTACT_KINDS[entry.kind]?.label,
        ...(entry.contacts || []).map((person) => person.name)].some((text) => text?.toLocaleLowerCase().includes(query))))
      // Newest additions stack on top.
      .sort((left, right) => String(right.added_at || '').localeCompare(String(left.added_at || '')));
  }, [network, peopleSearch, peopleFilter, peopleFilters]);
  const hardwareFilters = useMemo(() => [
    { category_id: 'all', name: 'All hardware', icon_key: 'lu:LuCpu',
      count: hardwareDirectory.devices.length },
    ...hardwareDirectory.categories.map((category) => ({ ...category,
      count: hardwareDirectory.devices.filter((device) => device.category_id === category.category_id).length })),
  ], [hardwareDirectory]);
  const filteredHardware = useMemo(() => {
    const query = hardwareSearch.trim().toLocaleLowerCase();
    return hardwareDirectory.devices.filter((device) => (hardwareFilter === 'all' || device.category_id === hardwareFilter)
      && (!query || [device.display_name, device.manufacturer, device.model, device.serial_number,
        device.mac_address, device.ip_address, device.property_name].some((text) => String(text || '').toLocaleLowerCase().includes(query))));
  }, [hardwareDirectory, hardwareFilter, hardwareSearch]);
  useEffect(() => {
    if (!componentDialogOpen) return;
    setPickerMode('icons'); setPeopleFilter('all'); setPeopleSearch(''); setSelectedPerson(null);
    setHardwareFilter('all'); setHardwareSearch(''); setSelectedHardware(null);
    autoComponentNameRef.current = '';
  }, [componentDialogOpen]);
  const filteredQuickComponents = useMemo(() => {
    const query = quickPickerSearch.trim().toLocaleLowerCase();
    return QUICK_COMPONENTS.filter((component) => !query
      || `${component.label} ${component.description}`.toLocaleLowerCase().includes(query));
  }, [quickPickerSearch]);
  const pinnedQuickComponentRecords = useMemo(() => pinnedQuickComponents
    .map((key) => QUICK_COMPONENTS.find((component) => component.key === key)).filter(Boolean), [pinnedQuickComponents]);
  const availableQuickComponentRecords = useMemo(() => filteredQuickComponents
    .filter((component) => !pinnedQuickComponents.includes(component.key)), [filteredQuickComponents, pinnedQuickComponents]);
  const lenderNeeds = useMemo(() => {
    const purchase = Number(property?.purchase_price || property?.acquisition_price) || 120000;
    const rehab = Number(property?.rehab_budget || property?.estimated_rehab_cost) || 30000;
    return { project_cost: purchase + rehab, loan_amount: (purchase + rehab) * .8, credit_score: 720, holding_months: 12 };
  }, [property]);
  const rankedLenders = useMemo(() => rankDemoLenders(DEMO_LENDERS, lenderType, lenderNeeds), [lenderType, lenderNeeds]);
  useEffect(() => {
    if (!lenderComparisonOpen) return;
    setSelectedLenderId((current) => rankedLenders.some((lender) => lender.id === current)
      ? current : rankedLenders.find((lender) => lender.eligible)?.id || rankedLenders[0]?.id || '');
  }, [lenderComparisonOpen, rankedLenders]);
  useEffect(() => {
    Promise.resolve(apiJson(PINNED_COMPONENTS_PREFERENCE)).then((preference) => {
      if (!Array.isArray(preference?.value)) return;
      setPinnedQuickComponents(preference.value.filter((key, index, keys) => keys.indexOf(key) === index
        && QUICK_COMPONENTS.some((component) => component.key === key)));
    }).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (!quickPickerOpen) return;
    const first = filteredQuickComponents[0];
    if (first && !filteredQuickComponents.some((component) => component.key === quickPickerSelection)) {
      setQuickPickerSelection(first.key);
    }
  }, [filteredQuickComponents, quickPickerOpen, quickPickerSelection]);
  const choosePerson = (entry, person = null) => {
    setSelectedPerson({ kind: entry.kind, id: entry.id, ...(person ? { contact_id: person.contact_id } : {}) });
    // Fill the name from the chosen person unless the operator already typed their own.
    const suggested = person ? `${entry.name} · ${person.name}`
      : entry.organization_name ? `${entry.organization_name} · ${entry.name}` : entry.name;
    if (!componentName.trim() || componentName === autoComponentNameRef.current) {
      setComponentName(suggested.slice(0, 100)); autoComponentNameRef.current = suggested.slice(0, 100);
    }
  };
  const chooseHardware = (device) => {
    if (!device.selectable) return;
    setSelectedHardware(device.hardware_device_id);
    if (!componentName.trim() || componentName === autoComponentNameRef.current) {
      setComponentName(device.display_name.slice(0, 100)); autoComponentNameRef.current = device.display_name.slice(0, 100);
    }
  };
  const createHardwareDevice = async () => {
    if (hardwareSaving) return;
    setHardwareSaving(true); setHardwareFormError('');
    try {
      let categoryId = hardwareDraft.category_id;
      let createdCategory = null;
      if (!categoryId) {
        const categoryResult = await apiJson('/hardware/categories', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: hardwareDraft.category_name }),
        });
        createdCategory = categoryResult.category; categoryId = createdCategory.category_id;
      }
      const result = await apiJson('/hardware/devices', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
          category_id: categoryId, display_name: hardwareDraft.display_name,
          manufacturer: hardwareDraft.manufacturer, model: hardwareDraft.model,
          ...Object.fromEntries(['serial_number', 'mac_address', 'ip_address', 'firmware_version']
            .filter((key) => hardwareDraft[key].trim()).map((key) => [key, hardwareDraft[key].trim()])),
        }),
      });
      const device = { ...result.device, selectable: true, property_name: null };
      setHardwareDirectory((current) => ({
        categories: createdCategory ? [...current.categories, createdCategory] : current.categories,
        devices: [...current.devices, device],
      }));
      setHardwareFilter(categoryId); chooseHardware(device); setHardwareFormOpen(false);
      setHardwareDraft({ category_id: '', category_name: '', display_name: '', manufacturer: '', model: '',
        serial_number: '', mac_address: '', ip_address: '', firmware_version: '' });
    } catch (requestError) {
      setHardwareFormError(requestError?.message || 'Could not create the hardware device.');
    } finally {
      setHardwareSaving(false);
    }
  };
  const createPersonAccount = async () => {
    if (personSaving) return;
    setPersonSaving(true); setPersonFormError('');
    try {
      const payload = { ...personDraft };
      if (!payload.temporary_password) delete payload.temporary_password;
      const result = await apiJson('/identity/people', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const entry = {
        kind: result.person.industry_key, id: result.person.person_id, name: result.person.display_name,
        status: 'active', specialty: CONTACT_KINDS[result.person.industry_key]?.label || '', contacts: [],
        added_at: new Date().toISOString(), directory_source: 'person_account',
      };
      setNetwork((current) => [entry, ...(current || [])]);
      setPeopleFilter(result.person.industry_key); choosePerson(entry);
      setCreatedPersonCredential({ name: result.person.display_name, email: result.person.email,
        temporaryPassword: result.temporary_password, expiresInHours: result.temporary_password_expires_in_hours });
      setPersonDraft({ first_name: '', last_name: '', email: '', industry_key: 'contractor', temporary_password: '' });
    } catch (requestError) {
      setPersonFormError(requestError?.message || 'Could not create the account.');
    } finally {
      setPersonSaving(false);
    }
  };
  const deletePersonAccount = async () => {
    if (!personToDelete || personDeleting || personDeleteName.trim() !== personToDelete.name) return;
    setPersonDeleting(true); setPersonFormError('');
    try {
      await apiJson(`/identity/people/${personToDelete.id}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmation_name: personDeleteName.trim() }),
      });
      setNetwork((current) => (current || []).filter((entry) => !(entry.kind === personToDelete.kind && entry.id === personToDelete.id)));
      if (selectedPerson?.kind === personToDelete.kind && selectedPerson?.id === personToDelete.id) setSelectedPerson(null);
      setPersonToDelete(null); setPersonDeleteName('');
    } catch (requestError) {
      setPersonFormError(requestError?.message || 'Could not close the account.');
    } finally {
      setPersonDeleting(false);
    }
  };
  useEffect(() => () => window.clearTimeout(sidebarOrderSaveTimerRef.current), []);
  useEffect(() => { setDeleteConfirmationText(''); setDeleteLinkedPersonAccount(false); }, [deleteLibraryItem]);

  const customLibraryItems = useMemo(() => [
    ...components.map((component) => ({ kind: 'component', id: component.component_id, sortOrder: component.sort_order, record: component })),
    ...componentTitles.map((title) => ({ kind: 'title', id: title.title_id, sortOrder: title.sort_order, record: title })),
  ].sort((left, right) => (left.sortOrder ?? 1000) - (right.sortOrder ?? 1000)), [components, componentTitles]);
  const sidebarItems = useMemo(() => {
    const available = new Map();
    const hiddenSteps = new Set(sidebarOrder.filter((key) => key.startsWith('hidden:step:')).map((key) => key.slice(12)));
    customLibraryItems.forEach((item) => available.set(`${item.kind}:${item.id}`, item));
    if (stepsTitle !== null) available.set('system:steps', { kind: 'system-title', id: 'steps', record: { name: stepsTitle } });
    NODE_TYPES.forEach(([type, label, icon]) => {
      const override = stepOverrides[type] || {};
      if (!hiddenSteps.has(type)) available.set(`step:${type}`, { kind: 'step', id: type,
        record: { type, label: override.label || label, icon: override.icon || icon } });
    });
    const defaultKeys = [...customLibraryItems.map((item) => `${item.kind}:${item.id}`),
      ...(stepsTitle !== null ? ['system:steps'] : []), ...NODE_TYPES.filter(([type]) => !hiddenSteps.has(type)).map(([type]) => `step:${type}`)];
    const keys = [...sidebarOrder.filter((key, index) => available.has(key) && sidebarOrder.indexOf(key) === index)];
    defaultKeys.forEach((key) => { if (!keys.includes(key)) keys.push(key); });
    return keys.map((key) => ({ ...available.get(key), key }));
  }, [customLibraryItems, sidebarOrder, stepsTitle, stepOverrides]);

  const propertyNode = useMemo(() => property ? {
    id: 'property', label: property.nickname || property.address_line1,
    detail: `${property.address_line1}, ${property.city}, ${property.state}`,
    icon: 'pi-home', ...propertyPosition,
  } : null, [property, propertyPosition]);
  const allNodes = useMemo(() => propertyNode ? [propertyNode, ...nodes] : nodes, [propertyNode, nodes]);
  // Expanded descriptions and resized nodes change a node's real size, so connection geometry
  // uses the rendered size when the browser can measure it.
  const observeNodeElement = (element) => {
    if (!element || typeof ResizeObserver === 'undefined') return;
    if (!nodeResizeObserverRef.current) {
      nodeResizeObserverRef.current = new ResizeObserver((entries) => setNodeSizes((current) => {
        let next = current;
        entries.forEach(({ target }) => {
          const nodeId = target.dataset.nodeId;
          const size = { w: target.offsetWidth, h: target.offsetHeight };
          if (!nodeId || !size.w || (current[nodeId]?.w === size.w && current[nodeId]?.h === size.h)) return;
          if (next === current) next = { ...current };
          next[nodeId] = size;
        });
        return next;
      }));
    }
    nodeResizeObserverRef.current.observe(element);
  };
  useEffect(() => () => nodeResizeObserverRef.current?.disconnect(), []);
  const withRenderedSize = (node) => ({ ...node, renderWidth: nodeSizes[node.id]?.w, renderHeight: nodeSizes[node.id]?.h });
  const allNodesById = useMemo(() => new Map(allNodes.map((node) => [node.id, {
    ...node, x: node.x + CANVAS_ORIGIN, y: node.y + CANVAS_ORIGIN,
    renderWidth: nodeSizes[node.id]?.w, renderHeight: nodeSizes[node.id]?.h,
  }])), [allNodes, nodeSizes]);
  const visibleNodes = useMemo(() => {
    const nodeById = new Map(nodes.map((node) => [node.id, node]));
    const hiddenByBranchCollapse = new Set();
    allNodes.forEach((node) => {
      if (!node.branches_collapsed || !normalizeCollapseSides(node.collapse_sides)?.length) return;
      connectedBranchNodeIds(edges, node.id, node.collapse_sides).forEach((nodeId) => hiddenByBranchCollapse.add(nodeId));
    });
    const isHiddenByCollapsedParent = (node) => {
      let parent = nodeById.get(node.parent_id);
      const visited = new Set();
      while (parent && !visited.has(parent.id)) {
        visited.add(parent.id);
        if (['inspector-documentation', 'inspection-category', 'contractor-bids'].includes(parent.type)
            && isGroupCollapsed(parent)) return true;
        parent = nodeById.get(parent.parent_id);
      }
      return false;
    };
    return allNodes.filter((node) => !hiddenByBranchCollapse.has(node.id) && !isHiddenByCollapsedParent(node));
  }, [allNodes, nodes, edges]);
  const visibleNodeIds = useMemo(() => new Set(visibleNodes.map((node) => node.id)), [visibleNodes]);
  const visibleEdges = useMemo(() => edges.filter((edge) => (
    visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
  )), [edges, visibleNodeIds]);
  const worldSize = useMemo(() => ({
    width: Math.max(BASE_WORLD_SIZE, ...allNodes.map((node) => node.x + CANVAS_ORIGIN + 800)),
    height: Math.max(BASE_WORLD_SIZE, ...allNodes.map((node) => node.y + CANVAS_ORIGIN + 800)),
  }), [allNodes]);

  const persistWorkflow = (nextNodes = nodesRef.current, nextPropertyPosition = propertyPositionRef.current, nextEdges = edgesRef.current) => {
    const payload = { nodes: nextNodes, edges: nextEdges, property_position: nextPropertyPosition };
    pendingSavesRef.current += 1;
    setWorkflowSaving(true); setWorkflowSaveError('');
    saveQueueRef.current = saveQueueRef.current.catch(() => undefined).then(() => apiJson(`/properties/${id}/rehab-workflow`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    })).catch((saveError) => setWorkflowSaveError(saveError.message || 'Could not save the workflow.')).finally(() => {
      pendingSavesRef.current -= 1;
      if (pendingSavesRef.current === 0) setWorkflowSaving(false);
    });
  };

  const graphSnapshot = () => ({
    nodes: nodesRef.current, edges: edgesRef.current, propertyPosition: propertyPositionRef.current,
  });
  const sameGraphSnapshot = (left, right) => JSON.stringify(left) === JSON.stringify(right);
  const rememberGraphChange = (previous) => {
    const current = graphSnapshot();
    if (sameGraphSnapshot(previous, current)) return;
    undoStackRef.current = [...undoStackRef.current.slice(-99), previous];
    redoStackRef.current = [];
  };
  const commitGraph = (nextNodes, nextEdges) => {
    const previous = graphSnapshot();
    nodesRef.current = nextNodes; edgesRef.current = nextEdges;
    setNodes(nextNodes); setEdges(nextEdges);
    rememberGraphChange(previous);
    void persistWorkflow(nextNodes, propertyPositionRef.current, nextEdges);
  };
  const restoreGraphSnapshot = (snapshot) => {
    nodesRef.current = snapshot.nodes; edgesRef.current = snapshot.edges;
    propertyPositionRef.current = snapshot.propertyPosition;
    setNodes(snapshot.nodes); setEdges(snapshot.edges); setPropertyPosition(snapshot.propertyPosition);
    setSelectedNodeIds([]); setConnectionDraft(null); connectionDragRef.current = null;
    void persistWorkflow(snapshot.nodes, snapshot.propertyPosition, snapshot.edges);
  };
  const undoGraph = () => {
    const snapshot = undoStackRef.current.pop();
    if (!snapshot) return;
    redoStackRef.current.push(graphSnapshot());
    restoreGraphSnapshot(snapshot);
  };
  const redoGraph = () => {
    const snapshot = redoStackRef.current.pop();
    if (!snapshot) return;
    undoStackRef.current.push(graphSnapshot());
    restoreGraphSnapshot(snapshot);
  };
  const copyRecipe = async (recipeId, reload = false) => {
    if (!recipeId || recipeCopying || (!reload && recipeId === lastRecipe?.recipe_id)) return;
    setRecipeCopying(true); setWorkflowSaveError('');
    try {
      await saveQueueRef.current.catch(() => undefined);
      const copied = await apiJson(`/properties/${id}/rehab-workflow/copy`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipe_id: recipeId }),
      });
      const copiedNodes = Array.isArray(copied?.nodes) ? copied.nodes : [];
      const copiedEdges = normalizeInspectorCategoryEdges(copiedNodes, Array.isArray(copied?.edges) ? copied.edges : []);
      const copiedPosition = copied?.property_position || { x: 70, y: 155 };
      const previous = graphSnapshot();
      nodesRef.current = copiedNodes; edgesRef.current = copiedEdges; propertyPositionRef.current = copiedPosition;
      setNodes(copiedNodes); setEdges(copiedEdges); setPropertyPosition(copiedPosition);
      rememberGraphChange(previous);
      const usedRecipe = recipes.find((recipe) => recipe.recipe_id === recipeId);
      setLastRecipe(usedRecipe ? { recipe_id: usedRecipe.recipe_id, name: usedRecipe.name } : null);
      setSelectedRecipeId(recipeId);
    } catch (copyError) {
      setWorkflowSaveError(copyError.message || 'Could not copy the selected workflow.');
    } finally {
      setRecipeCopying(false);
    }
  };
  const saveRecipe = async (requestedName = recipeName) => {
    const normalizedName = requestedName.trim();
    if (!normalizedName || recipeSaving) return;
    setRecipeSaving(true); setWorkflowSaveError('');
    try {
      await saveQueueRef.current.catch(() => undefined);
      const saved = await apiJson('/rehab-workflow-recipes', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: normalizedName, property_id: Number(id) }),
      });
      setRecipes((current) => [saved, ...current.filter((recipe) => recipe.recipe_id !== saved.recipe_id)]);
      setLastRecipe({ recipe_id: saved.recipe_id, name: saved.name });
      setSelectedRecipeId(saved.recipe_id);
      setSaveRecipeDialogOpen(false); setRecipeName('');
    } catch (saveError) {
      setWorkflowSaveError(saveError.message || 'Could not save the workflow recipe.');
    } finally {
      setRecipeSaving(false);
    }
  };
  const saveComponent = async () => {
    const normalizedName = componentName.trim();
    if (!normalizedName || componentSaving) return;
    const availableNodeIds = new Set(nodesRef.current.map((node) => node.id));
    const selectedComponentNodeIds = componentSelectionIds.filter((nodeId) => availableNodeIds.has(nodeId));
    const personEntry = !selectedComponentNodeIds.length && pickerMode === 'people' && selectedPerson
      ? network?.find((entry) => entry.kind === selectedPerson.kind && entry.id === selectedPerson.id) : null;
    const hardwareEntry = !selectedComponentNodeIds.length && pickerMode === 'hardware' && selectedHardware
      ? hardwareDirectory.devices.find((device) => device.hardware_device_id === selectedHardware && device.selectable) : null;
    if (!selectedComponentNodeIds.length && pickerMode === 'people' && !personEntry) return;
    if (!selectedComponentNodeIds.length && pickerMode === 'hardware' && !hardwareEntry) return;
    const savedIcon = personEntry ? CONTACT_KINDS[personEntry.kind]?.icon || 'pi-user'
      : hardwareEntry ? hardwareDirectory.categories.find((category) => category.category_id === hardwareEntry.category_id)?.icon_key || 'lu:LuCpu'
        : componentIcon;
    const snapshot = selectedComponentNodeIds.length
      ? createWorkflowComponentSnapshot(nodesRef.current, edgesRef.current, selectedComponentNodeIds)
      : personEntry ? { nodes: [{ id: 'component-node', type: 'contact', label: personEntry.name, detail: '',
        icon: savedIcon, x: 0, y: 0, contact_ref: selectedPerson }], edges: [] }
        : hardwareEntry ? { nodes: [{ id: 'component-node', type: 'hardware', label: hardwareEntry.display_name, detail: '',
          icon: savedIcon, x: 0, y: 0, hardware_ref: { kind: 'hardware_device', id: hardwareEntry.hardware_device_id } }], edges: [] }
        : { nodes: [{ id: 'component-node', type: 'custom', label: normalizedName, detail: 'Workflow step',
          icon: componentIcon, x: 0, y: 0 }], edges: [] };
    if (!snapshot.nodes.length) return;
    setComponentSaving(true); setWorkflowSaveError('');
    try {
      const saved = await apiJson('/rehab-workflow-components', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: normalizedName, icon: savedIcon, ...snapshot }),
      });
      setComponents((current) => [saved, ...current.filter((component) => component.component_id !== saved.component_id)]);
      setPaletteOpen(true);
      setComponentDialogOpen(false); setComponentSelectionIds([]); setComponentName(''); setComponentIcon('pi-box');
    } catch (saveError) {
      setWorkflowSaveError(saveError.message || 'Could not save the workflow component.');
    } finally {
      setComponentSaving(false);
    }
  };
  const updateComponent = async (componentId, changes) => {
    setWorkflowSaveError('');
    let previous;
    setComponents((current) => current.map((component) => {
      if (component.component_id !== componentId) return component;
      previous = component;
      return { ...component, ...changes };
    }));
    try {
      const updated = await apiJson(`/rehab-workflow-components/${componentId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(changes),
      });
      setComponents((current) => current.map((component) => component.component_id === componentId
        ? { ...component, ...updated } : component));
    } catch (updateError) {
      if (previous) setComponents((current) => current.map((component) => (
        component.component_id === componentId ? previous : component
      )));
      setWorkflowSaveError(updateError.message || 'Could not update the workflow component.');
    }
  };
  const createComponentTitle = async () => {
    setWorkflowSaveError('');
    try {
      const created = await apiJson('/rehab-workflow-component-titles', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: 'New title' }),
      });
      setComponentTitles((current) => [...current, created]);
      setNameEdit({ kind: 'title', id: created.title_id, value: created.name, original: created.name });
    } catch (createError) {
      setWorkflowSaveError(createError.message || 'Could not create the component title.');
    }
  };
  const updateComponentTitle = async (titleId, name) => {
    setWorkflowSaveError('');
    let previous;
    setComponentTitles((current) => current.map((title) => {
      if (title.title_id !== titleId) return title;
      previous = title;
      return { ...title, name };
    }));
    try {
      const updated = await apiJson(`/rehab-workflow-component-titles/${titleId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }),
      });
      setComponentTitles((current) => current.map((title) => title.title_id === titleId
        ? { ...title, ...updated } : title));
    } catch (updateError) {
      if (previous) setComponentTitles((current) => current.map((title) => (
        title.title_id === titleId ? previous : title
      )));
      setWorkflowSaveError(updateError.message || 'Could not update the component title.');
    }
  };
  const persistSidebarOrder = (items, immediately = false, nextStepsTitle = stepsTitle, nextStepOverrides = stepOverrides) => {
    window.clearTimeout(sidebarOrderSaveTimerRef.current);
    const save = () => Promise.resolve(apiJson('/rehab-workflow-sidebar-layout', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, steps_title: nextStepsTitle, step_overrides: nextStepOverrides }),
    })).catch((reorderError) => setWorkflowSaveError(reorderError.message || 'Could not save the sidebar order.'));
    if (immediately) void save();
    else sidebarOrderSaveTimerRef.current = window.setTimeout(save, 160);
  };
  const reorderSidebar = (draggedKey, targetKey) => {
    if (!draggedKey || draggedKey === targetKey) return;
    const current = sidebarItems.map((item) => item.key);
    const from = current.indexOf(draggedKey);
    const to = current.indexOf(targetKey);
    if (from < 0 || to < 0) return;
    const [moved] = current.splice(from, 1);
    current.splice(current.indexOf(targetKey), 0, moved);
    const persisted = [...current, ...sidebarOrder.filter((key) => key.startsWith('hidden:step:'))];
    setSidebarOrder(persisted);
    persistSidebarOrder(persisted);
  };
  const moveSidebarItemToEnd = (draggedKey) => {
    if (!draggedKey) return;
    const current = sidebarItems.map((item) => item.key).filter((key) => key !== draggedKey);
    current.push(draggedKey);
    const persisted = [...current, ...sidebarOrder.filter((key) => key.startsWith('hidden:step:'))];
    setSidebarOrder(persisted);
    persistSidebarOrder(persisted, true);
  };
  const confirmDeleteLibraryItem = async () => {
    const item = deleteLibraryItem;
    if (!item || libraryDeleting) return;
    if (item.kind === 'selection') {
      await removeSelectedSidebarItems(item.selectionKeys);
      setDeleteLibraryItem(null);
      return;
    }
    const closeLinkedAccount = item.kind === 'component' && item.person && deleteLinkedPersonAccount;
    if (closeLinkedAccount && deleteConfirmationText.trim() !== item.person.name) return;
    if (item.kind === 'system-title') {
      const nextOrder = [...sidebarItems.map((sidebarItem) => sidebarItem.key).filter((key) => key !== 'system:steps'),
        ...sidebarOrder.filter((key) => key.startsWith('hidden:step:'))];
      setStepsTitle(null); setSidebarOrder(nextOrder); setDeleteLibraryItem(null);
      persistSidebarOrder(nextOrder, true, null);
      return;
    }
    if (item.kind === 'step') {
      const nextOrder = [...sidebarItems.map((sidebarItem) => sidebarItem.key).filter((key) => key !== `step:${item.id}`),
        ...sidebarOrder.filter((key) => key.startsWith('hidden:step:')), `hidden:step:${item.id}`];
      setSidebarOrder(nextOrder); setDeleteLibraryItem(null);
      persistSidebarOrder(nextOrder, true);
      return;
    }
    setLibraryDeleting(true); setWorkflowSaveError('');
    const endpoint = item.kind === 'component'
      ? `/rehab-workflow-components/${item.id}` : `/rehab-workflow-component-titles/${item.id}`;
    try {
      if (closeLinkedAccount) {
        await apiJson(`/identity/people/${item.person.id}`, {
          method: 'DELETE', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ confirmation_name: item.person.name }),
        });
      }
      await apiJson(endpoint, { method: 'DELETE' });
      if (item.kind === 'component') {
        setComponents((current) => current.filter((component) => component.component_id !== item.id));
        if (closeLinkedAccount) {
          setNetwork((current) => (current || []).filter((entry) => !(entry.kind === item.person.kind && entry.id === item.person.id)));
          if (selectedPerson?.kind === item.person.kind && selectedPerson?.id === item.person.id) setSelectedPerson(null);
        }
      }
      else setComponentTitles((current) => current.filter((title) => title.title_id !== item.id));
      setDeleteLibraryItem(null);
    } catch (deleteError) {
      setWorkflowSaveError(deleteError.message || `Could not delete ${item.name}.`);
    } finally {
      setLibraryDeleting(false);
    }
  };
  const removeSelectedSidebarItems = async (selectionKeys = sidebarCleanupSelection) => {
    if (!selectionKeys.length) return;
    const selected = new Set(selectionKeys);
    const selectedComponents = sidebarItems.filter((item) => selected.has(item.key) && item.kind === 'component');
    setLibraryDeleting(true); setWorkflowSaveError('');
    const componentResults = await Promise.allSettled(selectedComponents.map((item) => (
      apiJson(`/rehab-workflow-components/${item.id}`, { method: 'DELETE' })
    )));
    const deletedComponentIds = new Set(selectedComponents.filter((item, index) => (
      componentResults[index].status === 'fulfilled'
    )).map((item) => item.id));
    const failedComponentNames = selectedComponents.filter((item, index) => (
      componentResults[index].status === 'rejected'
    )).map((item) => item.record.name);
    const removedKeys = new Set([...selected].filter((key) => (
      !key.startsWith('component:') || deletedComponentIds.has(key.slice('component:'.length))
    )));
    const visibleKeys = sidebarItems.map((item) => item.key).filter((key) => !removedKeys.has(key));
    const hiddenKeys = sidebarOrder.filter((key) => key.startsWith('hidden:step:'));
    removedKeys.forEach((key) => {
      const stepType = key.startsWith('step:') ? key.slice(5) : '';
      if (stepType && !hiddenKeys.includes(`hidden:step:${stepType}`)) hiddenKeys.push(`hidden:step:${stepType}`);
    });
    const nextOrder = [...visibleKeys, ...hiddenKeys];
    if (deletedComponentIds.size) setComponents((current) => current.filter((component) => (
      !deletedComponentIds.has(component.component_id)
    )));
    setSidebarOrder(nextOrder);
    setSidebarCleanupSelection([]);
    setSidebarCleanupOpen(false);
    persistSidebarOrder(nextOrder, true);
    if (failedComponentNames.length) setWorkflowSaveError(`Could not remove ${failedComponentNames.join(', ')}.`);
    setLibraryDeleting(false);
  };
  const beginLibraryMultiSelect = () => {
    const itemKey = deleteLibraryItem?.key;
    if (!itemKey) return;
    setSidebarCleanupOpen(true);
    setSidebarCleanupSelection([itemKey]);
    setDeleteLibraryItem(null);
  };
  const openSelectedLibraryRemoval = (itemKey) => {
    const selectionKeys = sidebarCleanupSelection.includes(itemKey)
      ? sidebarCleanupSelection : [...sidebarCleanupSelection, itemKey];
    setSidebarCleanupSelection(selectionKeys);
    setDeleteLibraryItem({ kind: 'selection', selectionKeys, name: `${selectionKeys.length} library item${selectionKeys.length === 1 ? '' : 's'}` });
  };
  const stopLibraryMultiSelect = () => {
    setSidebarCleanupOpen(false);
    setSidebarCleanupSelection([]);
    setDeleteLibraryItem(null);
  };
  const updateNodeFields = (nodeId, changes) => commitGraph(
    nodesRef.current.map((node) => node.id === nodeId ? { ...node, ...changes } : node), edgesRef.current,
  );
  const updateStepOverride = (stepType, changes) => {
    const nextOverrides = { ...stepOverrides, [stepType]: { ...stepOverrides[stepType], ...changes } };
    setStepOverrides(nextOverrides);
    persistSidebarOrder([...sidebarItems.map((item) => item.key), ...sidebarOrder.filter((key) => key.startsWith('hidden:step:'))],
      true, stepsTitle, nextOverrides);
  };
  const commitNameEdit = () => {
    const edit = nameEdit;
    if (!edit) return;
    setNameEdit(null);
    if (edit.field === 'detail') return;
    const name = edit.value.trim().slice(0, 100);
    if (!name || name === edit.original) return;
    if (edit.kind === 'node') updateNodeFields(edit.id, { label: name });
    else if (edit.kind === 'step') updateStepOverride(edit.id, { label: name });
    else if (edit.kind === 'component') void updateComponent(edit.id, { name });
    else if (edit.kind === 'title') void updateComponentTitle(edit.id, name);
    else {
      setStepsTitle(name);
      persistSidebarOrder(sidebarItems.map((item) => item.key), true, name);
    }
  };
  const chooseEditedIcon = (icon) => {
    const edit = iconEdit;
    setIconEdit(null); setIconSearch('');
    if (!edit || icon === edit.icon) return;
    if (edit.kind === 'node') updateNodeFields(edit.id, { icon });
    else if (edit.kind === 'step') updateStepOverride(edit.id, { icon });
    else void updateComponent(edit.id, { icon });
  };
  const nameEditKeyDown = (event) => {
    event.stopPropagation();
    if (event.key === 'Enter') { event.preventDefault(); commitNameEdit(); }
    if (event.key === 'Escape') { event.preventDefault(); setNameEdit(null); }
  };
  const startNodeEdit = (event, node) => {
    if (node.id === 'property') return;
    const hit = document.elementFromPoint?.(event.clientX, event.clientY);
    const element = hit && event.currentTarget.contains(hit) ? hit : event.target;
    // Anywhere in the node body below the header edits the description, not only over its text.
    const field = element?.closest?.('[data-node-edit]')?.dataset.nodeEdit
      || (element?.closest?.('.rehab-node-header,.rehab-rich-editor,[contenteditable="true"],button,input,textarea,label,.rehab-node-port,.rehab-node-resize') ? null : 'detail');
    if (!field) return;
    event.stopPropagation();
    if (field === 'label') setNameEdit({ kind: 'node', id: node.id, value: node.label, original: node.label });
    else if (field === 'detail') {
      const canvas = event.currentTarget.closest('.rehab-canvas');
      const detail = event.currentTarget.querySelector('.rehab-node-detail');
      setNameEdit({ kind: 'node', field: 'detail', id: node.id, initialViewport: {
        canvasScrollTop: canvas?.scrollTop || 0,
        canvasScrollLeft: canvas?.scrollLeft || 0,
        detailScrollTop: detail?.scrollTop || 0,
      } });
    }
    else { setIconSearch(''); setIconEdit({ kind: 'node', id: node.id, icon: displayIcon(node.icon), name: node.label }); }
  };
  const stepInstance = (type, label, icon, x, y) => {
    const bundleFactory = type === 'inspector-documentation' ? createInspectorDocumentationBundle
      : type === 'contractor-bids' ? createContractorBundle : null;
    if (!bundleFactory) return { nodes: [{ id: uniqueId(type), type, label, detail: 'Workflow step', icon, x, y }], edges: [] };
    const bundle = bundleFactory(x, y);
    return { nodes: bundle.nodes.map((node) => node.type === type ? { ...node, label, icon } : node), edges: bundle.edges };
  };
  const startNodeResize = (event, node, corner) => {
    if (event.button !== 0) return;
    event.stopPropagation(); event.preventDefault();
    const element = event.currentTarget.closest('[data-workflow-node]');
    nodeResizeRef.current = { id: node.id, corner, pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY,
      before: graphSnapshot(), start: { x: node.x, y: node.y,
        width: element?.offsetWidth || node.width || NODE_WIDTH, height: element?.offsetHeight || node.height || NODE_HEIGHT } };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const moveNodeResize = (event) => {
    const active = nodeResizeRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    event.stopPropagation();
    const box = resizeNodeBox(active.start, active.corner,
      (event.clientX - active.clientX) / zoom, (event.clientY - active.clientY) / zoom);
    const next = nodesRef.current.map((candidate) => candidate.id === active.id ? { ...candidate, ...box,
      x: Math.max(MIN_CANVAS_COORDINATE, box.x), y: Math.max(MIN_CANVAS_COORDINATE, box.y) } : candidate);
    nodesRef.current = next; setNodes(next);
  };
  const finishNodeResize = (event) => {
    const active = nodeResizeRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    event.stopPropagation();
    nodeResizeRef.current = null;
    rememberGraphChange(active.before);
    void persistWorkflow();
  };
  // Canvas and node pointer handlers call preventDefault, which stops the browser from moving
  // focus; blur an open canvas field explicitly so clicking away still saves it.
  const blurCanvasField = (event) => {
    const active = document.activeElement;
    if (active && active !== event.target && !active.contains(event.target)
        && canvasRef.current?.contains(active) && active.matches('input,textarea,[contenteditable="true"]')) active.blur();
  };
  const startNodeDrag = (event, node) => {
    blurCanvasField(event);
    // Links are excluded too: capturing the pointer on the node would retarget the click away from
    // the <a>, so saved links (and View product) would never open their new tab.
    if (event.button !== 0 || event.target.closest('a[href],button,input,textarea,select,label')) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const bounds = canvas.getBoundingClientRect();
    const point = canvasPointFromPointer({ clientX: event.clientX, clientY: event.clientY, bounds,
      scrollLeft: canvas.scrollLeft, scrollTop: canvas.scrollTop, zoom });
    let dragIds = [node.id];
    if (interactionMode === 'select' && node.id !== 'property') {
      if (event.metaKey || event.ctrlKey) {
        const nextSelection = selectedNodeIds.includes(node.id)
          ? selectedNodeIds.filter((selectedId) => selectedId !== node.id)
          : [...selectedNodeIds, node.id];
        setSelectedNodeIds(nextSelection);
        if (!nextSelection.includes(node.id)) return;
        dragIds = nextSelection;
      } else if (selectedNodeIds.includes(node.id)) dragIds = selectedNodeIds;
      else setSelectedNodeIds(dragIds);
    }
    // A parent's descendants belong to it: Shift-drag extraction treats the whole hierarchy as one
    // unit so parent-to-child lines are never bridged to outside nodes, and children hidden inside a
    // collapsed parent travel with it.
    const groupIds = [...workflowDescendantIds(nodesRef.current, dragIds)];
    const hiddenIds = workflowDescendantIds(nodesRef.current, nodesRef.current
      .filter((candidate) => dragIds.includes(candidate.id) && isGroupCollapsed(candidate)).map((candidate) => candidate.id));
    const positions = new Map(allNodes.filter((candidate) => dragIds.includes(candidate.id) || hiddenIds.has(candidate.id))
      .map((candidate) => [candidate.id, { x: candidate.x, y: candidate.y }]));
    nodeDragRef.current = { id: node.id, ids: dragIds, groupIds, pointerId: event.pointerId, startPoint: point,
      before: graphSnapshot(),
      positions, shiftExtract: event.shiftKey, moved: false, extraction: null };
    setReconnectTargetEdgeId(null);
    event.currentTarget.setPointerCapture?.(event.pointerId); setDraggingNodeId(node.id); event.preventDefault();
  };
  const moveNode = (event) => {
    const pan = canvasPanRef.current;
    const canvas = canvasRef.current;
    if (pan && canvas && pan.pointerId === event.pointerId) {
      canvas.scrollLeft = pan.scrollLeft - (event.clientX - pan.clientX);
      canvas.scrollTop = pan.scrollTop - (event.clientY - pan.clientY);
      return;
    }
    const selecting = selectionRef.current;
    if (selecting && canvas && selecting.pointerId === event.pointerId) {
      const bounds = canvas.getBoundingClientRect();
      const point = canvasPointFromPointer({ clientX: event.clientX, clientY: event.clientY, bounds,
        scrollLeft: canvas.scrollLeft, scrollTop: canvas.scrollTop, zoom });
      const rectangle = { x1: selecting.start.x, y1: selecting.start.y, x2: point.x, y2: point.y };
      setSelectionBox(rectangle);
      const hits = nodesInSelectionRect(visibleNodes.map((candidate) => ({
        ...withRenderedSize(candidate), x: candidate.x + CANVAS_ORIGIN, y: candidate.y + CANVAS_ORIGIN,
      })), rectangle);
      setSelectedNodeIds(selecting.additive ? [...new Set([...selecting.initial, ...hits])] : hits);
      return;
    }
    const active = nodeDragRef.current;
    if (active && canvas && active.pointerId === event.pointerId) {
      const bounds = canvas.getBoundingClientRect();
      const point = canvasPointFromPointer({ clientX: event.clientX, clientY: event.clientY, bounds,
        scrollLeft: canvas.scrollLeft, scrollTop: canvas.scrollTop, zoom });
      const deltaX = point.x - active.startPoint.x;
      const deltaY = point.y - active.startPoint.y;
      active.shiftExtract = active.shiftExtract || event.shiftKey;
      active.moved = active.moved || Math.hypot(deltaX, deltaY) > 4;
      if (active.shiftExtract && active.moved && !active.extraction && !active.ids.includes('property')) {
        active.extraction = extractWorkflowSelection(edgesRef.current, active.groupIds);
        edgesRef.current = active.extraction.edges; setEdges(active.extraction.edges);
      }
      if (active.moved) {
        const next = nodesRef.current.map((candidate) => {
          const initial = active.positions.get(candidate.id);
          return initial ? { ...candidate,
            x: Math.max(MIN_CANVAS_COORDINATE, initial.x + deltaX),
            y: Math.max(MIN_CANVAS_COORDINATE, initial.y + deltaY) } : candidate;
        });
        nodesRef.current = next; setNodes(next);
        if (active.extraction) {
          const selected = new Set(active.ids);
          const bottomNode = next.filter((candidate) => selected.has(candidate.id))
            .sort((left, right) => right.y - left.y)[0];
          const nodeMap = new Map(next.map((candidate) => [candidate.id, {
            ...withRenderedSize(candidate), x: candidate.x + CANVAS_ORIGIN, y: candidate.y + CANVAS_ORIGIN,
          }]));
          if (propertyNode) nodeMap.set('property', { ...propertyNode,
            x: propertyPositionRef.current.x + CANVAS_ORIGIN, y: propertyPositionRef.current.y + CANVAS_ORIGIN });
          const positionedBottom = bottomNode && nodeMap.get(bottomNode.id);
          const group = new Set(active.groupIds);
          const candidateEdges = edgesRef.current.filter((edge) => !group.has(edge.source) && !group.has(edge.target));
          const targetEdgeId = positionedBottom ? nearestWorkflowEdge(candidateEdges, nodeMap, [
            portPoint(positionedBottom, 'left'), portPoint(positionedBottom, 'right'),
          ], 44) : null;
          active.targetEdgeId = targetEdgeId;
          active.attachmentNodeId = targetEdgeId ? bottomNode.id : null;
          setReconnectTargetEdgeId(targetEdgeId);
        }
        const initialProperty = active.positions.get('property');
        if (initialProperty) {
          const nextProperty = { x: Math.max(MIN_CANVAS_COORDINATE, initialProperty.x + deltaX),
            y: Math.max(MIN_CANVAS_COORDINATE, initialProperty.y + deltaY) };
          propertyPositionRef.current = nextProperty; setPropertyPosition(nextProperty);
        }
      }
    }
    if (connectionDragRef.current && canvas && connectionDragRef.current.pointerId === event.pointerId) {
      const bounds = canvas.getBoundingClientRect();
      const point = canvasPointFromPointer({ clientX: event.clientX, clientY: event.clientY, bounds,
        scrollLeft: canvas.scrollLeft, scrollTop: canvas.scrollTop, zoom });
      const nextDraft = { ...connectionDragRef.current, moved: connectionDragRef.current.moved
        || Math.hypot(event.clientX - connectionDragRef.current.startX, event.clientY - connectionDragRef.current.startY) > 4, point };
      connectionDragRef.current = nextDraft;
      setConnectionDraft(nextDraft);
    }
  };
  const finishNodeDrag = (event) => {
    const active = nodeDragRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    if (active.extraction) {
      const hit = document.elementFromPoint?.(event.clientX, event.clientY);
      const targetEdgeId = hit?.closest?.('[data-workflow-edge]')?.dataset.edgeId || active.targetEdgeId;
      const anchorNodeId = hit?.closest?.('[data-workflow-node]')?.dataset.nodeId;
      if (targetEdgeId) {
        const attachmentNodeId = active.attachmentNodeId || nodesRef.current
          .filter((node) => active.ids.includes(node.id)).sort((left, right) => right.y - left.y)[0]?.id;
        const inserted = insertWorkflowSelectionIntoEdge(
          edgesRef.current, active.ids, targetEdgeId, active.extraction, uniqueId, attachmentNodeId,
        );
        edgesRef.current = inserted; setEdges(inserted);
      } else if (anchorNodeId && !active.groupIds.includes(anchorNodeId)) {
        const inserted = insertWorkflowSelection(edgesRef.current, active.ids, anchorNodeId, active.extraction);
        edgesRef.current = inserted; setEdges(inserted);
      }
    }
    if (active.moved) rememberGraphChange(active.before);
    nodeDragRef.current = null; setDraggingNodeId(null); setReconnectTargetEdgeId(null); void persistWorkflow();
  };
  const startCanvasInteraction = (event) => {
    blurCanvasField(event);
    if (event.button !== 0 || event.target.closest('button,input,textarea,select,label,.rehab-node,.rehab-connection-hit')) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (interactionMode === 'select') {
      const bounds = canvas.getBoundingClientRect();
      const start = canvasPointFromPointer({ clientX: event.clientX, clientY: event.clientY, bounds,
        scrollLeft: canvas.scrollLeft, scrollTop: canvas.scrollTop, zoom });
      const additive = event.metaKey || event.ctrlKey;
      selectionRef.current = { pointerId: event.pointerId, start, additive, initial: additive ? selectedNodeIds : [] };
      setSelectionBox({ x1: start.x, y1: start.y, x2: start.x, y2: start.y });
      if (!additive) setSelectedNodeIds([]);
      canvas.setPointerCapture?.(event.pointerId); event.preventDefault(); return;
    }
    canvasPanRef.current = { pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY,
      scrollLeft: canvas.scrollLeft, scrollTop: canvas.scrollTop };
    canvas.classList.add('rehab-canvas--panning');
    canvas.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };
  const finishCanvasPan = (event) => {
    if (!canvasPanRef.current || canvasPanRef.current.pointerId !== event.pointerId) return false;
    canvasPanRef.current = null;
    canvasRef.current?.classList.remove('rehab-canvas--panning');
    return true;
  };
  const finishSelection = (event) => {
    if (!selectionRef.current || selectionRef.current.pointerId !== event.pointerId) return false;
    selectionRef.current = null; setSelectionBox(null); return true;
  };
  const toggleInteractionMode = (event) => {
    if (event.target.closest('button,input,textarea,select,label,.rehab-node,.rehab-connection-hit')) return;
    setInteractionMode((mode) => mode === 'pan' ? 'select' : 'pan');
    setSelectedNodeIds([]); setSelectionBox(null);
  };
  const removeNode = (nodeId) => {
    const next = removeWorkflowNodeTrees(nodesRef.current, edgesRef.current, [nodeId]);
    commitGraph(next.nodes, next.edges);
  };
  const removeEdge = (edgeId) => commitGraph(
    nodesRef.current,
    edgesRef.current.filter((edge) => edge.id !== edgeId),
  );
  useEffect(() => {
    const deleteSelection = (event) => {
      if (interactionMode !== 'select' || !['Delete', 'Backspace'].includes(event.key)
          || event.target.closest?.('input,textarea,select,[contenteditable="true"]')) return;
      const selected = new Set(selectedNodeIds);
      if (!selected.size) return;
      event.preventDefault();
      const next = removeWorkflowNodeTrees(nodesRef.current, edgesRef.current, selected);
      commitGraph(next.nodes, next.edges);
      setSelectedNodeIds([]);
    };
    window.addEventListener('keydown', deleteSelection);
    return () => window.removeEventListener('keydown', deleteSelection);
  });
  useEffect(() => {
    const isTextEntry = (target) => Boolean(target.closest?.('input,textarea,select,[contenteditable="true"]'));
    const handleQuickPickerKeyDown = (event) => {
      if (event.code === 'Space' && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        if (isTextEntry(event.target) || document.querySelector('.p-dialog')) return;
        quickPickerSpaceHeldRef.current = true;
        event.preventDefault();
        return;
      }
      if (event.key !== 'Meta' || !quickPickerSpaceHeldRef.current || isTextEntry(event.target)
          || document.querySelector('.p-dialog')) return;
      event.preventDefault(); quickPickerSpaceHeldRef.current = false;
      setQuickPickerSearch(''); setQuickPickerSelection('notes'); setQuickPickerOpen(true);
    };
    const releaseSpace = (event) => { if (event.code === 'Space') quickPickerSpaceHeldRef.current = false; };
    const clearSpace = () => { quickPickerSpaceHeldRef.current = false; };
    window.addEventListener('keydown', handleQuickPickerKeyDown);
    window.addEventListener('keyup', releaseSpace);
    window.addEventListener('blur', clearSpace);
    return () => {
      window.removeEventListener('keydown', handleQuickPickerKeyDown);
      window.removeEventListener('keyup', releaseSpace);
      window.removeEventListener('blur', clearSpace);
    };
  }, []);
  const copySelection = () => {
    const ids = workflowDescendantIds(nodesRef.current, selectedNodeIds.filter((nodeId) => nodeId !== 'property'));
    const copied = nodesRef.current.filter((node) => ids.has(node.id));
    if (!copied.length) return false;
    const left = Math.min(...copied.map((node) => node.x));
    const right = Math.max(...copied.map((node) => node.x + nodeWidth(withRenderedSize(node))));
    workflowClipboardRef.current = { snapshot: createWorkflowComponentSnapshot(nodesRef.current, edgesRef.current, [...ids]),
      x: left, y: Math.min(...copied.map((node) => node.y)), width: right - left, pastes: 0 };
    return true;
  };
  const pasteSelection = () => {
    const clipboard = workflowClipboardRef.current;
    if (!clipboard) return false;
    clipboard.pastes += 1;
    const instance = instantiateWorkflowComponent(clipboard.snapshot,
      clipboard.x + clipboard.pastes * (clipboard.width + 60), clipboard.y);
    commitGraph([...nodesRef.current, ...instance.nodes], [...edgesRef.current, ...instance.edges]);
    setSelectedNodeIds(instance.nodes.map((node) => node.id));
    return true;
  };
  useEffect(() => {
    const handleClipboardShortcut = (event) => {
      const key = event.key.toLowerCase();
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey || !['c', 'v'].includes(key)
          || event.target.closest?.('input,textarea,select,[contenteditable="true"]')) return;
      if (key === 'c' ? copySelection() : pasteSelection()) event.preventDefault();
    };
    window.addEventListener('keydown', handleClipboardShortcut);
    return () => window.removeEventListener('keydown', handleClipboardShortcut);
  });
  useEffect(() => {
    const handleHistoryShortcut = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.key.toLowerCase() !== 'z'
          || event.target.closest?.('input,textarea,select,[contenteditable="true"]')) return;
      event.preventDefault();
      if (event.shiftKey) redoGraph();
      else undoGraph();
    };
    window.addEventListener('keydown', handleHistoryShortcut);
    return () => window.removeEventListener('keydown', handleHistoryShortcut);
  });
  const updateAmount = (nodeId, rawValue) => {
    const amount = rawValue === '' ? null : Number(rawValue);
    const next = nodesRef.current.map((node) => node.id === nodeId ? { ...node, amount } : node);
    nodesRef.current = next; setNodes(next);
  };
  const finishAmountEdit = () => {
    if (amountEditSnapshotRef.current) rememberGraphChange(amountEditSnapshotRef.current);
    amountEditSnapshotRef.current = null;
    void persistWorkflow();
  };
  const toggleNodeGroup = (nodeId) => {
    const next = nodesRef.current.map((node) => node.id === nodeId
      ? { ...node, collapsed: !isGroupCollapsed(node) }
      : node);
    commitGraph(next, edgesRef.current);
  };
  const toggleConnectedBranches = (node) => {
    if (!normalizeCollapseSides(node.collapse_sides)?.length) return;
    if (node.id === 'property') {
      const previous = graphSnapshot();
      const nextProperty = { ...propertyPositionRef.current, branches_collapsed: !node.branches_collapsed };
      propertyPositionRef.current = nextProperty; setPropertyPosition(nextProperty);
      rememberGraphChange(previous);
      void persistWorkflow(nodesRef.current, nextProperty, edgesRef.current);
    } else {
      commitGraph(nodesRef.current.map((candidate) => candidate.id === node.id
        ? { ...candidate, branches_collapsed: !candidate.branches_collapsed } : candidate), edgesRef.current);
    }
  };
  const startConnection = (event, nodeId, handle) => {
    if (event.button !== 0) return;
    event.stopPropagation(); event.preventDefault();
    const armed = connectionDragRef.current?.armed ? connectionDragRef.current : null;
    if (armed) {
      if (armed.fixed.nodeId === nodeId && armed.fixed.handle === handle) {
        connectionDragRef.current = null; setConnectionDraft(null); void persistWorkflow();
        return;
      }
      const connectedEdges = connectWorkflowPorts(edgesRef.current, armed.fixed, { nodeId, handle });
      connectionDragRef.current = null; setConnectionDraft(null);
      suppressPortClickRef.current = true;
      commitGraph(nodesRef.current, connectedEdges);
      return;
    }
    const fixed = { nodeId, handle, role: 'source' };
    connectionDragRef.current = { pointerId: event.pointerId, fixed, startX: event.clientX, startY: event.clientY, moved: false };
    setConnectionDraft(connectionDragRef.current);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const finishConnection = (event) => {
    const active = connectionDragRef.current;
    if (!active || active.pointerId !== event.pointerId) return false;
    const hitElement = document.elementFromPoint?.(event.clientX, event.clientY);
    let targetElement = hitElement?.closest?.('[data-workflow-port]');
    if (!targetElement) {
      const targetNode = hitElement?.closest?.('[data-workflow-node]');
      if (targetNode) targetElement = nearestWorkflowPort(
        Array.from(targetNode.querySelectorAll('[data-workflow-port]')), event.clientX, event.clientY,
      );
    }
    if (!targetElement) targetElement = nearestWorkflowPort(
      Array.from(document.querySelectorAll('[data-workflow-port]')), event.clientX, event.clientY, 36,
    );
    const drop = targetElement ? { nodeId: targetElement.dataset.nodeId, handle: targetElement.dataset.handle,
      role: active.fixed.role === 'source' ? 'target' : 'source' } : null;
    if (!active.moved && (!drop || (drop.nodeId === active.fixed.nodeId && drop.handle === active.fixed.handle))) {
      const armed = { fixed: active.fixed, armed: true };
      connectionDragRef.current = armed; setConnectionDraft(armed);
      void persistWorkflow(nodesRef.current, propertyPositionRef.current, edgesRef.current);
      return true;
    }
    let nextEdges = edgesRef.current;
    if (drop && drop.nodeId !== active.fixed.nodeId) {
      nextEdges = connectWorkflowPorts(nextEdges, active.fixed, drop);
    }
    connectionDragRef.current = null; setConnectionDraft(null);
    suppressPortClickRef.current = true;
    if (nextEdges !== edgesRef.current) commitGraph(nodesRef.current, nextEdges);
    return true;
  };
  const clickConnectionPort = (event, nodeId, handle) => {
    event.stopPropagation();
    if (suppressPortClickRef.current) { suppressPortClickRef.current = false; return; }
    const active = connectionDragRef.current;
    if (active?.armed) {
      if (active.fixed.nodeId === nodeId && active.fixed.handle === handle) return;
      const nextEdges = connectWorkflowPorts(edgesRef.current, active.fixed, { nodeId, handle });
      connectionDragRef.current = null; setConnectionDraft(null);
      commitGraph(nodesRef.current, nextEdges);
      return;
    }
    if (active?.fixed) {
      const armed = { fixed: active.fixed, armed: true };
      connectionDragRef.current = armed; setConnectionDraft(armed);
      return;
    }
    const fixed = { nodeId, handle, role: 'source' };
    const armed = { fixed, armed: true };
    connectionDragRef.current = armed; setConnectionDraft(armed);
  };
  const handleDrop = (event) => {
    event.preventDefault();
    const bounds = event.currentTarget.getBoundingClientRect();
    const point = canvasPointFromPointer({ clientX: event.clientX, clientY: event.clientY, bounds,
      scrollLeft: event.currentTarget.scrollLeft, scrollTop: event.currentTarget.scrollTop, zoom });
    const position = {
      x: Math.max(MIN_CANVAS_COORDINATE, point.x - CANVAS_ORIGIN - 75),
      y: Math.max(MIN_CANVAS_COORDINATE, point.y - CANVAS_ORIGIN - 28),
    };
    const definition = NODE_TYPES.find(([key]) => key === event.dataTransfer.getData('application/x-rehab-node'));
    const override = definition ? stepOverrides[definition[0]] || {} : null;
    const componentId = event.dataTransfer.getData('application/x-rehab-component');
    const component = definition ? null : components.find((candidate) => candidate.component_id === componentId);
    const instance = definition
      ? stepInstance(definition[0], override.label || definition[1], override.icon || definition[2], position.x, position.y)
      : component ? instantiateWorkflowComponent(component, position.x, position.y) : null;
    if (!instance) return;
    // Steps and components land the same way: one undoable graph change, with the dropped nodes selected.
    commitGraph([...nodesRef.current, ...instance.nodes], [...edgesRef.current, ...instance.edges]);
    setSelectedNodeIds(instance.nodes.map((node) => node.id));
  };
  const addQuickComponent = () => {
    const definition = QUICK_COMPONENTS.find((component) => component.key === quickPickerSelection);
    const canvas = canvasRef.current;
    if (!definition || !canvas) return;
    if (definition.key === 'lender-comparison') {
      setQuickPickerOpen(false); setQuickPickerSearch(''); setLenderComparisonOpen(true); return;
    }
    const bounds = canvas.getBoundingClientRect();
    const point = canvasPointFromPointer({ clientX: bounds.left + canvas.clientWidth / 2,
      clientY: bounds.top + canvas.clientHeight / 2, bounds, scrollLeft: canvas.scrollLeft,
      scrollTop: canvas.scrollTop, zoom });
    const node = {
      id: uniqueId(definition.key), type: definition.key, label: definition.label, detail: '', icon: definition.icon,
      x: Math.max(MIN_CANVAS_COORDINATE, Math.round(point.x - CANVAS_ORIGIN - definition.width / 2)),
      y: Math.max(MIN_CANVAS_COORDINATE, Math.round(point.y - CANVAS_ORIGIN - definition.height / 2)),
      width: definition.width, height: definition.height,
      ...(definition.key === 'notes' ? { appearance: DEFAULT_NOTE_APPEARANCE } : {}),
    };
    commitGraph([...nodesRef.current, node], edgesRef.current);
    setSelectedNodeIds([node.id]); setQuickPickerOpen(false); setQuickPickerSearch('');
  };
  const addSelectedLender = () => {
    const lender = rankedLenders.find((candidate) => candidate.id === selectedLenderId);
    const canvas = canvasRef.current;
    if (!lender || !canvas) return;
    const bounds = canvas.getBoundingClientRect();
    const point = canvasPointFromPointer({ clientX: bounds.left + canvas.clientWidth / 2,
      clientY: bounds.top + canvas.clientHeight / 2, bounds, scrollLeft: canvas.scrollLeft,
      scrollTop: canvas.scrollTop, zoom });
    const node = { id: uniqueId('lender'), type: 'lender', label: lender.name, icon: 'pi-wallet',
      detail: lender.reason, lender_offer: lender,
      x: Math.max(MIN_CANVAS_COORDINATE, Math.round(point.x - CANVAS_ORIGIN - 150)),
      y: Math.max(MIN_CANVAS_COORDINATE, Math.round(point.y - CANVAS_ORIGIN - 95)), width: 300, height: 190 };
    commitGraph([...nodesRef.current, node], edgesRef.current);
    setSelectedNodeIds([node.id]); setLenderComparisonOpen(false);
  };
  const toggleQuickComponentPin = (componentKey) => {
    const previous = pinnedQuickComponents;
    const next = previous.includes(componentKey)
      ? previous.filter((key) => key !== componentKey) : [...previous, componentKey];
    setPinnedQuickComponents(next); setQuickPickerError('');
    Promise.resolve(apiJson(PINNED_COMPONENTS_PREFERENCE, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: next }),
    })).catch((saveError) => {
      setPinnedQuickComponents(previous);
      setQuickPickerError(saveError?.message || 'Could not save pinned components.');
    });
  };
  const renderQuickComponentRow = (component, pinned) => <div key={component.key} className="rehab-quick-picker__row">
    <button type="button" role="option" aria-selected={quickPickerSelection === component.key} className={quickPickerSelection === component.key ? 'is-selected' : ''} onClick={() => setQuickPickerSelection(component.key)} onDoubleClick={addQuickComponent}>
      <span><i className={`pi ${component.icon}`} aria-hidden="true" /></span><strong>{component.label}</strong><small>{component.description}</small>
    </button>
    <button type="button" className={`rehab-quick-picker__pin${pinned ? ' is-pinned' : ''}`} aria-label={`${pinned ? 'Unpin' : 'Pin'} ${component.label}`} aria-pressed={pinned} title={`${pinned ? 'Unpin' : 'Pin'} ${component.label}`} onClick={() => toggleQuickComponentPin(component.key)}><i className={`pi ${pinned ? 'pi-bookmark-fill' : 'pi-bookmark'}`} aria-hidden="true" /></button>
  </div>;
  const openNoteStyle = (event, node) => {
    event.preventDefault(); event.stopPropagation();
    setNoteStyleEdit({ id: node.id, draft: normalizeNoteAppearance(node.appearance) || DEFAULT_NOTE_APPEARANCE });
  };
  const setNoteStyleColor = (key, value) => {
    const normalized = String(value || '').trim();
    setNoteStyleEdit((current) => current ? { ...current, draft: { ...current.draft, [key]: normalized } } : current);
  };
  const saveNoteStyle = () => {
    const normalized = normalizeNoteAppearance(noteStyleEdit?.draft);
    if (!noteStyleEdit || !normalized) return;
    updateNodeFields(noteStyleEdit.id, { appearance: normalized }); setNoteStyleEdit(null);
  };
  const openNodeStyle = (event, node, targetIds = null) => {
    event?.preventDefault(); event?.stopPropagation();
    setNodeStyleEdit({ id: node.id, draft: normalizeNodeAppearance(node.appearance) || DEFAULT_NODE_APPEARANCE,
      scope: targetIds?.length ? 'selected' : 'one', targetIds: targetIds || null });
    setSelectionMenu(null);
  };
  const setNodeStyleColor = (key, value) => {
    const normalized = String(value || '').trim();
    setNodeStyleEdit((current) => current ? { ...current, draft: { ...current.draft, [key]: normalized } } : current);
  };
  const saveNodeStyle = () => {
    const normalized = normalizeNodeAppearance(nodeStyleEdit?.draft);
    if (!nodeStyleEdit || !normalized) return;
    let targets;
    if (nodeStyleEdit.scope === 'selected') targets = new Set(nodeStyleEdit.targetIds || []);
    else if (nodeStyleEdit.scope === 'many') targets = new Set(connectedWorkflowNodeIds(edgesRef.current, nodeStyleEdit.id));
    else targets = new Set([nodeStyleEdit.id]);
    if (targets.has('property')) {
      const previous = graphSnapshot();
      const nextProperty = { ...propertyPositionRef.current, appearance: normalized };
      propertyPositionRef.current = nextProperty; setPropertyPosition(nextProperty);
      rememberGraphChange(previous);
      void persistWorkflow(nodesRef.current, nextProperty, edgesRef.current);
    }
    const next = nodesRef.current.map((node) => {
      if (!targets.has(node.id)) return node;
      if (node.type === 'notes') return { ...node, appearance: { ...(normalizeNoteAppearance(node.appearance) || DEFAULT_NOTE_APPEARANCE), ...normalized } };
      return { ...node, appearance: normalized };
    });
    if ([...targets].some((nodeId) => nodeId !== 'property')) commitGraph(next, edgesRef.current);
    setNodeStyleEdit(null);
  };
  const minimumPortLayout = (nodeId) => {
    const minimums = { top: 0, right: 0, bottom: 0, left: 0 };
    edgesRef.current.forEach((edge) => {
      [['source', 'source_handle'], ['target', 'target_handle']].forEach(([nodeKey, handleKey]) => {
        if (edge[nodeKey] !== nodeId) return;
        const handle = edge[handleKey];
        const side = portHandleSide(handle);
        minimums[side] = Math.max(minimums[side], portHandleIndex(handle) + 1);
      });
    });
    return minimums;
  };
  const openPortLayout = (event, node) => {
    event.preventDefault(); event.stopPropagation();
    setNodeActionMenuId(null);
    setPortLayoutEdit({ id: node.id, label: node.label, draft: normalizePortLayout(node.port_layout, node),
      collapseEnabled: Boolean(normalizeCollapseSides(node.collapse_sides)?.length),
      collapseSides: normalizeCollapseSides(node.collapse_sides) || [], branchesCollapsed: node.branches_collapsed === true,
      minimums: minimumPortLayout(node.id) });
  };
  const changePortCount = (side, change) => setPortLayoutEdit((current) => {
    if (!current) return current;
    const minimum = current.minimums[side] || 0;
    const count = Math.max(minimum, Math.min(8, current.draft[side] + change));
    return { ...current, draft: { ...current.draft, [side]: count } };
  });
  const setAllPortCounts = (count) => setPortLayoutEdit((current) => current ? {
    ...current, draft: Object.fromEntries(PORT_SIDES.map((side) => [side, Math.max(current.minimums[side] || 0, count)])),
  } : current);
  const toggleCollapseSide = (side) => setPortLayoutEdit((current) => {
    if (!current) return current;
    const collapseSides = current.collapseSides.includes(side)
      ? current.collapseSides.filter((candidate) => candidate !== side) : [...current.collapseSides, side];
    return { ...current, collapseSides: PORT_SIDES.filter((candidate) => collapseSides.includes(candidate)) };
  });
  const savePortLayout = () => {
    const normalized = normalizePortLayout(portLayoutEdit?.draft);
    const collapseSides = normalizeCollapseSides(portLayoutEdit?.collapseEnabled ? portLayoutEdit.collapseSides : []);
    if (!portLayoutEdit || !normalized || !collapseSides || (portLayoutEdit.collapseEnabled && !collapseSides.length)) return;
    const collapseSettings = { collapse_sides: collapseSides,
      branches_collapsed: collapseSides.length ? portLayoutEdit.branchesCollapsed : false };
    if (portLayoutEdit.id === 'property') {
      const previous = graphSnapshot();
      const nextProperty = { ...propertyPositionRef.current, port_layout: normalized, ...collapseSettings };
      propertyPositionRef.current = nextProperty; setPropertyPosition(nextProperty);
      rememberGraphChange(previous);
      void persistWorkflow(nodesRef.current, nextProperty, edgesRef.current);
    } else {
      commitGraph(nodesRef.current.map((node) => node.id === portLayoutEdit.id
        ? { ...node, port_layout: normalized, ...collapseSettings } : node), edgesRef.current);
    }
    setPortLayoutEdit(null);
  };
  const deleteSelectedNodes = () => {
    const selected = new Set(selectedNodeIds.filter((nodeId) => nodeId !== 'property'));
    if (!selected.size) return;
    const next = removeWorkflowNodeTrees(nodesRef.current, edgesRef.current, selected);
    commitGraph(next.nodes, next.edges); setSelectedNodeIds([]); setSelectionMenu(null);
  };
  useEffect(() => {
    if (!selectionMenu) return undefined;
    const close = (event) => { if (event.key === undefined || event.key === 'Escape') setSelectionMenu(null); };
    window.addEventListener('pointerdown', close); window.addEventListener('keydown', close);
    return () => { window.removeEventListener('pointerdown', close); window.removeEventListener('keydown', close); };
  }, [selectionMenu]);
  useEffect(() => {
    if (!nodeActionMenuId) return undefined;
    const close = (event) => {
      if (event.key === undefined || event.key === 'Escape') setNodeActionMenuId(null);
    };
    window.addEventListener('pointerdown', close); window.addEventListener('keydown', close);
    return () => { window.removeEventListener('pointerdown', close); window.removeEventListener('keydown', close); };
  }, [nodeActionMenuId]);
  const changeZoom = (requestedZoom, clientPoint) => {
    const canvas = canvasRef.current;
    const nextZoom = clampWorkflowZoom(requestedZoom);
    if (!canvas || nextZoom === zoom) return;
    const bounds = canvas.getBoundingClientRect();
    const anchorX = clientPoint?.x ?? bounds.left + canvas.clientWidth / 2;
    const anchorY = clientPoint?.y ?? bounds.top + canvas.clientHeight / 2;
    const logicalPoint = canvasPointFromPointer({ clientX: anchorX, clientY: anchorY, bounds,
      scrollLeft: canvas.scrollLeft, scrollTop: canvas.scrollTop, zoom });
    const viewportX = anchorX - bounds.left;
    const viewportY = anchorY - bounds.top;
    setZoom(nextZoom);
    window.requestAnimationFrame(() => {
      canvas.scrollLeft = logicalPoint.x * nextZoom - viewportX;
      canvas.scrollTop = logicalPoint.y * nextZoom - viewportY;
    });
  };
  const handleWheelZoom = (event) => {
    event.preventDefault();
    if (event.ctrlKey || event.metaKey) {
      changeZoom(zoom + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP), { x: event.clientX, y: event.clientY });
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const horizontalDelta = event.deltaX || (event.shiftKey ? event.deltaY : 0);
    const verticalDelta = event.shiftKey ? 0 : event.deltaY;
    canvas.scrollLeft += horizontalDelta;
    canvas.scrollTop += verticalDelta;
  };

  if ((!property || workflowLoading) && !error) return <PageLoader label="Loading rehab workspace" />;
  // Same header row as the icon grid: the source tabs replace the ICON label, so no height is added.
  const renderPickerToggle = () => <div className="rehab-picker-toggle" role="tablist" aria-label="Component source">
    {[['icons', 'Icons'], ['people', 'People'], ['hardware', 'Hardware']].map(([mode, label]) => <button key={mode} type="button" role="tab"
      aria-selected={pickerMode === mode} className={`rehab-picker-toggle__tab${pickerMode === mode ? ' is-active' : ''}`}
      onClick={() => { setPickerMode(mode); if (mode === 'hardware' && hardwareStatus === 'idle') loadHardware(); }}>{label}</button>)}
  </div>;
  const renderPeoplePicker = () => <fieldset className="rehab-icon-picker rehab-people-picker"><legend className="rehab-icon-picker__legend">Choose person</legend>
    <div className="rehab-icon-picker__header">{renderPickerToggle()}<label className="rehab-icon-picker__search"><i className="pi pi-search" aria-hidden="true" /><input type="search" aria-label="Search people" value={peopleSearch} placeholder="Search" onChange={(event) => setPeopleSearch(event.target.value)} /></label><button type="button" className="rehab-people-picker__add" aria-label="Add person" title="Add person" onClick={() => { setCreatedPersonCredential(null); setPersonFormError(''); setPersonFormOpen(true); }}><i className="pi pi-plus" aria-hidden="true" /></button></div>
    <div className="rehab-people-picker__body">
      <div className="rehab-people-picker__filters" role="group" aria-label="Industry or specialty">
        {peopleFilters.map((filter) => <button key={filter.key} type="button" aria-pressed={peopleFilter === filter.key}
          className={`rehab-people-picker__filter${peopleFilter === filter.key ? ' is-selected' : ''}`} onClick={() => setPeopleFilter(filter.key)}>
          <i className={`pi ${filter.icon}`} aria-hidden="true" /><span>{filter.label}</span><small>{filter.count}</small></button>)}
      </div>
      <div className="rehab-people-picker__list" role="group" aria-label="People">
        {networkStatus === 'no-organization' ? <p className="rehab-people-picker__note">Join or create an investor organization to add people.</p>
          : networkStatus === 'forbidden' ? <p className="rehab-people-picker__note">You do not have access to the people directory.</p>
            : networkStatus === 'error' ? <p className="rehab-people-picker__note">Could not load people.</p>
              : networkStatus === 'ready' && !filteredPeople.length ? <p className="rehab-people-picker__note">{peopleSearch ? 'No matches.' : `No ${activePeopleFilter.key === 'all' ? 'people' : activePeopleFilter.label.toLowerCase()} yet.`}</p> : null}
        {filteredPeople.map((entry) => <React.Fragment key={`${entry.kind}:${entry.id}`}>
          {[null, ...(entry.contacts || [])].map((person) => {
            const selected = selectedPerson?.kind === entry.kind && selectedPerson?.id === entry.id
              && (selectedPerson?.contact_id || null) === (person?.contact_id || null);
            return <div key={person?.contact_id || 'company'} className="rehab-people-picker__entry"><button type="button" aria-pressed={selected}
              aria-label={person ? `Choose ${entry.name} with ${person.name}` : `Choose ${entry.name}`}
              className={`rehab-people-picker__row${person ? ' rehab-people-picker__row--contact' : ''}${selected ? ' is-selected' : ''}`}
              onClick={() => choosePerson(entry, person)}>
              <i className={`pi ${person ? 'pi-user' : CONTACT_KINDS[entry.kind]?.icon || 'pi-user'}`} aria-hidden="true" />
              <strong>{entry.organization_name || entry.name}</strong>
              {(person?.name || (entry.organization_name ? entry.name : '')) ? <small>{person?.name || entry.name}</small> : null}
            </button>{!person && entry.directory_source === 'person_account' ? <button type="button" className="rehab-people-picker__delete" aria-label={`Delete account for ${entry.name}`} title={`Delete ${entry.name}'s account`} onClick={() => { setPersonFormError(''); setPersonDeleteName(''); setPersonToDelete(entry); }}><i className="pi pi-trash" aria-hidden="true" /></button> : null}</div>;
          })}
        </React.Fragment>)}
      </div>
    </div>
  </fieldset>;
  const renderHardwarePicker = () => <fieldset className="rehab-icon-picker rehab-people-picker rehab-hardware-picker"><legend className="rehab-icon-picker__legend">Choose hardware</legend>
    <div className="rehab-icon-picker__header">{renderPickerToggle()}<label className="rehab-icon-picker__search"><i className="pi pi-search" aria-hidden="true" /><input type="search" aria-label="Search hardware" value={hardwareSearch} placeholder="Search" onChange={(event) => setHardwareSearch(event.target.value)} /></label><button type="button" className="rehab-people-picker__add" aria-label="Add hardware" title="Add hardware" onClick={() => { setHardwareFormError(''); setHardwareFormOpen(true); }}><i className="pi pi-plus" aria-hidden="true" /></button></div>
    <div className="rehab-people-picker__body">
      <div className="rehab-people-picker__filters" role="group" aria-label="Hardware categories">
        {hardwareFilters.map((filter) => <button key={filter.category_id} type="button" aria-pressed={hardwareFilter === filter.category_id}
          className={`rehab-people-picker__filter${hardwareFilter === filter.category_id ? ' is-selected' : ''}`} onClick={() => setHardwareFilter(filter.category_id)}>
          <WorkflowIcon icon={filter.icon_key || 'lu:LuCpu'} aria-hidden="true" /><span>{filter.name}</span><small>{filter.count}</small></button>)}
      </div>
      <div className="rehab-people-picker__list" role="group" aria-label="Hardware">
        {hardwareStatus === 'loading' ? <p className="rehab-people-picker__note">Loading hardware…</p>
          : hardwareStatus === 'no-organization' ? <p className="rehab-people-picker__note">Choose an investor organization to manage hardware.</p>
            : hardwareStatus === 'error' ? <p className="rehab-people-picker__note">Could not load hardware.</p>
              : hardwareStatus === 'ready' && !filteredHardware.length ? <p className="rehab-people-picker__note">{hardwareSearch ? 'No matches.' : 'No hardware in this category yet.'}</p> : null}
        {filteredHardware.map((device) => <button key={device.hardware_device_id} type="button" disabled={!device.selectable}
          aria-pressed={selectedHardware === device.hardware_device_id} aria-label={`Choose ${device.display_name}`}
          className={`rehab-people-picker__row rehab-hardware-picker__row${selectedHardware === device.hardware_device_id ? ' is-selected' : ''}${!device.selectable ? ' is-unavailable' : ''}`}
          title={!device.selectable ? `Installed at ${device.property_name || 'another property'}` : ''} onClick={() => chooseHardware(device)}>
          <WorkflowIcon icon={hardwareDirectory.categories.find((category) => category.category_id === device.category_id)?.icon_key || 'lu:LuCpu'} aria-hidden="true" />
          <strong>{device.display_name}</strong><small>{!device.selectable ? `Installed at ${device.property_name || 'another property'}` : [device.manufacturer, device.model].filter(Boolean).join(' · ')}</small>
        </button>)}
      </div>
    </div>
  </fieldset>;
  const renderIconPicker = (value, onPick, withToggle = false) => {
    const query = iconSearch.trim().toLowerCase();
    const lucideOptions = LUCIDE_ICON_NAMES.map(lucideIconValue);
    const allOptions = [...COMPONENT_ICONS, ...lucideOptions];
    const visibleIcons = (query ? allOptions.filter((icon) => iconLabel(icon).includes(query))
      : [...COMPONENT_ICONS, ...lucideOptions.slice(0, 240)]).slice(0, 420);
    return <fieldset className="rehab-icon-picker"><legend className="rehab-icon-picker__legend">Choose icon</legend>
      <div className="rehab-icon-picker__header">{withToggle ? renderPickerToggle() : <span>Icon</span>}<label className="rehab-icon-picker__search"><i className="pi pi-search" aria-hidden="true" /><input type="search" aria-label="Search icons" value={iconSearch} placeholder="Search" onChange={(event) => setIconSearch(event.target.value)} /></label></div>
      <div>{visibleIcons.map((icon) => <button key={icon} type="button" className={value === icon ? 'is-selected' : ''} aria-label={`Choose ${iconLabel(icon)} icon${icon.startsWith('lu:') ? ' (Lucide)' : ''}`} aria-pressed={value === icon} title={`${iconLabel(icon)}${icon.startsWith('lu:') ? ' · Lucide' : ''}`} onClick={() => onPick(icon)}><WorkflowIcon icon={icon} aria-hidden="true" /></button>)}</div>
    </fieldset>;
  };
  const renderPort = (node, handle, extraClass = '') => {
    const selected = connectionDraft?.armed && connectionDraft.fixed.nodeId === node.id && connectionDraft.fixed.handle === handle;
    const side = portHandleSide(handle);
    const count = normalizePortLayout(node.port_layout, node)?.[side] || 1;
    const position = `${portPositionPercent(handle, count)}%`;
    const style = side === 'top' || side === 'bottom' ? { left: position } : { top: position };
    return <button type="button"
    style={style} className={`rehab-node-port rehab-node-port--${side} ${extraClass}${selected ? ' rehab-node-port--selected' : ''}`} aria-label={`${node.label} ${handle} connection${selected ? ' selected' : ''}`}
    data-workflow-port data-node-id={node.id} data-handle={handle}
    onPointerDown={(event) => startConnection(event, node.id, handle)}
    onClick={(event) => clickConnectionPort(event, node.id, handle)} />;
  };
  const linkedPersonConfirmationMatches = Boolean(deleteLibraryItem?.person)
    && deleteConfirmationText.trim() === deleteLibraryItem.person.name;

  return <div className="rehab-page">
    <header className="rehab-header">
      <div className="rehab-header__title"><span className="rehab-header__eyebrow">B[R]RRRR (Buy, Remotization, Rehab, Rent, Refinance, Repeat)</span><h1>{property?.nickname || property?.address_line1 || 'Property rehab'}</h1>
      <blockquote className={`rehab-quote${quoteExpanded ? ' is-expanded' : ''}`} cite={SYSTEMS_QUOTE.url}>
        <div><p className="rehab-quote__text">{SYSTEMS_QUOTE.text}</p>
          {quoteExpanded ? <footer className="rehab-quote__cite">— {SYSTEMS_QUOTE.authors}, <a href={SYSTEMS_QUOTE.url} target="_blank" rel="noopener noreferrer">{SYSTEMS_QUOTE.source}</a></footer> : null}</div>
        <button type="button" className="rehab-quote__toggle" aria-label={quoteExpanded ? 'Collapse quote' : 'Expand quote'} title={quoteExpanded ? 'Collapse quote' : 'Expand quote'} aria-expanded={quoteExpanded} onClick={() => setQuoteExpanded((open) => !open)}><i className={`pi ${quoteExpanded ? 'pi-angle-up' : 'pi-angle-down'}`} aria-hidden="true" /></button>
      </blockquote>
      {/* <p>Drag from any dot and release on any side of another node, or click two dots. Click a line to remove it. {workflowSaving ? 'Saving…' : 'Changes save automatically.'}</p> */}
      </div>
      <AppActionGroup><AppButton variant="text" label="Properties" icon="pi pi-arrow-left" onClick={() => navigate('/properties')} />{lastRecipe ? <AppButton variant="outline" label={recipeSaving ? 'Saving…' : 'Save changes'} icon="pi pi-save" title={`Update ${lastRecipe.name}`} disabled={nodes.length === 0 || recipeSaving} onClick={() => saveRecipe(lastRecipe.name)} /> : null}<AppButton variant="outline" label={lastRecipe ? 'Save as new' : 'Save recipe'} icon="pi pi-plus" disabled={nodes.length === 0} onClick={() => setSaveRecipeDialogOpen(true)} /><AppButton variant="primary" label="Maintenance" icon="pi pi-wrench" onClick={() => navigate(`/properties/${id}`)} /></AppActionGroup>
    </header>
    {error ? <div className="rehab-error">{error}</div> : <>{workflowSaveError ? <div className="rehab-error rehab-error--compact">{workflowSaveError}</div> : null}{workspaceSizeError ? <div className="rehab-error rehab-error--compact">{workspaceSizeError}</div> : null}{hardwareModeError ? <div className="rehab-error rehab-error--compact">{hardwareModeError}</div> : null}<div ref={workspaceRef} className={`rehab-workspace${paletteOpen ? '' : ' rehab-workspace--palette-collapsed'}`} style={{ ...textStyleCssVariables(textStyleDefaults), ...(workspaceSize ? { width: `max(100%, ${workspaceSize.width}px)`, height: workspaceSize.height } : {}) }}>
      <aside className="rehab-palette"><div className="rehab-palette__header">{paletteOpen ? <span className="rehab-palette__title"><strong>COMPONENTS</strong><button type="button" className="rehab-palette__toggle" aria-label="Save new component" title="Save new component" onClick={() => { setIconSearch(''); setComponentSelectionIds([]); setComponentDialogOpen(true); }}><i className="pi pi-plus" aria-hidden="true" /></button><button type="button" className="rehab-palette__toggle rehab-palette__title-add" aria-label="Add component title" title="Add title" onClick={() => void createComponentTitle()}>T</button></span> : <strong />}<button type="button" className="rehab-palette__toggle" aria-label={paletteOpen ? 'Collapse step library' : 'Expand step library'} onClick={() => setPaletteOpen((open) => !open)}><i className={`pi ${paletteOpen ? 'pi-angle-left' : 'pi-angle-right'}`} aria-hidden="true" /></button></div>{paletteOpen ? <div className="rehab-palette__items">
        {sidebarItems.map((item) => {
          const itemKey = item.key;
          const record = item.record;
          if (nameEdit?.kind === item.kind && nameEdit.id === item.id) return <div key={itemKey} className={`rehab-palette__editing${item.kind.includes('title') ? ' rehab-palette__editing--title' : ''}`} onDragEnter={(event) => { event.preventDefault(); reorderSidebar(libraryDragRef.current, itemKey); }} onDragOver={(event) => event.preventDefault()}>
            {item.kind === 'component' || item.kind === 'step' ? <WorkflowIcon icon={record.icon} aria-hidden="true" /> : null}<input aria-label={`Rename ${record.name ?? record.label}`} value={nameEdit.value} maxLength={100} autoFocus onFocus={(event) => event.target.select()} onChange={(event) => setNameEdit({ ...nameEdit, value: event.target.value })} onKeyDown={nameEditKeyDown} onBlur={commitNameEdit} />
          </div>;
          const dragHandlers = {
            draggable: !sidebarCleanupOpen,
            onDragStart: (event) => {
              if (sidebarCleanupOpen) { event.preventDefault(); return; }
              libraryDragRef.current = itemKey;
              event.dataTransfer.effectAllowed = item.kind === 'title' || item.kind === 'system-title' ? 'move' : 'copyMove';
              event.dataTransfer.setData('text/plain', itemKey);
              if (item.kind === 'component') event.dataTransfer.setData('application/x-rehab-component', item.id);
              if (item.kind === 'step') event.dataTransfer.setData('application/x-rehab-node', record.type);
            },
            onDragEnd: () => { libraryDragRef.current = null; },
            onDragEnter: (event) => { event.preventDefault(); reorderSidebar(libraryDragRef.current, itemKey); },
            onDragOver: (event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; },
          };
          if (item.kind === 'title' || item.kind === 'system-title') return <div key={itemKey} className="rehab-palette__section rehab-palette__section--custom" {...dragHandlers} onContextMenu={(event) => { event.preventDefault(); setDeleteLibraryItem({ kind: item.kind, id: item.id, name: record.name }); }}><span title="Double-click to rename" onDoubleClick={() => setNameEdit({ kind: item.kind, id: item.id, value: record.name, original: record.name })}>{record.name}</span></div>;
          const itemName = item.kind === 'step' ? record.label : record.name;
          const startSidebarEdit = (event) => {
            const field = event.target.closest('[data-sidebar-edit]')?.dataset.sidebarEdit;
            if (!field) return;
            event.stopPropagation();
            if (field === 'name') setNameEdit({ kind: item.kind, id: item.id, value: itemName, original: itemName });
            else { setIconSearch(''); setIconEdit({ kind: item.kind, id: item.id, icon: displayIcon(record.icon), name: itemName }); }
          };
          // Plug-and-play steps and saved components share one card: the whole card drags (browsers do not
          // start a native drag from an element nested inside a <button>), double-click edits.
          const personRef = item.kind === 'component'
            ? record.nodes?.find((node) => node.contact_ref)?.contact_ref : null;
          const linkedPerson = personRef ? network?.find((entry) => entry.directory_source === 'person_account'
            && entry.kind === personRef.kind && entry.id === personRef.id) : null;
          const cleanupSelected = sidebarCleanupSelection.includes(itemKey);
          const cleanupSelectable = sidebarCleanupOpen && (item.kind === 'step' || item.kind === 'component');
          return <button key={itemKey} type="button" {...dragHandlers} className={`${cleanupSelectable ? 'rehab-palette__cleanup-item' : ''}${cleanupSelected ? ' is-selected' : ''}`} title={cleanupSelectable ? `${cleanupSelected ? 'Keep' : 'Select'} ${itemName}` : `Drag ${itemName}`} aria-pressed={cleanupSelectable ? cleanupSelected : undefined} onClick={cleanupSelectable ? () => setSidebarCleanupSelection((current) => current.includes(itemKey) ? current.filter((key) => key !== itemKey) : [...current, itemKey]) : undefined} onContextMenu={(event) => { event.preventDefault(); if (sidebarCleanupOpen) { openSelectedLibraryRemoval(itemKey); return; } setDeleteLibraryItem({ kind: item.kind, id: item.id, key: itemKey, name: itemName,
            person: linkedPerson ? { id: linkedPerson.id, kind: linkedPerson.kind, name: linkedPerson.name } : null }); }} onDoubleClick={sidebarCleanupOpen ? undefined : startSidebarEdit}><WorkflowIcon icon={record.icon} data-sidebar-edit="icon" draggable={false} title={sidebarCleanupOpen ? undefined : 'Double-click to change icon'} /><span data-sidebar-edit="name" title={sidebarCleanupOpen ? undefined : 'Double-click to rename'}>{itemName}</span><i className={`pi ${cleanupSelectable ? cleanupSelected ? 'pi-check-square' : 'pi-stop' : 'pi-bars'}`} aria-hidden="true" /></button>;
        })}
        <div className="rehab-palette__drop-end" aria-label="Move sidebar item to end" onDragEnter={(event) => { event.preventDefault(); moveSidebarItemToEnd(libraryDragRef.current); }} onDragOver={(event) => event.preventDefault()} />
      </div> : null}</aside>
      <div className="rehab-canvas-frame">
        <div className="rehab-recipe-toolbar">
          <span>RECIPE</span>
          <select aria-label="Current recipe" value={selectedRecipeId} disabled={recipeCopying} onChange={(event) => { const recipeId = event.target.value; if (recipeId) void copyRecipe(recipeId); }}>
            <option value="">{recipes.length ? 'Select recipe' : 'No saved recipes'}</option>
            {recipes.map((recipe) => <option key={recipe.recipe_id} value={recipe.recipe_id}>{recipe.name}</option>)}
          </select>
          {recipeCopying ? <i className="pi pi-spin pi-spinner" aria-label="Loading recipe" /> : <i className="pi pi-chevron-down" aria-hidden="true" />}
          <button type="button" className="rehab-recipe-toolbar__reload" aria-label="Reload current recipe" title="Reload saved recipe" disabled={!selectedRecipeId || recipeCopying} onClick={() => void copyRecipe(selectedRecipeId, true)}><i className="pi pi-refresh" aria-hidden="true" /><span>Reload</span></button>
        </div>
        <div className="rehab-zoom-controls" role="group" aria-label="Workflow zoom controls">
          <button type="button" aria-label="Zoom out" disabled={zoom <= MIN_ZOOM} onClick={() => changeZoom(zoom - ZOOM_STEP)}><i className="pi pi-minus" aria-hidden="true" /></button>
          <button type="button" className="rehab-zoom-value" aria-label="Reset zoom" title="Reset zoom" onClick={() => changeZoom(1)}>{Math.round(zoom * 100)}%</button>
          <button type="button" aria-label="Zoom in" disabled={zoom >= MAX_ZOOM} onClick={() => changeZoom(zoom + ZOOM_STEP)}><i className="pi pi-plus" aria-hidden="true" /></button>
        </div>
      <main ref={canvasRef} className={`rehab-canvas rehab-canvas--${interactionMode}`} onWheel={handleWheelZoom} onDoubleClick={toggleInteractionMode} onContextMenu={(event) => { if (selectedNodeIds.length) { event.preventDefault(); if (selectedNodeIds.length > 1) setSelectionMenu({ x: event.clientX, y: event.clientY }); else { setComponentSelectionIds([...selectedNodeIds]); setComponentDialogOpen(true); } } }} onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }} onDrop={handleDrop}
        onPointerDown={startCanvasInteraction} onPointerMove={moveNode}
        onPointerUp={(event) => { if (finishCanvasPan(event) || finishSelection(event)) return; if (!finishConnection(event)) finishNodeDrag(event); }}
        onPointerCancel={(event) => { if (finishCanvasPan(event) || finishSelection(event)) return; if (!finishConnection(event)) finishNodeDrag(event); }}>
        <div className="rehab-canvas-world-shell" style={{ width: worldSize.width * zoom, height: worldSize.height * zoom }}>
        <div className="rehab-canvas-world" style={{ width: worldSize.width, height: worldSize.height, transform: `scale(${zoom})` }}>
        {selectionBox ? <div className="rehab-selection-box" style={{
          left: Math.min(selectionBox.x1, selectionBox.x2), top: Math.min(selectionBox.y1, selectionBox.y2),
          width: Math.abs(selectionBox.x2 - selectionBox.x1), height: Math.abs(selectionBox.y2 - selectionBox.y1),
        }} /> : null}
        <svg className="rehab-connections" aria-label="Workflow connections"><defs><marker id="rehab-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" /></marker></defs>
          {visibleEdges.map((edge) => { const source = allNodesById.get(edge.source); const target = allNodesById.get(edge.target); if (!source || !target) return null; const path = edgePath(portPoint(source, edge.source_handle), portPoint(target, edge.target_handle)); const label = `Remove connection from ${source.label} to ${target.label}`; return <g key={edge.id} className={`rehab-connection-group${reconnectTargetEdgeId === edge.id ? ' rehab-connection-group--reconnect' : ''}`}>
            <path className="rehab-connection-line" d={path} markerStart="url(#rehab-arrow)" markerEnd="url(#rehab-arrow)" />
            <path className="rehab-connection-hit" d={path} role="button" tabIndex="0" aria-label={label} data-workflow-edge data-edge-id={edge.id}
              onClick={(event) => { event.stopPropagation(); removeEdge(edge.id); }}
              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === 'Delete' || event.key === 'Backspace') { event.preventDefault(); removeEdge(edge.id); } }} />
          </g>; })}
          {connectionDraft?.point && (() => { const fixedNode = allNodesById.get(connectionDraft.fixed.nodeId); if (!fixedNode) return null; const fixedPoint = portPoint(fixedNode, connectionDraft.fixed.handle); const start = connectionDraft.fixed.role === 'source' ? fixedPoint : connectionDraft.point; const end = connectionDraft.fixed.role === 'source' ? connectionDraft.point : fixedPoint; return <path className="rehab-connection-draft" d={edgePath(start, end)} />; })()}
        </svg>
        {visibleNodes.map((node, index) => {
          const bidChild = node.type?.startsWith('bid-');
          const inspectionCategory = node.type === 'inspection-category';
          const inspectionArea = node.type === 'inspection-area';
          const expandableGroup = inspectionCategory || node.type === 'inspector-documentation' || node.type === 'contractor-bids';
          const groupCollapsed = expandableGroup && isGroupCollapsed(node);
          const branchCollapseEnabled = Boolean(normalizeCollapseSides(node.collapse_sides)?.length);
          const headerCollapsed = branchCollapseEnabled ? node.branches_collapsed === true : groupCollapsed;
          const editingName = nameEdit?.kind === 'node' && nameEdit.id === node.id && nameEdit.field !== 'detail';
          // People nodes show the directory record's current name; it is renamed in the directory, not on the canvas.
          const contact = node.type === 'contact' ? resolveContactNode(node, network) : null;
          const contactUnavailable = Boolean(contact && !contact.available && networkStatus === 'ready');
          const hardware = node.type === 'hardware' ? resolveHardwareNode(node, hardwareDirectory) : null;
          const hardwareUnavailable = Boolean(hardware && !hardware.available && hardwareStatus === 'ready');
          const editingDetail = nameEdit?.kind === 'node' && nameEdit.id === node.id && nameEdit.field === 'detail';
          const workflowNode = index > 0;
          const detailText = node.detail || '';
          const detailExpanded = Boolean(node.detail_expanded) || editingDetail;
          const canExpandDetail = Boolean(node.detail_expanded) || detailText.length > 28 || detailText.includes('\n');
          // A dragged size is the node's height while collapsed and its minimum height while expanded.
          const sizeStyle = { ...(node.width ? { width: node.width } : {}),
            ...(node.height ? (detailExpanded ? { minHeight: node.height } : { height: node.height }) : {}) };
          const notesNode = node.type === 'notes';
          const noteAppearance = notesNode
            ? (noteStyleEdit?.id === node.id ? noteStyleEdit.draft : normalizeNoteAppearance(node.appearance) || DEFAULT_NOTE_APPEARANCE)
            : null;
          const noteStyle = noteAppearance ? {
            '--rehab-note-background': noteAppearance.background_color,
            '--rehab-note-header': noteAppearance.header_color,
            '--rehab-note-border': noteAppearance.border_color,
            '--rehab-note-text': noteAppearance.text_color,
          } : {};
          const nodeAppearance = !notesNode
            ? (nodeStyleEdit?.id === node.id ? nodeStyleEdit.draft : normalizeNodeAppearance(node.appearance)) : null;
          const nodeAppearanceStyle = nodeAppearance ? {
            '--rehab-node-background': nodeAppearance.background_color,
            '--rehab-node-header': nodeAppearance.header_color,
            '--rehab-node-border': nodeAppearance.border_color,
          } : {};
          return <div key={node.id} ref={observeNodeElement} data-workflow-node data-node-id={node.id} onPointerDown={(event) => startNodeDrag(event, node)} onDoubleClick={(event) => startNodeEdit(event, node)} onWheel={(event) => {
            const detail = editingDetail
              ? event.currentTarget.querySelector('.rehab-rich-editor__body')
              : event.currentTarget.querySelector('.rehab-node-detail');
            if (!detail || (!editingDetail && (!node.height || detailExpanded))) return;
            event.preventDefault(); event.stopPropagation();
            detail.scrollTop += event.deltaY;
          }} className={`rehab-node${index === 0 ? ' rehab-node--property' : ''}${bidChild ? ' rehab-node--bid-child' : ''}${inspectionCategory ? ' rehab-node--inspection-category' : ''}${inspectionArea ? ' rehab-node--inspection-area' : ''}${notesNode ? ' rehab-node--notes' : ''}${nodeAppearance ? ' rehab-node--custom-appearance' : ''}${isActualOverEstimate(node, nodes) ? ' rehab-node--over-budget' : ''}${selectedNodeIds.includes(node.id) ? ' rehab-node--selected' : ''}${draggingNodeId === node.id ? ' rehab-node--dragging' : ''}${node.height ? ' rehab-node--sized' : ''}${detailExpanded ? ' rehab-node--detail-expanded' : ''}${contact || hardware ? ' rehab-node--contact' : ''}${contactUnavailable || hardwareUnavailable ? ' rehab-node--unavailable' : ''}`} style={{ left: node.x + CANVAS_ORIGIN, top: node.y + CANVAS_ORIGIN, ...sizeStyle, ...noteStyle, ...nodeAppearanceStyle }}>
            {PORT_SIDES.flatMap((side) => portHandles(node, side).map((handle) => <React.Fragment key={handle}>{renderPort(node, handle)}</React.Fragment>))}
            <div className="rehab-node-header"><WorkflowIcon icon={node.icon} {...(index > 0 ? { 'data-node-edit': 'icon', title: 'Double-click to change icon' } : {})} />{editingName
              ? <input className="rehab-node-name-input" aria-label={`Rename ${node.label}`} value={nameEdit.value} maxLength={100} autoFocus onFocus={(event) => event.target.select()} onPointerDown={(event) => event.stopPropagation()} onChange={(event) => setNameEdit({ ...nameEdit, value: event.target.value })} onKeyDown={nameEditKeyDown} onBlur={commitNameEdit} />
              : contact ? <strong title={contactUnavailable ? 'Not in your current directory' : contact.name}>{contact.name}</strong>
              : hardware ? <strong title={hardwareUnavailable ? 'Hardware unavailable' : hardware.name}>{hardware.name}</strong>
              : <strong {...(index > 0 ? { 'data-node-edit': 'label', title: 'Double-click to rename' } : {})}>{node.label}</strong>}<span className="rehab-node-actions">{branchCollapseEnabled || expandableGroup ? <button type="button" className="rehab-node-toggle" aria-label={`${headerCollapsed ? 'Expand' : 'Collapse'} ${node.label}`} aria-expanded={!headerCollapsed} onClick={(event) => { event.stopPropagation(); if (branchCollapseEnabled) toggleConnectedBranches(node); else toggleNodeGroup(node.id); }}><i className={`pi ${headerCollapsed ? 'pi-chevron-down' : 'pi-chevron-up'}`} aria-hidden="true" /></button> : null}<span className="rehab-node-action-menu" onPointerDown={(event) => event.stopPropagation()}><button type="button" className="rehab-node-more" aria-label={`Actions for ${node.label}`} title="Node actions" aria-expanded={nodeActionMenuId === node.id} onClick={(event) => { event.stopPropagation(); setNodeActionMenuId((current) => current === node.id ? null : node.id); }}><i className="pi pi-ellipsis-v" aria-hidden="true" /></button>{nodeActionMenuId === node.id ? <span role="menu" aria-label={`${node.label} actions`}><button type="button" role="menuitem" onClick={(event) => { setNodeActionMenuId(null); if (notesNode) openNoteStyle(event, node); else openNodeStyle(event, node); }}><i className="pi pi-palette" aria-hidden="true" />Appearance</button>{hardware?.available ? <label className="rehab-node-mode"><i className="pi pi-bolt" aria-hidden="true" /><select aria-label={`${hardware.name} connection mode`} value={hardware.connection_mode} disabled={hardwareModeSaving === node.hardware_ref.id} onChange={(event) => changeHardwareConnectionMode(node.hardware_ref.id, event.target.value)}><option value="live">Live</option><option value="demo">Demo</option><option value="simulated">Simulated</option></select></label> : null}<button type="button" role="menuitem" onClick={(event) => openPortLayout(event, node)}><i className="pi pi-share-alt" aria-hidden="true" />Connections &amp; collapse</button>{index > 0 ? <button type="button" role="menuitem" className="is-danger" onClick={(event) => { event.stopPropagation(); setNodeActionMenuId(null); removeNode(node.id); }}><i className="pi pi-trash" aria-hidden="true" />Remove node</button> : null}</span> : null}</span></span></div>
            {(node.type === 'bid-estimate' || node.type === 'bid-actual') ? <label className="rehab-node-amount"><span>$</span><input type="number" min="0" step="0.01" aria-label={`${node.label} amount`} value={node.amount ?? ''} placeholder="0" onFocus={() => { amountEditSnapshotRef.current = graphSnapshot(); }} onChange={(event) => updateAmount(node.id, event.target.value)} onBlur={finishAmountEdit} /></label> : null}
            {contact ? <small className="rehab-node-contact-meta">{contactUnavailable ? 'Unavailable contact' : contact.meta || CONTACT_KINDS[node.contact_ref?.kind]?.label}</small> : null}
            {hardware ? <small className="rehab-node-contact-meta">{hardwareUnavailable ? 'Unavailable hardware' : hardware.meta || 'Hardware device'}</small> : null}
            {node.type === 'lender' && node.lender_offer ? <div className="rehab-lender-node">
              <span className="rehab-lender-node__demo">DEMO DATA · #{node.lender_offer.rank}</span>
              <div><strong>{node.lender_offer.rate}%</strong><small>Rate</small></div>
              <div><strong>{node.lender_offer.points}</strong><small>Points</small></div>
              <div><strong>${Number(node.lender_offer.cash_required).toLocaleString()}</strong><small>Cash needed</small></div>
              <p>{node.lender_offer.type === 'hard-money' ? 'Hard money' : 'Conventional'} · {node.lender_offer.funding_days} day funding · {node.lender_offer.max_ltc}% max LTC</p>
            </div> : !workflowNode || (contact && !detailText && !editingDetail) ? (workflowNode ? null : <small>{node.detail}</small>) : editingDetail
              ? <RichNodeEditor node={node} propertyId={id} mediaById={workflowMedia} initialViewport={nameEdit.initialViewport}
                onMediaAdded={(item) => setWorkflowMedia((current) => ({ ...current, [item.media_id]: item }))}
                textColors={textColors} onTextColorsChange={changeTextColors} textStyleDefaults={textStyleDefaults} onTextStyleDefaultsChange={changeTextStyleDefaults} onOpen={ensureTextColors} paletteError={textColorsError} styleDefaultsError={textStyleDefaultsError}
                onCancel={() => setNameEdit(null)} onSave={(changes) => { setNameEdit(null); updateNodeFields(node.id, changes); }} />
              : <div className="rehab-node-detail"><small data-node-edit="detail" title="Double-click to edit description">
                {node.rich_content?.version === 2 ? <WorkflowBlocksView blocks={node.rich_content.blocks} mediaById={workflowMedia} />
                  : node.rich_content?.body_html ? <span className="rehab-node-rich-text rehab-content-surface" dangerouslySetInnerHTML={{ __html: richHtmlWithMediaUrls(node.rich_content.body_html, workflowMedia) }} />
                  : detailText || <span className="rehab-node-detail__placeholder">Add description…</span>}
                {node.rich_content?.images?.some((image) => !node.rich_content.body_html?.includes(`data-media-id="${image.media_id}"`)) ? <span className="rehab-node-media-preview">{node.rich_content.images.filter((image) => !node.rich_content.body_html?.includes(`data-media-id="${image.media_id}"`)).map((image) => {
                  const media = workflowMedia[image.media_id];
                  return <span key={image.media_id} className="rehab-node-media-preview__item">
                    {media?.url ? <img src={media.url} alt={image.caption || media.original_name || 'Workflow attachment'} /> : null}
                    {image.caption ? <strong>{image.caption}</strong> : null}
                    {image.purchase_url ? <a href={image.purchase_url} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}>View product</a> : null}
                  </span>;
                })}</span> : null}
              </small>{canExpandDetail || node.rich_content?.images?.length ? <button type="button" className="rehab-node-detail-toggle" aria-label={`${detailExpanded ? 'Collapse' : 'Expand'} ${node.label} description`} aria-expanded={detailExpanded} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); updateNodeFields(node.id, { detail_expanded: !node.detail_expanded }); }}><i className={`pi ${detailExpanded ? 'pi-angle-up' : 'pi-angle-down'}`} aria-hidden="true" /></button> : null}</div>}
            {workflowNode ? RESIZE_CORNERS.map((corner) => <span key={corner} className={`rehab-node-resize rehab-node-resize--${corner}`} data-resize-corner={corner} title="Drag to resize" aria-hidden="true" onPointerDown={(event) => startNodeResize(event, node, corner)} onPointerMove={moveNodeResize} onPointerUp={finishNodeResize} onPointerCancel={finishNodeResize} />) : null}
          </div>;
        })}
        {nodes.length === 0 && <div className="rehab-canvas-hint"><i className="pi pi-arrow-left" /><span>Drag the next rehab step from the library</span></div>}
        </div>
        </div>
      </main>
      <div className="rehab-mode-indicator" aria-live="polite"><i className={`pi ${interactionMode === 'pan' ? 'pi-arrows-alt' : 'pi-clone'}`} aria-hidden="true" /><strong>{interactionMode === 'pan' ? 'PAN' : 'SELECT'}</strong><span>{interactionMode === 'pan' ? 'Double-click canvas to select' : 'Drag a box · Shift-drag to reconnect'}</span></div>
      </div>
      <button type="button" className="rehab-workspace-resize" aria-label="Resize workflow grid" title="Drag to resize the workflow grid" onPointerDown={startWorkspaceResize} onPointerMove={moveWorkspaceResize} onPointerUp={finishWorkspaceResize} onPointerCancel={finishWorkspaceResize} />
    </div></>}
    {selectionMenu ? <div className="rehab-selection-menu" role="menu" aria-label="Selected node actions" style={{ left: Math.min(selectionMenu.x, window.innerWidth - 190), top: Math.min(selectionMenu.y, window.innerHeight - 130) }} onPointerDown={(event) => event.stopPropagation()}>
      <button type="button" role="menuitem" onClick={() => { const anchor = nodesRef.current.find((node) => selectedNodeIds.includes(node.id) && node.id !== 'property'); if (anchor) openNodeStyle(null, anchor, selectedNodeIds.filter((nodeId) => nodeId !== 'property')); }}><i className="pi pi-palette" aria-hidden="true" />Change colors</button>
      <button type="button" role="menuitem" onClick={() => { setComponentSelectionIds(selectedNodeIds.filter((nodeId) => nodeId !== 'property')); setComponentDialogOpen(true); setSelectionMenu(null); }}><i className="pi pi-box" aria-hidden="true" />Save selection as component</button>
      <button type="button" role="menuitem" className="is-danger" onClick={deleteSelectedNodes}><i className="pi pi-trash" aria-hidden="true" />Delete selected</button>
    </div> : null}
    <AppFormDialog title="Add canvas component" visible={quickPickerOpen} onHide={() => setQuickPickerOpen(false)} modal draggable={false} className="rehab-quick-picker">
      <AppField label="Find a component" htmlFor="rehab-quick-component-search">
        <div className="rehab-quick-picker__search"><i className="pi pi-search" aria-hidden="true" /><input id="rehab-quick-component-search" value={quickPickerSearch} autoFocus placeholder="Search components" onChange={(event) => setQuickPickerSearch(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && filteredQuickComponents.length) addQuickComponent(); }} /></div>
      </AppField>
      {pinnedQuickComponentRecords.length ? <section className="rehab-quick-picker__section"><strong>PINNED</strong><div className="rehab-quick-picker__results rehab-quick-picker__results--pinned" role="listbox" aria-label="Pinned canvas components">{pinnedQuickComponentRecords.map((component) => renderQuickComponentRow(component, true))}</div></section> : null}
      {availableQuickComponentRecords.length || quickPickerSearch ? <section className="rehab-quick-picker__section"><strong>ALL COMPONENTS</strong><div className="rehab-quick-picker__results" role="listbox" aria-label="All canvas components">
        {availableQuickComponentRecords.map((component) => renderQuickComponentRow(component, false))}
        {!filteredQuickComponents.length ? <p>No matching components.</p> : null}
      </div></section> : null}
      {quickPickerError ? <p className="rehab-quick-picker__error" role="alert">{quickPickerError}</p> : null}
      <AppActionGroup><AppButton variant="text" label="Cancel" onClick={() => setQuickPickerOpen(false)} /><AppButton variant="primary" label="Add component" icon="pi pi-plus" disabled={!filteredQuickComponents.some((component) => component.key === quickPickerSelection)} onClick={addQuickComponent} /></AppActionGroup>
    </AppFormDialog>
    <AppFormDialog title="Lender comparison" titleMeta="Demo data" visible={lenderComparisonOpen} onHide={() => setLenderComparisonOpen(false)} modal draggable={false} className="rehab-lender-dialog">
      <div className="rehab-lender-dialog__summary"><span>Estimated project <strong>${Math.round(lenderNeeds.project_cost).toLocaleString()}</strong></span><span>Target loan <strong>${Math.round(lenderNeeds.loan_amount).toLocaleString()}</strong></span></div>
      <div className="rehab-lender-type-switch" role="group" aria-label="Lender type">
        <button type="button" className={lenderType === 'hard-money' ? 'is-active' : ''} aria-pressed={lenderType === 'hard-money'} onClick={() => setLenderType('hard-money')}>Hard money</button>
        <button type="button" className={lenderType === 'conventional' ? 'is-active' : ''} aria-pressed={lenderType === 'conventional'} onClick={() => setLenderType('conventional')}>Conventional</button>
      </div>
      <p className="rehab-lender-dialog__notice"><i className="pi pi-info-circle" aria-hidden="true" /> Practice offers only—not live quotes. Ranking favors eligible offers with the lowest projected cost.</p>
      <div className="rehab-lender-results" role="radiogroup" aria-label={`${lenderType === 'hard-money' ? 'Hard money' : 'Conventional'} lenders`}>
        {rankedLenders.map((lender) => <button key={lender.id} type="button" role="radio" aria-checked={selectedLenderId === lender.id} disabled={!lender.eligible} className={`${selectedLenderId === lender.id ? 'is-selected' : ''}${!lender.eligible ? ' is-ineligible' : ''}`} onClick={() => setSelectedLenderId(lender.id)}>
          <span className="rehab-lender-rank">#{lender.rank}</span><span className="rehab-lender-name"><strong>{lender.name}</strong><small>{lender.reason}</small></span>
          <span><strong>{lender.rate}%</strong><small>Rate</small></span><span><strong>{lender.points}</strong><small>Points</small></span>
          <span><strong>${lender.cash_required.toLocaleString()}</strong><small>Cash</small></span><span><strong>{lender.funding_days}d</strong><small>Funding</small></span>
        </button>)}
      </div>
      <AppActionGroup><AppButton variant="text" label="Cancel" onClick={() => setLenderComparisonOpen(false)} /><AppButton variant="primary" label="Add selected lender" icon="pi pi-plus" disabled={!rankedLenders.some((lender) => lender.id === selectedLenderId && lender.eligible)} onClick={addSelectedLender} /></AppActionGroup>
    </AppFormDialog>
    <AppFormDialog title="Note appearance" visible={Boolean(noteStyleEdit)} onHide={() => setNoteStyleEdit(null)} modal draggable={false} className="rehab-note-style-dialog">
      <div className="rehab-note-style-presets" aria-label="Note color presets">{NOTE_APPEARANCE_PRESETS.map(([name, appearance]) => <button key={name} type="button" title={name} aria-label={`Use ${name} note style`} onClick={() => setNoteStyleEdit((current) => current ? { ...current, draft: appearance } : current)} style={{ '--preset-background': appearance.background_color, '--preset-header': appearance.header_color, '--preset-border': appearance.border_color }}><span />{name}</button>)}</div>
      <div className="rehab-note-style-fields">{[
        ['background_color', 'Background'], ['header_color', 'Header'], ['border_color', 'Border'], ['text_color', 'Default text'],
      ].map(([key, label]) => <AppField key={key} label={label} htmlFor={`rehab-note-${key}`}><div className="rehab-note-color-field"><input type="color" aria-label={`${label} color picker`} value={normalizeNoteAppearance(noteStyleEdit?.draft)?.[key] || DEFAULT_NOTE_APPEARANCE[key]} onChange={(event) => setNoteStyleColor(key, event.target.value)} /><input id={`rehab-note-${key}`} value={noteStyleEdit?.draft?.[key] || ''} maxLength={7} onChange={(event) => setNoteStyleColor(key, event.target.value)} /></div></AppField>)}</div>
      <AppActionGroup><AppButton variant="text" label="Reset" onClick={() => setNoteStyleEdit((current) => current ? { ...current, draft: DEFAULT_NOTE_APPEARANCE } : current)} /><AppButton variant="text" label="Cancel" onClick={() => setNoteStyleEdit(null)} /><AppButton variant="primary" label="Save style" icon="pi pi-save" disabled={!normalizeNoteAppearance(noteStyleEdit?.draft)} onClick={saveNoteStyle} /></AppActionGroup>
    </AppFormDialog>
    <AppFormDialog title={nodeStyleEdit?.scope === 'selected' ? 'Selected node colors' : 'Node appearance'} visible={Boolean(nodeStyleEdit)} onHide={() => setNodeStyleEdit(null)} modal draggable={false} className="rehab-note-style-dialog rehab-node-style-dialog">
      <p className="rehab-node-style-dialog__note">Only the node background, header, and border change. Existing fonts, text colors, icons, content, and formatting stay exactly as saved.</p>
      <div className="rehab-note-style-presets" aria-label="Node color presets">{NOTE_APPEARANCE_PRESETS.map(([name, appearance]) => <button key={name} type="button" title={name} aria-label={`Use ${name} node colors`} onClick={() => setNodeStyleEdit((current) => current ? { ...current, draft: { background_color: appearance.background_color, header_color: appearance.header_color, border_color: appearance.border_color } } : current)} style={{ '--preset-background': appearance.background_color, '--preset-header': appearance.header_color, '--preset-border': appearance.border_color }}><span />{name}</button>)}</div>
      <div className="rehab-note-style-fields">{[
        ['background_color', 'Background'], ['header_color', 'Header'], ['border_color', 'Border'],
      ].map(([key, label]) => <AppField key={key} label={label} htmlFor={`rehab-node-${key}`}><div className="rehab-note-color-field"><input type="color" aria-label={`${label} color picker`} value={normalizeNodeAppearance(nodeStyleEdit?.draft)?.[key] || DEFAULT_NODE_APPEARANCE[key]} onChange={(event) => setNodeStyleColor(key, event.target.value)} /><input id={`rehab-node-${key}`} value={nodeStyleEdit?.draft?.[key] || ''} maxLength={7} onChange={(event) => setNodeStyleColor(key, event.target.value)} /></div></AppField>)}</div>
      {nodeStyleEdit?.scope !== 'selected' ? <div className="rehab-node-style-scope"><span>Apply to</span><div role="group" aria-label="Color application scope"><button type="button" className={nodeStyleEdit?.scope === 'one' ? 'is-selected' : ''} aria-pressed={nodeStyleEdit?.scope === 'one'} onClick={() => setNodeStyleEdit((current) => ({ ...current, scope: 'one' }))}>One node</button><button type="button" className={nodeStyleEdit?.scope === 'many' ? 'is-selected' : ''} aria-pressed={nodeStyleEdit?.scope === 'many'} onClick={() => setNodeStyleEdit((current) => ({ ...current, scope: 'many' }))}>Many connected</button></div></div> : <p className="rehab-node-style-dialog__selection-count">Applying to {nodeStyleEdit?.targetIds?.length || 0} selected nodes</p>}
      <AppActionGroup><AppButton variant="text" label="Reset" onClick={() => setNodeStyleEdit((current) => current ? { ...current, draft: DEFAULT_NODE_APPEARANCE } : current)} /><AppButton variant="text" label="Cancel" onClick={() => setNodeStyleEdit(null)} /><AppButton variant="primary" label="Save colors" icon="pi pi-save" disabled={!normalizeNodeAppearance(nodeStyleEdit?.draft)} onClick={saveNodeStyle} /></AppActionGroup>
    </AppFormDialog>
    <AppFormDialog title="Connection points" titleMeta={portLayoutEdit?.label} visible={Boolean(portLayoutEdit)} onHide={() => setPortLayoutEdit(null)} modal draggable={false} className="rehab-port-layout-dialog">
      <div className="rehab-port-layout-preview" aria-hidden="true">
        {PORT_SIDES.map((side) => Array.from({ length: portLayoutEdit?.draft?.[side] || 0 }, (_, index) => <span key={`${side}-${index}`} className={`is-${side}`} style={(side === 'top' || side === 'bottom') ? { left: `${portPositionPercent(`${side}-${index}`, portLayoutEdit?.draft?.[side])}%` } : { top: `${portPositionPercent(`${side}-${index}`, portLayoutEdit?.draft?.[side])}%` }} />))}
        <strong>{portLayoutEdit?.draft ? Object.values(portLayoutEdit.draft).reduce((sum, count) => sum + count, 0) : 0}</strong><small>points</small>
      </div>
      <div className="rehab-port-layout-controls">{PORT_SIDES.map((side) => <div key={side}><span>{side}</span><button type="button" aria-label={`Remove ${side} connection point`} disabled={(portLayoutEdit?.draft?.[side] || 0) <= (portLayoutEdit?.minimums?.[side] || 0)} onClick={() => changePortCount(side, -1)}><i className="pi pi-minus" /></button><output aria-label={`${side} connection count`}>{portLayoutEdit?.draft?.[side] || 0}</output><button type="button" aria-label={`Add ${side} connection point`} disabled={(portLayoutEdit?.draft?.[side] || 0) >= 8} onClick={() => changePortCount(side, 1)}><i className="pi pi-plus" /></button></div>)}</div>
      <div className="rehab-port-layout-uniform"><span>Same on all sides</span><div>{[0, 1, 2, 4].map((count) => <button key={count} type="button" onClick={() => setAllPortCounts(count)}>{count}</button>)}</div></div>
      <div className="rehab-port-collapse"><button type="button" className={portLayoutEdit?.collapseEnabled ? 'is-selected' : ''} aria-pressed={Boolean(portLayoutEdit?.collapseEnabled)} onClick={() => setPortLayoutEdit((current) => current ? { ...current, collapseEnabled: !current.collapseEnabled, branchesCollapsed: false } : current)}><i className="pi pi-chevron-down" aria-hidden="true" /><span><strong>Collapse caret</strong><small>Hide connected branches by side</small></span></button>{portLayoutEdit?.collapseEnabled ? <div role="group" aria-label="Collapse connected sides"><span>Sides</span>{PORT_SIDES.map((side) => <button key={side} type="button" className={portLayoutEdit.collapseSides.includes(side) ? 'is-selected' : ''} aria-pressed={portLayoutEdit.collapseSides.includes(side)} onClick={() => toggleCollapseSide(side)}>{side}</button>)}</div> : null}</div>
      <AppActionGroup><AppButton variant="text" label="Cancel" onClick={() => setPortLayoutEdit(null)} /><AppButton variant="primary" label="Save connections" icon="pi pi-save" disabled={!normalizePortLayout(portLayoutEdit?.draft) || (portLayoutEdit?.collapseEnabled && !portLayoutEdit?.collapseSides?.length)} onClick={savePortLayout} /></AppActionGroup>
    </AppFormDialog>
    <AppFormDialog title="Save as new recipe" visible={saveRecipeDialogOpen} onHide={() => !recipeSaving && setSaveRecipeDialogOpen(false)} modal draggable={false} className="rehab-recipe-dialog">
      <AppField label="Recipe name" htmlFor="rehab-recipe-name">
        <input id="rehab-recipe-name" value={recipeName} maxLength={100} disabled={recipeSaving} placeholder="Standard BRRRR rehab" onChange={(event) => setRecipeName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void saveRecipe(); }} />
      </AppField>
      <AppActionGroup><AppButton variant="text" label="Cancel" disabled={recipeSaving} onClick={() => setSaveRecipeDialogOpen(false)} /><AppButton variant="primary" label={recipeSaving ? 'Saving…' : 'Create recipe'} icon="pi pi-save" disabled={!recipeName.trim() || recipeSaving} onClick={() => saveRecipe()} /></AppActionGroup>
    </AppFormDialog>
    <AppFormDialog title={componentSelectionIds.length ? 'Save selected component' : 'Create component'} visible={componentDialogOpen} onHide={() => { if (!componentSaving) { setComponentDialogOpen(false); setComponentSelectionIds([]); setIconSearch(''); } }} modal draggable={false} className="rehab-component-dialog">
      <AppField label="Component name" htmlFor="rehab-component-name">
        <input id="rehab-component-name" value={componentName} maxLength={100} autoFocus disabled={componentSaving} placeholder="Security setup" onChange={(event) => setComponentName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void saveComponent(); }} />
      </AppField>
      {componentSelectionIds.length ? renderIconPicker(componentIcon, setComponentIcon)
        : pickerMode === 'people' ? renderPeoplePicker() : pickerMode === 'hardware' ? renderHardwarePicker() : renderIconPicker(componentIcon, setComponentIcon, true)}
      <AppActionGroup><AppButton variant="text" label="Cancel" disabled={componentSaving} onClick={() => { setComponentDialogOpen(false); setComponentSelectionIds([]); }} /><AppButton variant="primary" label={componentSaving ? 'Saving…' : 'Save component'} icon="pi pi-save" disabled={!componentName.trim() || componentSaving || (!componentSelectionIds.length && ((pickerMode === 'people' && !selectedPerson) || (pickerMode === 'hardware' && !selectedHardware)))} onClick={() => saveComponent()} /></AppActionGroup>
    </AppFormDialog>
    <AppFormDialog title={iconEdit ? `Change icon · ${iconEdit.name}` : 'Change icon'} visible={Boolean(iconEdit)} onHide={() => { setIconEdit(null); setIconSearch(''); }} modal draggable={false} className="rehab-component-dialog">
      {iconEdit ? renderIconPicker(iconEdit.icon, chooseEditedIcon) : null}
      <AppActionGroup><AppButton variant="text" label="Cancel" onClick={() => { setIconEdit(null); setIconSearch(''); }} /></AppActionGroup>
    </AppFormDialog>
    <AppFormDialog title="Add hardware" visible={hardwareFormOpen} onHide={() => !hardwareSaving && setHardwareFormOpen(false)} modal draggable={false} className="rehab-hardware-dialog">
      <AppField label="Category" htmlFor="rehab-hardware-category"><select id="rehab-hardware-category" value={hardwareDraft.category_id} onChange={(event) => setHardwareDraft((current) => ({ ...current, category_id: event.target.value }))}><option value="">Create a new category</option>{hardwareDirectory.categories.map((category) => <option key={category.category_id} value={category.category_id}>{category.name}</option>)}</select></AppField>
      {!hardwareDraft.category_id ? <AppField label="New category name" htmlFor="rehab-hardware-category-name"><input id="rehab-hardware-category-name" value={hardwareDraft.category_name} maxLength={80} placeholder="Networking" onChange={(event) => setHardwareDraft((current) => ({ ...current, category_name: event.target.value }))} /></AppField> : null}
      <AppField label="Device name" htmlFor="rehab-hardware-name"><input id="rehab-hardware-name" value={hardwareDraft.display_name} maxLength={120} placeholder="Main Street network switch" onChange={(event) => setHardwareDraft((current) => ({ ...current, display_name: event.target.value }))} /></AppField>
      <div className="rehab-person-form__names"><AppField label="Manufacturer" htmlFor="rehab-hardware-manufacturer"><input id="rehab-hardware-manufacturer" value={hardwareDraft.manufacturer} maxLength={120} placeholder="MikroTik" onChange={(event) => setHardwareDraft((current) => ({ ...current, manufacturer: event.target.value }))} /></AppField><AppField label="Model" htmlFor="rehab-hardware-model"><input id="rehab-hardware-model" value={hardwareDraft.model} maxLength={120} placeholder="CRS112" onChange={(event) => setHardwareDraft((current) => ({ ...current, model: event.target.value }))} /></AppField></div>
      <div className="rehab-hardware-form__grid"><AppField label="Serial (optional)" htmlFor="rehab-hardware-serial"><input id="rehab-hardware-serial" value={hardwareDraft.serial_number} maxLength={160} onChange={(event) => setHardwareDraft((current) => ({ ...current, serial_number: event.target.value }))} /></AppField><AppField label="Firmware (optional)" htmlFor="rehab-hardware-firmware"><input id="rehab-hardware-firmware" value={hardwareDraft.firmware_version} maxLength={120} placeholder="7.xx" onChange={(event) => setHardwareDraft((current) => ({ ...current, firmware_version: event.target.value }))} /></AppField><AppField label="MAC address (optional)" htmlFor="rehab-hardware-mac"><input id="rehab-hardware-mac" value={hardwareDraft.mac_address} maxLength={17} placeholder="12:34:56:78:90:AB" onChange={(event) => setHardwareDraft((current) => ({ ...current, mac_address: event.target.value }))} /></AppField><AppField label="IP address (optional)" htmlFor="rehab-hardware-ip"><input id="rehab-hardware-ip" value={hardwareDraft.ip_address} maxLength={45} placeholder="10.0.0.14" onChange={(event) => setHardwareDraft((current) => ({ ...current, ip_address: event.target.value }))} /></AppField></div>
      <p className="rehab-people-picker__note">The device stays in available inventory until its component is placed on a property canvas.</p>
      {hardwareFormError ? <p className="rehab-person-form__error" role="alert">{hardwareFormError}</p> : null}
      <AppActionGroup><AppButton variant="text" label="Cancel" disabled={hardwareSaving} onClick={() => setHardwareFormOpen(false)} /><AppButton variant="primary" label={hardwareSaving ? 'Creating…' : 'Create hardware'} icon="pi pi-save" disabled={hardwareSaving || (!hardwareDraft.category_id && !hardwareDraft.category_name.trim()) || !hardwareDraft.display_name.trim() || !hardwareDraft.manufacturer.trim() || !hardwareDraft.model.trim()} onClick={() => void createHardwareDevice()} /></AppActionGroup>
    </AppFormDialog>
    <AppFormDialog title={createdPersonCredential ? 'Account created' : 'Add person account'} visible={personFormOpen} onHide={() => !personSaving && setPersonFormOpen(false)} modal draggable={false} className="rehab-person-dialog">
      {createdPersonCredential ? <div className="rehab-person-credential"><p><strong>{createdPersonCredential.name}</strong> can now sign in with this temporary password. It is shown only here and expires in {createdPersonCredential.expiresInHours} hours.</p><AppField label="Email" htmlFor="rehab-created-person-email"><input id="rehab-created-person-email" readOnly value={createdPersonCredential.email} /></AppField><AppField label="Temporary password" htmlFor="rehab-created-person-password"><input id="rehab-created-person-password" readOnly value={createdPersonCredential.temporaryPassword} onFocus={(event) => event.target.select()} /></AppField></div>
        : <><div className="rehab-person-form__names"><AppField label="First name" htmlFor="rehab-person-first-name"><input id="rehab-person-first-name" value={personDraft.first_name} maxLength={100} autoFocus onChange={(event) => setPersonDraft((current) => ({ ...current, first_name: event.target.value }))} /></AppField><AppField label="Last name" htmlFor="rehab-person-last-name"><input id="rehab-person-last-name" value={personDraft.last_name} maxLength={100} onChange={(event) => setPersonDraft((current) => ({ ...current, last_name: event.target.value }))} /></AppField></div><AppField label="Email" htmlFor="rehab-person-email"><input id="rehab-person-email" type="email" value={personDraft.email} maxLength={255} onChange={(event) => setPersonDraft((current) => ({ ...current, email: event.target.value }))} /></AppField><AppField label="Industry" htmlFor="rehab-person-industry"><select id="rehab-person-industry" value={personDraft.industry_key} onChange={(event) => setPersonDraft((current) => ({ ...current, industry_key: event.target.value }))}>{ONBOARDING_PATHS.map((path) => <option key={path.key} value={path.key}>{path.title}</option>)}</select></AppField><AppField label="Temporary password (optional)" htmlFor="rehab-person-password"><input id="rehab-person-password" type="text" value={personDraft.temporary_password} placeholder="Leave blank to generate securely" onChange={(event) => setPersonDraft((current) => ({ ...current, temporary_password: event.target.value }))} /></AppField></>}
      {personFormError ? <p className="rehab-person-form__error" role="alert">{personFormError}</p> : null}
      <AppActionGroup><AppButton variant="text" label={createdPersonCredential ? 'Done' : 'Cancel'} disabled={personSaving} onClick={() => setPersonFormOpen(false)} />{!createdPersonCredential ? <AppButton variant="primary" label={personSaving ? 'Creating…' : 'Create account'} icon="pi pi-user-plus" disabled={personSaving || !personDraft.first_name.trim() || !personDraft.last_name.trim() || !personDraft.email.trim()} onClick={() => void createPersonAccount()} /> : null}</AppActionGroup>
    </AppFormDialog>
    <AppFormDialog title="Delete account?" visible={Boolean(personToDelete)} onHide={() => !personDeleting && setPersonToDelete(null)} modal draggable={false} className="rehab-confirm-dialog">
      <p>This PERMANENTLY deletes <strong>{personToDelete?.name}</strong>&apos;s account from the CRM.</p>
      <AppField label={`Type “${personToDelete?.name || ''}” to confirm`} htmlFor="rehab-delete-person-name"><input id="rehab-delete-person-name" value={personDeleteName} autoFocus autoComplete="off" disabled={personDeleting} onChange={(event) => setPersonDeleteName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void deletePersonAccount(); }} /></AppField>
      {personFormError ? <p className="rehab-person-form__error" role="alert">{personFormError}</p> : null}
      <AppActionGroup><AppButton variant="text" label="Cancel" disabled={personDeleting} onClick={() => setPersonToDelete(null)} /><AppButton variant="danger" label={personDeleting ? 'Deleting…' : 'Delete account'} icon="pi pi-trash" disabled={personDeleting || personDeleteName.trim() !== personToDelete?.name} onClick={() => void deletePersonAccount()} /></AppActionGroup>
    </AppFormDialog>
    <AppFormDialog title={deleteLibraryItem?.kind === 'selection' ? 'Remove selected items?' : `Delete ${deleteLibraryItem?.kind?.includes('title') ? 'title' : deleteLibraryItem?.kind || 'library item'}?`} visible={Boolean(deleteLibraryItem)} onHide={() => !libraryDeleting && setDeleteLibraryItem(null)} modal draggable={false} className="rehab-confirm-dialog">
      {deleteLibraryItem?.kind === 'selection' ? <p>This will remove <strong>{deleteLibraryItem.selectionKeys.length} selected library items</strong>. Canvas nodes already created from them will not be changed.</p>
        : deleteLibraryItem?.person ? <><div className="rehab-delete-scope" role="group" aria-label="Deletion scope">
        <button type="button" className={deleteLinkedPersonAccount ? '' : 'is-selected'} aria-pressed={!deleteLinkedPersonAccount} disabled={libraryDeleting} onClick={() => { setDeleteLinkedPersonAccount(false); setDeleteConfirmationText(''); }}>Component only</button>
        <button type="button" className={deleteLinkedPersonAccount ? 'is-selected is-danger' : ''} aria-pressed={deleteLinkedPersonAccount} disabled={libraryDeleting} onClick={() => { setDeleteLinkedPersonAccount(true); setDeleteConfirmationText(''); }}>Close account too</button>
      </div><p>{deleteLinkedPersonAccount ? <>This PERMANENTLY deletes <strong>{deleteLibraryItem.person.name}</strong>&apos;s account from the CRM.</>
        : <>Delete component for <strong>{deleteLibraryItem.person.name}</strong>.</>}</p></>
        : <p>This will {deleteLibraryItem?.kind === 'step' ? 'remove' : 'permanently remove'} <strong>{deleteLibraryItem?.name}</strong> from your component library. Canvas nodes already created from it will not be changed.</p>}
      {deleteLibraryItem?.person && deleteLinkedPersonAccount ? <AppField label={`Type “${deleteLibraryItem.person.name}” to confirm`} htmlFor="rehab-delete-person-component-name">
        <input id="rehab-delete-person-component-name" value={deleteConfirmationText} autoFocus autoComplete="off" disabled={libraryDeleting} onChange={(event) => setDeleteConfirmationText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && linkedPersonConfirmationMatches) void confirmDeleteLibraryItem(); }} />
      </AppField> : null}
      <AppActionGroup><AppButton variant="text" label="Cancel" disabled={libraryDeleting} onClick={() => setDeleteLibraryItem(null)} />{deleteLibraryItem?.kind === 'selection' ? <AppButton variant="text" label="Stop selecting" disabled={libraryDeleting} onClick={stopLibraryMultiSelect} /> : ['step', 'component'].includes(deleteLibraryItem?.kind) ? <AppButton variant="secondary" label="Select many" disabled={libraryDeleting} onClick={beginLibraryMultiSelect} /> : null}<AppButton variant="danger" label={libraryDeleting ? 'Deleting…' : deleteLibraryItem?.kind === 'selection' ? 'Remove selected' : deleteLibraryItem?.person && deleteLinkedPersonAccount ? 'Close account and delete' : `${deleteLibraryItem?.kind === 'step' ? 'Remove' : 'Delete'} ${deleteLibraryItem?.kind === 'component' ? 'component' : deleteLibraryItem?.kind === 'step' ? 'step' : 'title'}`} icon="pi pi-trash" disabled={libraryDeleting || Boolean(deleteLibraryItem?.person && deleteLinkedPersonAccount && !linkedPersonConfirmationMatches)} onClick={() => void confirmDeleteLibraryItem()} /></AppActionGroup>
    </AppFormDialog>
  </div>;
}
